/**
 * WebSocketPool — Singleton connection pool managing N parallel WebSocket sessions.
 *
 * Zero React/Zustand dependencies. Communicates exclusively via {@link PoolEventEmitter}.
 * Each session owns its own WebSocketClient, MessageHandler, StreamingSessionReducer,
 * and SessionReconnector.
 *
 * Architecture: 1 WS = 1 conversation. conversationId is set at slot creation,
 * not from a backend `conversation_created` event. The frontend generates the UUID
 * and sends it with the first message.
 *
 * Slot management:
 * - Up to {@link MAX_ACTIVE_SLOTS} concurrent sessions.
 * - When the pool is full, the oldest ready session is evicted first.
 * - If no ready sessions exist, the request is rejected (returns null).
 *
 * Backend-owned lifecycle deadlines:
 * - The backend owns all business timeouts (heartbeat 15s ACK, inactivity 30min, replay window 5 min, turn budget 1h30).
 * - The frontend reacts to backend events (`session_expired`, `agent_done`).
 * - A short local timer only coalesces draft transport frames; it never changes a
 *   plan deadline or launches a job.
 * - One safety timer bounds a manual memory compaction that never gets its verdict
 *   (`COMPACTION_CLIENT_TIMEOUT_MS`, derived from the backend LLM budget): it only
 *   unblocks the composer and reloads history, it never cancels anything server-side.
 */

import { supabase } from '@/integrations/supabase/client';
import { ErrorService } from '@/lib/errors/ErrorService';
import { toast } from 'sonner';
import { toastDuration } from '@/components/ui/sonner';
import {
  redactionStreamHub,
  type DecisionDeLiaison,
  type DecisionDEcoute,
  type DemandeDEcoute,
} from '@/features/redaction/streaming/redactionStreamHub';
import type { CanvasTemplateMeta } from '@/features/redaction/state/canvasEditorStore';
import { chargerLEditeurDeRedaction } from '@/features/redaction/editor/chargerLEditeur';
import {
  canvasDocumentRuntime,
  type CanvasRuntimeDocumentReplacementDecision,
  type CanvasRuntimeReadySession,
  type CanvasRuntimeSession,
} from '@/features/redaction/state/canvasDocumentRuntime';
import {
  CanvasDraftContentUnreadableError,
  DEFAULT_HYDRATION_TIMEOUT_MS,
} from '@/features/redaction/state/canvasResidenceCapacity';
import {
  executerLIntentionDocumentaire,
  signalerUnContenuDeBrouillonIllisible,
} from '@/features/redaction/state/canvasDocumentOrchestration';
import { preparerLAdmissionDuResident } from '@/features/redaction/state/canvasAdmission';
import { canvasDraftApi } from '@/features/redaction/api/canvasDraft.api';
import { presentationDUnePageNeuve } from '@/features/redaction/state/canvasDocumentScope';
import { EcheanceDepasseeError, ouvrirUneEcheance, type EcheanceUnique } from './echeanceUnique';
import {
  captureCanvasResidentRecovery,
  captureCanvasResidentReplacementTarget,
  replaceCanvasResidentTemplate,
  restoreCanvasResidentAfterFailedReplacement,
  startBlankCanvasResident,
  type CanvasResidentRecoverySnapshot,
  type CanvasResidentReplacementTarget,
} from '@/features/redaction/state/canvasResidentPresentation';
import {
  decrireLeRefusDeSortiePourLAgent,
  ouvrirUnDocumentVierge,
  poserLIdentiteDuDocument,
  preparerLaSortieDuDocumentCourant,
  type IdentiteDuDocumentPose,
} from '@/features/redaction/state/canvasBasculeDeBrouillon';
import type { SuperDocRuntimeEditor, SuperDocInstance } from '@superdoc/react';
import {
  CANVAS_MOUNT_WAIT_MS,
  describeCanvasUnavailable,
  openCanvas,
  waitForCanvasEditorReady,
} from '@/features/redaction/state/canvasOpening';
import {
  attendreQueLeSlotCanvasSoitPret,
  attendreQueLHoteSoitAffiche,
  isCanvasHostParked,
} from '@/features/redaction/state/canvasHostElement';
import { useCanvasVisibiliteStore } from '@/features/redaction/state/canvasVisibilite';
import {
  pinCanvasDocumentTurn,
  type CanvasDocumentTurnPin,
} from '@/features/redaction/state/canvasDocumentTurnPin';
import {
  lireEtatCanvasPourAgent,
} from '@/features/redaction/state/canvasAgentState';
import { captureCanvasOperationMetric } from '@/features/redaction/diagnostics/canvasOperationMetrics';
import { executeCanvasAgentRequest } from '@/features/redaction/state/canvasAgentRequestGuard';
import { executeSuperDocCanvasRequest, type CanvasExecutionContext } from '@/features/redaction/state/superDocCanvasExecutor';
import { captureCanvasAgentTransportDiagnostic } from '@/features/redaction/diagnostics/canvasWriteDiagnostics';
import {
  applyActions,
  type CanvasBridgeContext,
  type CanvasWriteDocumentApi,
  type HoteUiCanvas,
  extractParagraphs,
  extractSelection,
  extractContextRange,
  extractDocumentStructure,
  extractParagraphFormat,
} from '@/features/redaction/state/canvasBridge';
import type { SuperDocDocumentApi } from '@/features/redaction/state/canvas-agent/superDocDocumentApi';
import {
  CANVAS_ADMISSION_BROWSER_DEADLINE_MS,
  CANVAS_OPEN_BROWSER_DEADLINE_MS,
  CANVAS_OPEN_DOCUMENT_BROWSER_DEADLINE_MS,
  CANVAS_LOAD_TEMPLATE_BROWSER_DEADLINE_MS,
  CanvasActionNameSchema,
  CanvasActionSchema,
  ConversationCasesPayloadSchema,
  StructuralParser,
  decrireIdentiteDeMontage,
  identiteDeMontageDuneAdmission,
  memeMontageEpoqueExigee,
  readConversationCaseRefusal,
} from '@hector/shared-types';
import type {
  CanvasAction,
  CanvasActionResult,
  CanvasDocumentDepart,
  CanvasMountReadiness,
  CanvasMountIdentity,
  CanvasOpenDocumentResultPayload,
  CanvasOperationProbeResultPayload,
  CanvasSuperDocResultPayload,
  ConversationCaseGesture,
  StructuredBlock,
} from '@hector/shared-types';
import { qualifierLesCapacitesDuDocument } from '@/features/redaction/state/canvas-agent/capacitesDuDocument';
import {
  creerVerificateurDEpoque,
  memeEpoque,
  type JetonDEpoque,
} from '@/features/redaction/state/canvas-agent/executionAsynchrone';

/**
 * Le refus rendu à l'agent quand le brouillon d'un fil EXISTE mais que son
 * contenu n'a pas pu être téléchargé (#1586).
 *
 * Publie aussi le verdict durable (`signalerUnContenuDeBrouillonIllisible`) :
 * l'avocat qui rouvre ce fil sur `/assistant` doit lire la même vérité que
 * l'agent. ⚠️ Le message interdit nommément la page neuve : c'est le repli
 * qu'un modèle essaierait, et son premier autosave supprimerait ce brouillon.
 */
function refusDeContenuDeBrouillonIllisible(
  prefixe: string,
  error: CanvasDraftContentUnreadableError,
): string {
  signalerUnContenuDeBrouillonIllisible({
    conversationId: error.conversationId,
    draftId: error.draftId,
    cause: error,
  });
  // ⚠️ Plus « son premier autosave supprime ce brouillon » : c'était vrai tant
  // que chaque publication supprimait les autres brouillons du fil, ce qui n'est
  // plus le cas depuis le 2026-09-22. Le risque qui reste est de CACHER le
  // travail de l'avocat derrière une page vide, et c'est lui que la phrase dit.
  return `${prefixe}: its saved draft draftId=${error.draftId} exists but its content could not be downloaded `
    + `(${error.detail}). Nothing was mounted. Retry once; never call open_canvas_document with source blank to `
    + 'work around it — the lawyer would face an empty page while their draft stays unreadable.';
}

/**
 * Les deux verbes d'interface ne changent pas le DOCX. Toute autre action
 * réussie doit invalider le brouillon durable du scope exact : le Document API
 * ne publie pas uniformément le même événement d'update selon le type de
 * mutation (notamment pour certains formats/commentaires).
 */
function canvasActionChangesDocument(action: CanvasAction): boolean {
  return action.kind !== 'navigate_to_paragraph' && action.kind !== 'set_active_comment';
}

import { WebSocketClient } from './WebSocketClient';
import type { ConnectionCloseReason, TurnHuntResult } from './WebSocketClient';
import { MessageHandler } from './MessageHandler';
import { StreamingSessionReducer } from './StreamingSessionReducer';
import { SessionReconnector } from './SessionReconnector';
import { CanvasOperationProgressRegistry, type CanvasOperationProgressHandle } from './canvasOperationProgressRegistry';
import { PoolEventEmitter } from './PoolEventEmitter';
import { TokenManager } from './TokenManager';
import {
  getCaseDiscoveryModelPreference,
  type CaseDiscoveryModelPreference,
} from '../utils/caseDiscoveryModelPreference';

import { COMPACTION_CLIENT_TIMEOUT_MS, WS_AGENT_URL, WS_RECONNECT_CONFIG } from '../constants';
import { lEcranAssistantAfficheLeFil, lireLaRouteDeLOnglet } from '../utils/doitBasculerVersLAssistant';
import { uneBasculeDuDockEstAnnoncee } from '../state/basculeAnnoncee';
import {
  IDLE_CONTEXT_COMPACTION,
  MAX_ACTIVE_SLOTS,
  getSessionCategory,
  isExchangeActive,
  pickSlotToEvict,
} from '../types/session.types';
import type {
  CauseDEcritureAgentCanvas,
  CompactContextCapability,
  ContextCompactionState,
  ServerFinishingCause,
  SessionStatus,
  TimelineItem,
} from '../types/session.types';
import type { TerminalTurnReason } from '../types/transcript.types';
import type { CaseContextMode } from '../types/tools.types';
import type {
  BackendMessage,
  UserMessage,
  ToolsConfig,
  AttachedDocument,
  FrontendMessage,
  SendMessageOptions,
} from '../types/websocket.types';
import type {
  CitationRegistry,
  RedactionPlanDraftSnapshot,
  RedactionPlanResponsePayload,
  SkillChangeResponse,
  TurnDisplayEntry,
} from '@hector/shared-types';
import type {
  LegalQuestion,
  Jurisdiction,
  FondementJuridique,
  QuestionProgress,
} from '../components/jurisprudence/types';

// ============================================================================
// Internal types
// ============================================================================

/** Per-session state managed by the pool. Not exposed to consumers. */
/** Sentinelle de `resumeVerdictTurnId` : le `session_resume` a été envoyé alors qu'un message
 * en file allait partir — son verdict est périmé par construction (voir sendSessionResume). */
const RESUME_VERDICT_SUPERSEDED = '__resume-verdict-superseded__';

/** Note posée quand le backend n'a plus le début du tour à rattraper (journal plafonné, #1888). */
const TURN_CATCHUP_INCOMPLETE_NOTE = "Le début de cette réponse n'est pas affiché ici. La réponse complète apparaîtra à la fin du tour.";

interface PoolConnection {
  client: WebSocketClient;
  handler: MessageHandler;
  reducer: StreamingSessionReducer;
  reconnector: SessionReconnector;
  sessionKey: string;
  /** Conversation UUID — set at slot creation (1 WS = 1 conversation). */
  conversationId: string;
  caseContextMode: CaseContextMode;
  caseId: string | null;
  /** Host-owned dossier anchor. Never overwritten by the composer's dossier. */
  lockedCaseId: string | null;
  userId: string | null;
  pendingMessage: {
    content: string;
    toolsConfig: ToolsConfig;
    attachedDocuments?: AttachedDocument[];
    modelPreference: CaseDiscoveryModelPreference | null;
    localTurnId?: string;
    caseId: string | null;
    caseChanges?: readonly ConversationCaseGesture[];
    caseIsAlreadyPrincipal?: boolean;
    uiActionCapabilities: SendMessageOptions['uiActionCapabilities'];
  } | null;
  /** Resolves when the backend accepts or rejects the one pending user turn. */
  pendingMessageAcceptance: ((accepted: boolean) => void) | null;
  currentLocalTurnId: string | null;
  /** Tour figé au premier `job_started`; un terminal concurrent peut vider le courant. */
  redactionTurnIdByJob: Map<string, string | null>;
  /** Pins exacts, possédés par cette connexion et indexés par le tour qui les a acquis. */
  documentTurnPins: Map<string, CanvasDocumentTurnPin>;
  /** One exact document replacement at a time; residence joins cannot distinguish two seeds. */
  canvasDocumentLoad?: { turnId: string | null; controller: AbortController };
  /** Transport-only batches; backend remains the sole owner of plan deadlines. */
  redactionPlanDraftFlushes: Map<string, PendingRedactionPlanDraftFlush>;
  status: SessionStatus;
  /** True after first connection_established — distinguishes reconnection from initial connect. */
  hasEstablished: boolean;
  /**
   * `currentLocalTurnId` au moment où CE socket a envoyé `session_resume`. Le verdict
   * `{ resumed:false }` ne vaut que pour ce tour-là : si un message en file est parti
   * entre-temps (nouveau `currentLocalTurnId`), le verdict est périmé et ignoré (#1688).
   */
  resumeVerdictTurnId: string | null;
  /**
   * Session ouverte par `openSessionForResume` (page neuve sur une conversation dont
   * l'historique se termine sans réponse finale) : à l'ACK d'authentification, envoyer
   * `session_resume` au lieu d'attendre un premier message (2026-09-28).
   */
  resumeOnEstablish: boolean;
  /**
   * Tour local ouvert par `openSessionForResume` (#1888). Sa timeline est un FRAGMENT :
   * elle ne reçoit que le tampon rejoué puis le direct, jamais ce qui a été émis avant
   * le rechargement. `emitTerminalSnapshot` le signale au bridge, qui recharge
   * l'historique au lieu de valider. Comparé par id : le tour suivant de la même session
   * (envoyé par cette page, donc complet) n'est pas concerné.
   */
  resumedOnLoadTurnId: string | null;
  /**
   * Rattrapage attendu (#1888) : le tour local `turnId` a été repris au chargement de page
   * et n'a pas encore reçu son début ; `entries` accumule les trames `turn_catchup` jusqu'à
   * la dernière. `null` dès qu'il est appliqué, que le tour finit ou qu'un autre commence.
   * Cet état décide seul de DEMANDER le rattrapage (`session_resume.catchup`, candidates de
   * la chasse comprises) et de l'ACCEPTER. Une reconnexion sans rechargement ne le pose
   * jamais : sa timeline tient déjà le début du tour, le rattraper le doublerait.
   */
  catchup: { turnId: string; entries: TurnDisplayEntry[] } | null;
  /**
   * Un `conversation_busy` a été reçu pour le tour local : on attend la fin du tour distant
   * (`turn_settled`) composer fermé, puis on rend le brouillon (accusé `false`) au lieu
   * de rouvrir la saisie aussitôt — ce qui laissait l'avocat marteler Entrée et, à la
   * treizième tentative, remplacer son propre tour en cours (logs prod 2026-09-24).
   */
  busyAwaitingSettle: boolean;
  /**
   * Un verdict « tour hors de portée » a été reçu et `turn_settled` est attendu. Témoin de
   * la garde d'état de `turn_settled` (avec la note `server_finishing`, que la chasse
   * d'instance diffère : pendant la chasse, la note est absente mais l'attente est réelle).
   */
  remoteTurnAwaited: boolean;
  /** Negotiated per socket so rolling backend/frontend deployments stay safe. */
  supportsRedactionPlanDraftUpdates: boolean;
  /** Negotiated per socket so an older backend keeps its historical send path. */
  supportsUserMessageAcceptance: boolean;
  /** Negotiated per socket; prevents a new frame from reaching an old backend. */
  supportsCanvasOperationProgress: boolean;
  /** Server-advertised passive probe protocol for this exact socket generation. */
  supportsCanvasOperationProbe: boolean;
  /** Per-connection owner of request → mount → monotonic progress identity. */
  canvasOperationProgress: CanvasOperationProgressRegistry;
  /**
   * Réponse de la socket COURANTE sur `capabilities.compactContext`. Remise à
   * `unknown` à chaque coupure : une trame émise avant le prochain ACK serait
   * perdue sans trace (cf. incident 2026-09-11), et la reconnexion peut atterrir
   * sur un backend plus ancien qui rejetterait la trame.
   */
  socketCompactContext: CompactContextCapability;
  /** Dernière réponse publiée au store — collante à travers les coupures. */
  announcedCompactContext: CompactContextCapability;
  /** Compaction manuelle de cette connexion. Jamais reflétée dans `status`. */
  contextCompaction: ContextCompactionFlight;
  createdAt: number;
}

/**
 * Cycle d'une compaction manuelle sur une connexion.
 *
 * `awaiting_socket` existe pour la conversation ROUVERTE : au clic, aucune socket
 * authentifiée n'a encore dit si elle accepte `compact_context`. La demande
 * attend donc l'ACK générique — jamais `onopen`, où une trame se perd — puis part
 * ou se solde par un refus explicite.
 */
type ContextCompactionFlight =
  | { phase: 'idle' }
  | { phase: 'awaiting_socket'; toolsConfig: ToolsConfig; caseId: string | null }
  | { phase: 'awaiting_verdict'; timeout: ReturnType<typeof setTimeout> };

const COMPACTION_UNSUPPORTED_MESSAGE = "La compaction manuelle n'est pas encore disponible sur ce serveur.";
const COMPACTION_CONNECTION_FAILED_MESSAGE = 'La connexion au serveur a échoué. Vous pouvez relancer la compaction.';
const COMPACTION_TIMEOUT_MESSAGE = "La compaction ne répond pas. L'historique est conservé ; rechargez la conversation pour vérifier son état.";

interface RedactionPlanDraftUpdatePayload {
  requestId: string;
  revision: number;
  snapshot: RedactionPlanDraftSnapshot;
}

interface PendingRedactionPlanDraftFlush {
  payload: RedactionPlanDraftUpdatePayload;
  trailingTimer: ReturnType<typeof setTimeout>;
  maxWaitTimer: ReturnType<typeof setTimeout>;
}

// The first edit still reaches the backend immediately. Later edits are folded
// briefly, but continuous typing cannot defer a reset of the server deadline
// for longer than one second.
const REDACTION_PLAN_DRAFT_TRAILING_MS = 300;
const REDACTION_PLAN_DRAFT_MAX_WAIT_MS = 1_000;

/**
 * Immutable identity required to stop a Canvas document turn safely.
 *
 * ⚠️ **`(conversationId, jobId)`, et l'époque de montage a été RETIRÉE le
 * 2026-09-10 (#1336).** `documentSessionId` avait disparu d'abord — la
 * conversation EST la clé du document logique —, et le contrat a porté
 * `mountEpoch` le temps d'un lot, avec cette intention : « un "Arrêter la
 * rédaction" cliqué sur un montage remplacé depuis ne doit pas interrompre le
 * travail du montage courant ». **L'intention est juste ; l'époque ne la
 * servait pas.**
 *
 * La fence confrontait une valeur figée dans le PASSÉ au montage COURANT
 * (`canvasDocumentRuntime.getSessionForConversation`). Elle ne répondait donc
 * pas « ce geste vise-t-il bien ce travail ? » mais « ce document a-t-il été
 * remonté depuis ? » — question sans rapport avec la légitimité de
 * l'interruption, parce que **rien en aval n'est adressé par un montage** :
 * `user_interrupt` porte `conversation_id`, et `releaseDocumentTurnPin` est
 * indexé par `(conversation, tour)`.
 *
 * Les deux gardes « l'arbitrage de l'époque de montage » de
 * `__tests__/WebSocketPool.*.test.ts` mettent en scène les deux seuls régimes
 * possibles :
 *  - **le travail a CHANGÉ** — `redactionTurnIdByJob` refuse le premier, et le
 *    journal montre l'époque encore concordante au moment du refus
 *    (`expected=…#4 mounted=4 capturedTurn=no`). L'époque est REDONDANTE.
 *  - **le travail est le MÊME et le montage a avancé** (refroidissement LRU,
 *    remplacement de gabarit, remontage React) — l'écoute vit hors de React et
 *    survit aux remontages par construction (#857), le travail écrit toujours,
 *    et seule l'époque refusait : l'avocat cliquait « Arrêter » et recevait un
 *    toast « connexion perdue » mensonger. L'époque est NUISIBLE.
 *
 * ⚠️ **Ne pas confondre avec l'époque que porte la LIAISON**
 * (`DemandeDEcoute.mountEpoch`, `redactionStreamHub`) : celle-là est réénoncée
 * au backend dans `redaction_sse_complete` et confrontée au montage que le
 * collector a ADMIS — deux valeurs figées au même instant, donc comparables.
 * Ici la comparaison était passé/présent, ce qui n'est pas la même garde.
 *
 * ⚠️ **Trou connu, que l'époque ne bouchait pas non plus** : pour `jobId: null`
 * (lot d'outils canvas sans job SSE) il ne reste aucune garde de péremption —
 * ni côté appelant (`deciderArretDuTravailAffiche` ne filtre que sur un job non
 * nul), ni ici (`exactJob` se réduit à « un tour est actif »). Un lot n'est pas
 * plus lié à un montage qu'un job ne l'est ; le discriminant juste serait le
 * TOUR capturé au rendu, pas une époque.
 */
export interface CanvasWorkIdentity {
  readonly conversationId: string;
  /** `null` for legacy canvas tool batches that have no HTTP redaction job. */
  readonly jobId: string | null;
}

/**
 * Budget d'attente du remontage physique lors d'un remplacement de document.
 *
 * IMPORTÉ, PAS RECOPIÉ. Remonter un éditeur coûte ce que coûte le monter à
 * froid : la borne est donc l'enveloppe d'hydratation du coordinateur de
 * résidence, `DEFAULT_HYDRATION_TIMEOUT_MS` (`canvasResidenceCapacity.ts`), qui
 * porte la justification de sa valeur et sa source. Une constante recopiée à la
 * main dériverait en silence le jour où l'enveloppe bouge — ce chemin et le
 * chemin froid attendraient alors deux durées différentes pour le même travail.
 *
 * ⚠️ Ce ne sont PAS les bornes des opérations canvas (`INSPECT_TIMEOUT_MS`,
 * `MUTATION_TIMEOUT_MS`, `SuperDocCanvasTools.ts`), qui gouvernent
 * `canvas_inspect` / `canvas_perform_action`. Le budget backend de CE chemin est le
 * `CANVAS_LOAD_TEMPLATE_ACK_TIMEOUT_MS`, l'attente de `LoadDocumentIntoCanvas.ts`.
 */
const CANVAS_DOCUMENT_REPLACEMENT_TIMEOUT_MS = DEFAULT_HYDRATION_TIMEOUT_MS;

/**
 * Âge au-delà duquel une écriture canvas de l'agent n'est plus exécutée (#44).
 *
 * Calé sur `MUTATION_INACTIVITY_BEFORE_PROBE_MS` (backend, `SuperDocCanvasTools.ts`) :
 * après 15 s sans nouvelles du navigateur, le serveur sonde l'opération et, si la
 * socket est morte, la déclare « effet inconnu ». Une commande qui arrive plus
 * tard a donc pu être abandonnée. Sur une connexion saine elle arrive en moins
 * d'une seconde : 15 s de retard signifient déjà un onglet gelé ou hors ligne.
 * L'estimation sous-évalue un gel d'environ 10 % (`serverFrameAge.ts`) : le refus
 * joue en pratique au-delà de ~16,7 s de gel.
 */
const CANVAS_WRITE_MAX_DELIVERY_AGE_MS = 15_000;

/**
 * La borne de l'attente de A′, remonté après un B qui n'a pas monté (#1338, D4).
 *
 * Le tool backend attend 30 s AU TOTAL (`LoadDocumentIntoCanvas`,
 * `CANVAS_LOAD_TEMPLATE_ACK_TIMEOUT_MS`) et l'attente de B en consomme déjà
 * 14 (`CANVAS_DOCUMENT_REPLACEMENT_TIMEOUT_MS`). La restauration se borne donc à
 * 30 − 14 − export de A − marge.
 *
 * Mesuré au gate Chromium le 2026-09-10
 * (`tests/ws-mocked/mesure-1338-export-de-reprise.spec.ts`) : export de A de 19 à
 * 26 ms (gabarit blanc, `01-titres-styles.docx`, `05-marqueurs-acte.docx`) ;
 * A′ prêt environ 1,9 s après l'échec de B ; accusé complet à 15,98 s. Borne
 * 10 s : il reste ~6 s de marge pour ce que `ws-mocked` ne mesure pas — le
 * téléchargement de la pièce côté backend, le base64 et le transit WebSocket
 * avant que la trame n'atteigne le navigateur.
 */
const CANVAS_DOCUMENT_RESTORATION_TIMEOUT_MS = 10_000;

/**
 * L'action demandée remplace-t-elle tout le document (`write_markdown` en
 * `replace`) ? Seul verbe dont l'issue dépend de ce que l'avocat VOIT (#1586).
 * Lu avant la validation de l'enveloppe, d'où la lecture défensive : une forme
 * inattendue ne remplace rien ici et sera refusée par la garde de requête.
 */
function remplaceLeDocument(action: unknown): boolean {
  if (typeof action !== 'object' || action === null) return false;
  return 'action' in action && action.action === 'write_markdown'
    && 'mode' in action && action.mode === 'replace';
}

/**
 * Ce qu'un chargement à chaud laisse à l'écran une fois B commité (#1338, D4).
 *
 * Chaque issue a SA trame corrélée — B prêt, A restauré, A perdu — et aucune
 * ne passe par un jet : `MessageHandler` n'attend pas les handlers asynchrones,
 * et un refus levé tard deviendrait un délai dépassé côté backend (#1396).
 */
type IssueDuRemplacementChaud =
  | {
    readonly issue: 'b-pret';
    readonly session: CanvasRuntimeReadySession;
    /** Faux quand un brouillon du fil n'a pas pu être adopté : il a été posé comme une copie. */
    readonly identiteAdoptee: boolean;
  }
  | {
    readonly issue: 'a-restaure';
    /** Pourquoi B n'est pas devenu prêt. */
    readonly cause: string;
    readonly epoqueDeB: number;
    readonly restaure: CanvasRuntimeReadySession;
  }
  | {
    readonly issue: 'a-perdu';
    readonly cause: string;
    readonly epoqueDeB: number;
    /** Pourquoi A n'a pas pu revenir — dit tel quel à l'agent. */
    readonly raison: string;
  };

/** La copie de A prise avant le commit, ou la raison pour laquelle il n'y en a pas. */
type RepriseDeA =
  | { readonly copie: CanvasResidentRecoverySnapshot; readonly sansCopie: null }
  | { readonly copie: null; readonly sansCopie: string };

/**
 * Attend `pending`, ou rejette dès que `signal` avorte (#1354/D9).
 *
 * Une attente de montage ne se dénoue pas d'elle-même quand le tour se termine
 * ou que la socket meurt : sans cette course, le handler continuerait à écrire
 * dans un document qui n'appartient plus au tour qui l'a demandé.
 */
function attendreSaufAnnulation<T>(
  pending: Promise<T>,
  signal: AbortSignal,
  messageDAnnulation: () => string,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      signal.removeEventListener('abort', abort);
      reject(new Error(messageDAnnulation()));
    };
    pending.then((value) => {
      signal.removeEventListener('abort', abort);
      resolve(value);
    }, (error: unknown) => {
      signal.removeEventListener('abort', abort);
      reject(error);
    });
    if (signal.aborted) abort();
    else signal.addEventListener('abort', abort, { once: true });
  });
}

/**
 * L'erreur d'un chargement à chaud qui n'a pas abouti : elle dit ce que l'écran
 * montre VRAIMENT (#1338, piège 3 de la carte). « The previous document remains
 * visible » n'est écrit QUE lorsque A est effectivement revenu, prêt, et le
 * texte nomme l'époque de A′ — celle que l'agent doit désormais adresser.
 */
function decrireUnChargementChaudEchoue(input: {
  /** Provenance optionnelle d'une pièce `documents`, jamais l'identifiant de montage. */
  readonly documentId: string | null;
  readonly conversationId: string;
  readonly issue: Exclude<IssueDuRemplacementChaud, { readonly issue: 'b-pret' }>;
}): string {
  const tete = `load_document_into_canvas could not mount documentId=${input.documentId ?? 'none'} `
    + `for conversationId=${input.conversationId}: ${input.issue.cause}`;
  if (input.issue.issue === 'a-restaure') {
    return `${tete} The previous document remains visible: documentId=${input.issue.restaure.documentId ?? 'none'} `
      + `was restored at mountEpoch=${input.issue.restaure.mountEpoch} (the failed candidate held `
      + `mountEpoch=${input.issue.epoqueDeB}). Do not report documentId=${input.documentId ?? 'none'} as opened; address any `
      + `further canvas command to mountEpoch=${input.issue.restaure.mountEpoch}.`;
  }
  return `${tete} The document shown before this load could NOT be put back: ${input.issue.raison} `
    + 'Tell the lawyer, re-inspect the canvas, then reopen a document.';
}

// ============================================================================
// WebSocketPool
// ============================================================================

let instance: WebSocketPool | null = null;

interface NormalizedBackendError {
  message: string;
  code?: string;
}

/**
 * Cause de la note d'attente `server_finishing`, lue du verdict de reprise. Le backend ne
 * sait dire que « une autre socket vivante tient le tour » ; son silence (autre instance,
 * backend ancien) laisse le libellé historique, celui d'une coupure.
 */
function serverFinishingCause(turnOwnerConnected: boolean | undefined): ServerFinishingCause | undefined {
  return turnOwnerConnected === true ? 'open_elsewhere' : undefined;
}

function normalizeBackendErrorPayload(payload: unknown): NormalizedBackendError {
  if (!payload || typeof payload !== 'object') {
    return {
      message: 'Le serveur a signalé une erreur sans détail. Vous pouvez relancer votre demande.',
    };
  }

  const record = payload as Record<string, unknown>;
  const message =
    (typeof record.details === 'string' && record.details.trim()) ||
    (typeof record.error === 'string' && record.error.trim()) ||
    'Le serveur a signalé une erreur sans détail. Vous pouvez relancer votre demande.';
  const code = typeof record.code === 'string' ? record.code : undefined;

  return code ? { message, code } : { message };
}

/**
 * Le couple `(tool, action)` d'un receipt d'échec, dérivé de la requête.
 *
 * Le contrat partagé (`CanvasSuperDocResultPayloadSchema`) lie les deux : seul
 * `canvas_inspect` accepte `action: 'inspect'`, `canvas_perform_action` exige
 * l'un de ses verbes. Un receipt d'échec mal apparié est **rejeté à la
 * frontière**, et l'agent ne reçoit alors rien du tout — un refus silencieux
 * est pire que le refus qu'on essayait de lui rendre.
 *
 * ⚠️ **Le type de retour est une union CORRÉLÉE, une branche par outil**
 * (#1336, critère 5). Déclaré comme deux champs indépendants
 * (`{ tool: …['tool']; action: …['action'] }`), il laissait le compilateur
 * incapable de prouver l'appariement d'un receipt construit par spread : il le
 * refusait (`'inspect'` n'est pas assignable aux verbes de
 * `canvas_perform_action`), et un `as CanvasSuperDocResultPayload` taisait ce
 * refus — la vérification qui distingue les deux branches était annulée au
 * seul endroit où elle compte. Porter la corrélation dans le type la rend au
 * site d'appel, sans cast.
 */
export function describeCanvasReceiptIdentity(payload: unknown):
  | { tool: 'canvas_inspect'; action: 'inspect' }
  | {
    tool: 'canvas_perform_action';
    action: Extract<CanvasSuperDocResultPayload, { tool: 'canvas_perform_action' }>['action'];
  } {
  const tool = (payload as { tool?: unknown }).tool;
  if (tool !== 'canvas_perform_action') return { tool: 'canvas_inspect', action: 'inspect' };
  const action = (payload as { action?: { action?: unknown } }).action;
  const verb = typeof action === 'object' && action !== null ? action.action : undefined;
  // Le verbe est VALIDÉ contre le catalogue partagé, pas seulement typé : une
  // chaîne hors énumération (dérive de contrat, requête forgée) produirait un
  // receipt invalide, donc rejeté à la frontière — l'agent ne recevrait rien.
  // Ce chemin d'échec s'exécute AVANT la validation d'enveloppe de l'exécuteur,
  // il ne peut donc rien présumer de son entrée. Le catalogue est un
  // sous-ensemble strict de l'énumération du receipt : ce qu'il accepte y est
  // toujours accepté.
  const parsedVerb = CanvasActionNameSchema.safeParse(verb);
  return {
    tool: 'canvas_perform_action',
    // Repli le moins trompeur : dans l'énumération, et ne prétend pas qu'une
    // inspection a eu lieu.
    action: parsedVerb.success ? parsedVerb.data : 'rewrite_block',
  };
}

type CanvasDocumentStateAnnouncement = {
  conversationId: string;
  mountEpoch: number;
  documentId: string | null;
  revision: string;
  /** The backend session keys which actually received the state frame. */
  sessionKeys: readonly string[];
};

export class WebSocketPool {
  // Le report est injecté ici — et pas dans l'émetteur — pour lui garder son
  // absence de dépendances. `silent: true` ne coupe que le toast : l'exception
  // part bien vers PostHog, seul moyen d'apprendre qu'un abonné du pool casse.
  readonly events = new PoolEventEmitter((error, event) => {
    // `ErrorContext` est une forme fermée (RGPD : aucune clé libre) — le nom de
    // l'événement voyage donc dans `action`, qui est déjà le champ « ce que le
    // code faisait ». Un nom d'événement du pool n'est pas une donnée utilisateur.
    ErrorService.handle(error, {
      context: { action: `ws_pool_listener:${event}` },
      silent: true,
    });
  });

  private connections = new Map<string, PoolConnection>();
  private timelineCache = new Map<string, TimelineItem[]>();
  private tokenManager: TokenManager;
  private sessionCounter = 0;
  private unsubscribeCanvasReady: (() => void) | null = null;
  private unsubscribeCanvasInsertionSettled: (() => void) | null = null;
  private readonly canvasResourceSubscriptions = new Map<string, {
    mountEpoch: number;
    unsubscribe: () => void;
  }>();
  /** Shares a capability read when `onSessionReady` and a load ACK race on the same mount object. */
  private readonly canvasStateAnnouncementsInFlight = new WeakMap<
    CanvasRuntimeSession,
    Promise<CanvasDocumentStateAnnouncement | null>
  >();
  /**
   * Terminaux HTTP/SSE reçus avant que le dernier delta React soit vidé dans
   * SuperDoc. La clé reste liée à la conversation : un job peut être relancé
   * dans un autre fil sans que son signal final débloque le mauvais agent.
   */
  private pendingRedactionSseCompletions = new Map<string, {
    conversationId: string;
    jobId: string;
    status: 'complete' | 'error' | 'cancelled';
  }>();
  /** Une fermeture manuelle ne doit pas être annulée par une progression tardive. */
  private redactionCanvasOpenedForJobIds = new Set<string>();
  /**
   * Signaux d'écriture agent déjà émis, clés `${conversationId}|${cle}` (#1588).
   *
   * ⚠️ Vidé par `destroyAll()` SEUL. `redactionCanvasOpenedForJobIds` se vide sur
   * `redaction_plan_results` ; celui-ci surtout pas : un `redaction_job_started`
   * rejoué après la fin du job rebasculerait l'avocat vers un acte qu'il a déjà
   * vu s'écrire. La croissance est bornée par les travaux d'un onglet.
   */
  private readonly signauxDEcritureCanvasEmis = new Set<string>();
  /**
   * Admissions qui précèdent le premier montage DOCX visible, clés
   * `${conversationId}|${cle}` (#1588). Cet ensemble reste distinct des
   * écritures : le chargement émet ensuite son propre signal d'écriture avec
   * la même requête, que l'avocat doit toujours pouvoir voir arriver.
   */
  private readonly signauxDAdmissionCanvasEmis = new Set<string>();
  /** Temporary pins held after admission until the stream reaches a terminal state. */
  private canvasAdmissionPins = new Map<string, {
    conversationId: string;
    jobId: string;
    release: () => void;
  }>();
  /** Abort only admissions still waiting for a physical editor; pins use their own release frame. */
  private canvasAdmissionAbortControllers = new Map<string, { conversationId: string; controller: AbortController }>();

  /** Scope temporary admission pins to their conversation as request ids are wire values, not global keys. */
  private canvasAdmissionPinKey(conversationId: string, requestId: string): string {
    return JSON.stringify([conversationId, requestId]);
  }

  private constructor() {
    this.tokenManager = new TokenManager(supabase);
    // Le hub d'écoute des rédactions (#857) ne connaît aucun transport : il
    // retient la conversation DU TRAVAIL et demande ici qu'on lui porte la fin.
    // La connexion est retrouvée au moment de l'annonce, pas au démarrage du
    // flux — entre les deux il a pu y avoir une reconnexion, et une clé de
    // session capturée d'avance viserait une connexion morte.
    redactionStreamHub.definirNotificateurDeFin((conversationId, jobId, statut) => {
      this.notifyRedactionSseCompleteForConversation(conversationId, jobId, statut);
    });
    // Chaque document qui devient ready dans le canvas (tool, dialog Importer,
    // restauration localStorage, switch) est annoncé au backend pour que le
    // CanvasSessionBroker suive TOUJOURS la session réellement montée.
    this.unsubscribeCanvasReady = canvasDocumentRuntime.onSessionReady(
      (session) => {
        this.subscribeCanvasActionResources(session);
        // `catch` obligatoire sur un fire-and-forget : sans lui, un rejet de la
        // lecture de capacités deviendrait une unhandled rejection qui ferait
        // disparaître l'annonce sans la moindre trace — et l'agent refuserait
        // ensuite d'inspecter un canvas pourtant ouvert.
        void this.announceCanvasDocumentState(session).catch((erreur: unknown) => {
          console.warn('[canvas] annonce de session interrompue', erreur);
        });
      },
    );
    // Le terminal du transport est seulement provisoire : le dernier delta
    // peut être rendu et mis en file APRES lui. Cette annonce n'arrive qu'après
    // `PointDEcritureCanvas.auRepos()` sur le montage exact (#1606).
    this.unsubscribeCanvasInsertionSettled = canvasDocumentRuntime.onStreamInsertionSettled(
      (event) => this.finishPendingRedactionSseCompletion(event),
    );
  }

  /**
   * Re-announces readiness only when a mount-owned action resource changes.
   * The named-letterhead resolver is installed by a React effect after the
   * editor is ready; subscribing to the document scope closes that ordering gap.
   */
  private subscribeCanvasActionResources(session: CanvasRuntimeSession): void {
    const conversationId = session.conversationId;
    const mountEpoch = session.mountEpoch;
    const current = this.canvasResourceSubscriptions.get(conversationId);
    if (current?.mountEpoch === mountEpoch) return;
    current?.unsubscribe();
    const scope = canvasDocumentRuntime.getScope(conversationId);
    if (!scope) return;
    let dernierResolveurEnTete = scope.getPhysicalResources().namedLetterheadResolver;
    const unsubscribe = scope.subscribePhysical(() => {
      const live = canvasDocumentRuntime.getSessionForConversation(conversationId);
      if (!live || live.mountEpoch !== mountEpoch || live.state !== 'ready') return;
      const resolveurEnTeteCourant = scope.getPhysicalResources().namedLetterheadResolver;
      // Zoom, tracked-changes and writer activity are physical scope updates,
      // but only this resource changes the agent-facing action readiness.
      if (resolveurEnTeteCourant === dernierResolveurEnTete) return;
      dernierResolveurEnTete = resolveurEnTeteCourant;
      void this.announceCanvasDocumentState(live).catch((error: unknown) => {
        console.warn('[canvas] actualisation des ressources refusée', error);
      });
    });
    let unregisterDisposer: () => void = () => undefined;
    const subscription = {
      mountEpoch,
      unsubscribe: () => {
        unsubscribe();
        unregisterDisposer();
        // A cooled scope must not pin its listener (or an obsolete mount) in
        // this long-lived WebSocket pool until logout/HMR.
        if (this.canvasResourceSubscriptions.get(conversationId) === subscription) {
          this.canvasResourceSubscriptions.delete(conversationId);
        }
      },
    };
    this.canvasResourceSubscriptions.set(conversationId, subscription);
    unregisterDisposer = scope.registerDisposer(subscription.unsubscribe);
  }

  /**
   * Réhydratation du broker canvas : le CanvasSessionBroker backend est in-memory
   * (perdu à la reconnexion hors grace-period ou au restart backend). Si un document
   * est déjà monté pour CETTE conversation, ré-annoncer son état évite le
   * CANVAS_SESSION_REQUIRED mensonger au prochain canvas_inspect, et c'est aussi LA
   * trame qui rouvre le circuit Canvas d'un tour adopté : le backend ne réarme le
   * transport fermé par la coupure (`rearmTransportAfterOwnerResume`) que sur une
   * réannonce venue de la socket qui vient d'adopter la SessionState.
   * Deux appelants, une seule règle : l'ACK `connection_established` et la chasse
   * d'instance gagnée (#1816), dont l'ACK générique a été consommé par la chasse.
   * Pas de test sur le porteur ici : `announceCanvasDocumentState` le lit et
   * filtre déjà les connexions lui-même (#862).
   */
  private reannounceMountedCanvas(conn: PoolConnection): void {
    const mounted = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
    if (!mounted || mounted.state !== 'ready') return;
    void this.announceCanvasDocumentState(mounted).catch((erreur: unknown) => {
      console.warn(`[canvas] réannonce de session interrompue (conversationId=${conn.conversationId})`, erreur);
    });
  }

  /**
   * Envoie `canvas_document_state` à la connexion de la conversation propriétaire du
   * canvas. Une réannonce strictement identique ne révoque pas les cibles inspectées ;
   * une nouvelle époque, un autre document ou une autre révision, si. Readiness
   * reste toutefois remplacée par la preuve de la dernière annonce.
   */
  private announceCanvasDocumentState(session: CanvasRuntimeSession): Promise<boolean> {
    return this.announceCanvasDocumentStateProof(session).then((announcement) => announcement !== null);
  }

  /**
   * Returns the exact state snapshot placed on the WebSocket, not just whether
   * a send was attempted. Replacement ACKs compare this proof with the still-live
   * runtime state; a revision or connection change must not look like a confirmed
   * mount merely because some WebSocket accepted a frame.
   */
  private announceCanvasDocumentStateProof(
    session: CanvasRuntimeSession,
  ): Promise<CanvasDocumentStateAnnouncement | null> {
    const inFlight = this.canvasStateAnnouncementsInFlight.get(session);
    if (inFlight) return inFlight;

    const announcement = this.announceCanvasDocumentStateNow(session);
    this.canvasStateAnnouncementsInFlight.set(session, announcement);
    void announcement.then(
      () => {
        if (this.canvasStateAnnouncementsInFlight.get(session) === announcement) {
          this.canvasStateAnnouncementsInFlight.delete(session);
        }
      },
      () => {
        if (this.canvasStateAnnouncementsInFlight.get(session) === announcement) {
          this.canvasStateAnnouncementsInFlight.delete(session);
        }
      },
    );
    return announcement;
  }

  private async announceCanvasDocumentStateNow(
    session: CanvasRuntimeSession,
  ): Promise<CanvasDocumentStateAnnouncement | null> {
    const readinessStartedAt = performance.now();
    // ⚠️ **Plus de branche « session sans propriétaire » (#1336, lot N).**
    // `CanvasRuntimeSession.conversationId` n'est plus nullable : un scope EST un
    // montage, et un montage sans conversation ne désignerait rien. Le garde-fou
    // que #1331 avait posé ici — avertir qu'un document né sans fil serait
    // inatteignable par l'agent — est devenu structurel, il vit dans le type et
    // dans `acquerirPourConversation`, qui refuse une conversation vide.
    const conversationId = session.conversationId;
    if (!session.revision) {
      // Celui-ci est vraiment anormal : SuperDoc est monté mais ne rend pas de
      // révision lisible. Sans cette trace, l'absence d'annonce ne se manifeste
      // que bien plus loin, par un `CANVAS_SESSION_REQUIRED` du broker qui
      // n'apprend rien sur sa cause.
      console.warn(
        "[canvas] état du document non annoncé — révision SuperDoc illisible, l'agent refusera "
        + `d'inspecter le canvas (conversationId=${conversationId}, `
        + `mountEpoch=${session.mountEpoch}, état=${session.state})`,
      );
      captureCanvasOperationMetric({
        phase: 'readiness', outcome: 'no_revision',
        durationMs: performance.now() - readinessStartedAt,
        letterheadResource: 'not_required',
      });
      return null;
    }
    // Les capacités du document, sous borne d'attente (#987) : c'est ce qui
    // permet au backend de ne proposer au modèle que les verbes que CE document
    // sait exécuter, sans attendre une première inspection. Un runtime qui ne
    // répond pas est annoncé explicitement non qualifié : le modèle peut encore
    // lire le Canvas, mais ne reçoit aucune mutation à deviner.
    const attendue: JetonDEpoque = {
      conversationId,
      mountEpoch: session.mountEpoch,
    };
    const capabilityQualification = await qualifierLesCapacitesDuDocument(session.editor?.doc);
    // `[]` est aussi le pont vers un backend antérieur, qui ne connaît pas
    // encore l'état discriminé mais sait déjà retirer `canvas_perform_action`
    // quand sa liste de verbes est vide. Ne jamais omettre ce champ dans le cas
    // non qualifié : l'omission historique rouvrait le catalogue complet.
    const executableActions = capabilityQualification.status === 'qualified'
      ? capabilityQualification.executableActions
      : [];
    const courante = canvasDocumentRuntime.getSessionForConversation(conversationId);
    const epoqueCourante: JetonDEpoque | null = courante
      ? { conversationId: courante.conversationId, mountEpoch: courante.mountEpoch }
      : null;
    if (!courante || courante.state !== 'ready' || !memeEpoque(attendue, epoqueCourante)) {
      console.debug(
        '[canvas] annonce prête abandonnée après lecture des capacités — le montage a changé '
        + `(attendu=${conversationId}#${session.mountEpoch})`,
      );
      captureCanvasOperationMetric({
        phase: 'readiness', outcome: 'stale_mount',
        durationMs: performance.now() - readinessStartedAt,
        letterheadResource: 'not_required',
      });
      return null;
    }
    if (!courante.revision) {
      console.warn(
        `[canvas] état du document non annoncé — révision SuperDoc devenue illisible pendant la qualification `
        + `(conversationId=${conversationId}, mountEpoch=${courante.mountEpoch})`,
      );
      captureCanvasOperationMetric({
        phase: 'readiness', outcome: 'no_revision',
        durationMs: performance.now() - readinessStartedAt,
        letterheadResource: 'not_required',
      });
      return null;
    }

    const presentation = canvasDocumentRuntime.getScope(conversationId)?.getPresentation() ?? null;
    const physicalResources = canvasDocumentRuntime.getScope(conversationId)?.getPhysicalResources() ?? null;
    const actionResources: CanvasMountReadiness['actionResources'] = capabilityQualification.status === 'qualified'
      && capabilityQualification.executableActions.includes('set_letterhead')
      ? [{
        action: 'set_letterhead',
        resource: 'named_letterhead_resolver',
        status: physicalResources?.namedLetterheadResolver ? 'available' : 'unavailable',
      }]
      : [];
    const readiness: CanvasMountReadiness = {
      version: 1,
      mountEpoch: courante.mountEpoch,
      editor: 'ready',
      capabilities: capabilityQualification,
      actionResources,
    };
    const meta = presentation?.templateMeta ?? null;
    const documentTitle = presentation?.documentTitle ?? meta?.documentTitle ?? null;
    const announcementState: Omit<CanvasDocumentStateAnnouncement, 'sessionKeys'> = {
      conversationId,
      mountEpoch: courante.mountEpoch,
      documentId: meta?.documentId ?? courante.documentId ?? null,
      revision: courante.revision,
    };
    let annoncee = 0;
    const sessionKeys: string[] = [];
    for (const [sessionKey, conn] of this.connections) {
      if (conn.conversationId !== conversationId) continue;
      if (!conn.client.isConnected()) continue;
      annoncee += 1;
      sessionKeys.push(sessionKey);
      conn.client.send({
        type: 'canvas_document_state',
        conversation_id: conn.conversationId,
        payload: {
          // ⚠️ **La forme CONVERSATION, et le courtier l'exige désormais**
          // (#1336, lot A) : la garde agent de ce bundle n'applique plus que
          // `CanvasConversationInspectEnvelopeSchema` / `…Action…`, tous deux
          // `.strict()`. Le courtier rend la forme que le montage a annoncée,
          // sans négocier — annoncer encore `documentSessionId` lui ferait donc
          // renvoyer des enveloppes legacy que la garde refuserait, et la chaîne
          // agent casserait en silence.
          conversationId: announcementState.conversationId,
          mountEpoch: announcementState.mountEpoch,
          // Capabilities are asynchronous; announce the revision read from the
          // live same-mount session, not the stale event snapshot captured before await.
          revision: announcementState.revision,
          documentId: announcementState.documentId,
          documentTitle,
          executableActions,
          capabilityQualification,
          readiness,
        },
      });
      this.log(sessionKey, conn.conversationId, 'CANVAS_DOC_STATE', `mountEpoch=${courante.mountEpoch} documentId=${meta?.documentId ?? courante.documentId ?? 'none'}`);
    }
    if (annoncee === 0) {
      // Le TROISIÈME retour muet, rendu observable : aucune connexion ouverte
      // pour le porteur. Nominal juste avant l'ouverture de la connexion (le
      // rattrapage est `connection_established`), mais c'est aussi la seule
      // trace qui reste si ce rattrapage n'a pas lieu — sans elle, l'absence
      // d'annonce ne se manifeste qu'à distance, par un
      // `CANVAS_SESSION_REQUIRED` qui n'apprend rien sur sa cause.
      console.debug(
        "[canvas] état du document non annoncé — aucune connexion ouverte pour la conversation "
        + `porteuse (conversationId=${conversationId}, mountEpoch=${courante.mountEpoch}, `
        + `connexions=${this.connections.size})`,
      );
    }
    captureCanvasOperationMetric({
      phase: 'readiness',
      outcome: annoncee === 0
        ? 'no_connection'
        : capabilityQualification.status === 'qualified' ? 'qualified' : 'unqualified',
      durationMs: performance.now() - readinessStartedAt,
      letterheadResource: actionResources[0]?.status ?? 'not_required',
    });
    return annoncee > 0 ? { ...announcementState, sessionKeys } : null;
  }

  /**
   * Identité métier (uuid/titre) transmise à l'exécuteur. La session, elle,
   * reste dans la garde de requête et ne possède aucun fallback d'affichage.
   */
  private buildCanvasExecutionContext(
    conversationId: string,
    progress?: Pick<CanvasExecutionContext, 'onExecutionStarted' | 'onUnitCompleted'>,
  ): CanvasExecutionContext {
    const session = canvasDocumentRuntime.getSessionForConversation(conversationId);
    const presentation = canvasDocumentRuntime.getScope(conversationId)?.getPresentation() ?? null;
    const meta = presentation?.templateMeta ?? null;
    return {
      document: {
        id: meta?.documentId ?? session?.documentId ?? null,
        title: presentation?.documentTitle ?? meta?.documentTitle ?? null,
      },
      ...progress,
    };
  }

  /** Return the singleton instance, creating it on first call. */
  static getInstance(): WebSocketPool {
    if (!instance) {
      instance = new WebSocketPool();
    }
    return instance;
  }

  // ==========================================================================
  // Public API
  // ==========================================================================

  /**
   * Open a new session in the pool. Allocates a slot, opens a WebSocket,
   * wires all message handlers, and returns the session key.
   *
   * 1 WS = 1 conversation: conversationId is set at slot creation.
   * If a slot already exists for this conversation, returns its key.
   *
   * @returns The session key, or `null` if the pool is full and no ready session
   *          can be evicted.
   */
  async openSession(
    userId: string,
    conversationId: string,
    caseId?: string,
    caseContextMode: CaseContextMode = 'selectable',
  ): Promise<string | null> {
    if (caseContextMode !== 'selectable' && !caseId) {
      throw new Error(
        `WebSocketPool.openSession requires caseId when caseContextMode is "${caseContextMode}". Open the session with the host dossier instead of a general context.`,
      );
    }

    // Check if a slot already exists for this conversation
    for (const [key, conn] of this.connections) {
      if (conn.conversationId === conversationId) {
        this.log(key, conversationId, 'REUSE', 'slot already exists');
        return key;
      }
    }

    // Slot management: reclaim the oldest `ready` slot if full. Never evict a
    // `connecting` slot here: it may already have the user's first prompt
    // queued while WS auth is still in flight. Nor a slot whose manual memory
    // compaction is unsettled (cf. pickSlotToEvict).
    if (this.getOccupiedSlotCount() >= MAX_ACTIVE_SLOTS) {
      const evicted = this.evictReclaimableSlot();
      if (!evicted) return null;
    }

    const sessionKey = `session-${++this.sessionCounter}-${Date.now()}`;
    const token = await this.tokenManager.getToken();
    if (!token) {
      console.error('[WebSocketPool] No auth token — cannot open session');
      return null;
    }

    const client = new WebSocketClient(WS_AGENT_URL, token);
    const handler = new MessageHandler();
    const reducer = new StreamingSessionReducer();

    const conn: PoolConnection = {
      client,
      handler,
      reducer,
      reconnector: null as unknown as SessionReconnector, // set below
      sessionKey,
      conversationId,
      caseContextMode,
      caseId: caseId ?? null,
      lockedCaseId: caseContextMode === 'locked' ? caseId ?? null : null,
      userId,
      pendingMessage: null,
      pendingMessageAcceptance: null,
      currentLocalTurnId: null,
      redactionTurnIdByJob: new Map(),
      documentTurnPins: new Map(),
      redactionPlanDraftFlushes: new Map(),
      status: 'connecting',
      hasEstablished: false, // Set to true after first connection_established (for session_resume)
      resumeVerdictTurnId: null,
      resumeOnEstablish: false,
      resumedOnLoadTurnId: null,
      catchup: null,
      busyAwaitingSettle: false,
      remoteTurnAwaited: false,
      supportsRedactionPlanDraftUpdates: false,
      supportsUserMessageAcceptance: false,
      supportsCanvasOperationProgress: false,
      supportsCanvasOperationProbe: false,
      canvasOperationProgress: null as unknown as CanvasOperationProgressRegistry,
      socketCompactContext: 'unknown',
      announcedCompactContext: 'unknown',
      contextCompaction: { phase: 'idle' },
      createdAt: Date.now(),
    };

    conn.canvasOperationProgress = new CanvasOperationProgressRegistry({
      emit: (payload) => {
        // A reconnect can land on an older backend. Never let a stale callback
        // emit a frame whose type that socket has not explicitly negotiated.
        if (!conn.supportsCanvasOperationProgress || !conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_operation_progress',
          conversation_id: conn.conversationId,
          payload,
        } as FrontendMessage);
      },
    });

    // Build reconnector (needs conn reference for the connect callback)
    conn.reconnector = new SessionReconnector({
      maxAttempts: WS_RECONNECT_CONFIG.maxAttempts,
      baseDelay: WS_RECONNECT_CONFIG.baseDelay,
      onStatusChange: (info) => {
        if (info) {
          this.events.emit('connection_changed', sessionKey, false, {
            attempt: info.attempt,
            maxAttempts: info.maxAttempts,
            countdown: info.countdown,
          });
        } else {
          const connected = conn.client.isConnected();
          this.events.emit('connection_changed', sessionKey, connected);
        }
      },
      onFailed: () => {
        // Une compaction dont la socket est morte n'aura jamais de verdict : sans
        // ce retour au repos, le rond tournerait et l'envoi resterait bloqué à vie.
        this.abandonContextCompactionOnLostSocket(conn, 'reconnexion épuisée');
        // Idem pour une attente `conversation_busy` : plus de socket, plus de verdict.
        conn.busyAwaitingSettle = false;
        conn.remoteTurnAwaited = false;
        // WHY transition to error: without this, an occupied slot stays
        // 'streaming' OR 'connecting' forever after reconnection exhaustion.
        // For 'streaming' the UI shows "Impossible de se connecter" but input
        // stays disabled (isStreaming=true) with no recovery. For 'connecting'
        // the slot wedges occupied and uncollectable — it keeps counting
        // toward MAX_ACTIVE_SLOTS and locks the user out of new conversations
        // (incident 2026-06-18). Error makes the slot terminal/evictable and
        // triggers the status-transition effect (commit transcript, enable input).
        if (getSessionCategory(conn.status) === 'occupied') {
          this.log(sessionKey, conn.conversationId, 'RECONNECT_EXHAUSTED', `status=${conn.status}`);
          // Le tour ne reprendra pas. Libérer ici son pin EXACT évite de garder
          // un résident local jusqu'à une destruction qui peut ne jamais venir.
          this.releaseDocumentTurnPin(conn, conn.currentLocalTurnId, 'reconnexion épuisée');
          const timeline = this.getTimeline(conn);
          this.updateTimeline(conn, [
            ...timeline,
            { type: 'agent_error' as const, id: `err-reconnect-${Date.now()}`, message: 'La connexion au serveur a échoué. Vous pouvez relancer votre demande.', source: 'reconnect' },
          ]);
          this.resolvePendingMessageAcceptance(conn, false);
          this.setStatus(conn, 'error');
        }
        this.events.emit('connection_changed', sessionKey, false);
      },
      connect: () => this.attemptReconnect(sessionKey),
    });

    this.connections.set(sessionKey, conn);
    this.timelineCache.set(sessionKey, []);

    // WHY no conversation_confirmed here: this emission previously fired before the DB
    // row existed. With phantom conversations, the SessionBridge handler removes the
    // phantom on conversation_confirmed — doing it here would remove the phantom
    // before the backend creates the row, causing a sidebar flash.
    // The backend's conversation_created event (handled below in wireHandlers) now
    // fires conversation_confirmed at the right time (after DB INSERT).

    // Wire WS event handlers before connecting
    this.wireHandlers(conn);

    this.log(sessionKey, conversationId, 'OPEN', `caseId=${caseId ?? 'none'}`);

    try {
      await client.connect();
      this.setStatus(conn, 'connecting'); // stays connecting until connection_established
      return sessionKey;
    } catch (err) {
      ErrorService.handle(err, { context: { action: 'ws_pool_open' }, silent: true });
      this.cleanup(sessionKey);
      return null;
    }
  }

  /**
   * Ouvre une session pour REPRENDRE un tour que cette page n'a pas lancé (F5, second
   * onglet, autre poste) : la conversation a un message user sans réponse finale en base.
   * À l'ACK d'authentification, la socket envoie `session_resume` et se met en `streaming`
   * (composer fermé). Trois issues possibles, toutes gérées par les handlers existants :
   * adoption (même instance, tampon intact) → rejeu puis direct ; `turn_in_progress` →
   * note puis `turn_settled` → rechargement ; `resumed:false` → rechargement.
   * `localTurnId` est le tour local que l'hôte a ouvert (beginTurn) pour projeter la
   * timeline : sans tour local, rien ne s'affiche (buildTranscriptExchanges).
   */
  async openSessionForResume(
    userId: string,
    conversationId: string,
    caseId: string | undefined,
    caseContextMode: CaseContextMode,
    localTurnId: string,
  ): Promise<string | null> {
    const existing = [...this.connections.entries()].find(([, c]) => c.conversationId === conversationId);
    if (existing) {
      // Une session vit déjà pour cette conversation : elle porte son propre état, la
      // reprise au chargement n'a pas lieu d'être. On refuse plutôt que de
      // désynchroniser le tour local que l'appelant vient d'ouvrir.
      this.log(existing[0], conversationId, 'RESUME_ON_LOAD_SKIPPED', 'a session already exists for this conversation');
      return null;
    }
    const sessionKey = await this.openSession(userId, conversationId, caseId, caseContextMode);
    if (!sessionKey) return null;
    const conn = this.connections.get(sessionKey);
    if (!conn) return null;
    conn.resumeOnEstablish = true;
    conn.currentLocalTurnId = localTurnId;
    conn.resumedOnLoadTurnId = localTurnId;
    conn.catchup = { turnId: localTurnId, entries: [] };
    this.log(sessionKey, conversationId, 'RESUME_ON_LOAD', `localTurnId=${localTurnId}`);
    return sessionKey;
  }

  /**
   * Send a user message on an existing session. If the WebSocket is disconnected,
   * the message is queued and a reconnection is attempted.
   */
  async sendMessage(
    sessionKey: string,
    content: string,
    toolsConfig: ToolsConfig,
    options: SendMessageOptions,
  ): Promise<void> {
    const conn = this.connections.get(sessionKey);
    if (!conn) {
      console.error('[WebSocketPool] sendMessage: session not found', sessionKey, 'available:', [...this.connections.keys()]);
      return;
    }
    const turnCaseId = this.resolveRequestedCaseId(conn, options.caseId);
    if (conn.caseContextMode === 'host_selectable' && !turnCaseId) {
      throw new Error(
        `WebSocketPool.sendMessage cannot send host_selectable session "${sessionKey}" without a dossier. Reopen the session with its host case before sending.`,
      );
    }

    // The backend accepts exactly one exchange per conversation. `pendingMessage`
    // is therefore not a queue: it is the one unsent user turn held while auth or
    // a reconnect completes. Resetting the reducer before this guard used to let a
    // second click overwrite that turn (and its case) silently. Reject at the
    // transport boundary as well as in the composer, because callers outside React
    // must not be able to bypass the one-exchange protocol.
    if (conn.pendingMessage) {
      throw new Error(
        `WebSocketPool.sendMessage rejected for conversation "${conn.conversationId}": it already has a pending user turn while the connection is being established or restored. Wait for that turn to start before sending another message.`,
      );
    }
    if (isExchangeActive(conn.status)) {
      throw new Error(
        `WebSocketPool.sendMessage rejected for conversation "${conn.conversationId}": an exchange is already streaming. Wait for the agent to finish or stop the current turn before sending another message.`,
      );
    }
    // Même frontière transport que ci-dessus : pendant une compaction manuelle le
    // backend tient le verrou de conversation, et un tour en attente d'ACK
    // partirait sur la même socket que la compaction qu'il bloquerait.
    if (conn.contextCompaction.phase !== 'idle') {
      throw new Error(
        `WebSocketPool.sendMessage rejected for conversation "${conn.conversationId}": a manual memory compaction is in progress (phase=${conn.contextCompaction.phase}). The backend holds the conversation lock and would answer conversation_busy; wait for the compaction verdict before sending.`,
      );
    }

    // Reset reducer for a new exchange
    conn.reducer.uncancel();
    conn.reducer.reset();
    conn.currentLocalTurnId = options.localTurnId ?? null;
    conn.catchup = null;
    // Un nouveau tour repart de zéro : une attente `conversation_busy` d'un tour
    // précédent ne doit pas rendre le brouillon de CE tour sur un `turn_settled` légitime.
    conn.busyAwaitingSettle = false;
    conn.remoteTurnAwaited = false;
    this.updateTimeline(conn, []);

    this.log(sessionKey, conn.conversationId, 'SEND', `status=${conn.status} connected=${conn.client.isConnected()}`);

    const modelPreference = getCaseDiscoveryModelPreference();

    if (conn.status === 'connecting') {
      // WS is open but backend hasn't confirmed auth yet (connection_established
      // not received). Queue the message — it will be sent automatically when
      // the connection_established handler fires.
      conn.pendingMessage = {
        content,
        toolsConfig,
        attachedDocuments: options.attachments,
        modelPreference,
        localTurnId: options.localTurnId,
        caseId: turnCaseId,
        caseChanges: options.caseChanges,
        caseIsAlreadyPrincipal: options.caseIsAlreadyPrincipal,
        // The host may reuse and mutate its array on a later render. Keep the
        // authoritative capabilities coupled to the queued turn that captured them.
        uiActionCapabilities: [...options.uiActionCapabilities],
      };
    } else if (conn.client.isConnected()) {
      this.sendMessageDirect(conn, content, toolsConfig, {
        ...options,
        modelPreference,
        caseId: turnCaseId,
        uiActionCapabilities: [...options.uiActionCapabilities],
      });
    } else {
      // Queue and reconnect
      conn.pendingMessage = {
        content,
        toolsConfig,
        attachedDocuments: options.attachments,
        modelPreference,
        localTurnId: options.localTurnId,
        caseId: turnCaseId,
        caseChanges: options.caseChanges,
        caseIsAlreadyPrincipal: options.caseIsAlreadyPrincipal,
        uiActionCapabilities: [...options.uiActionCapabilities],
      };
      conn.reconnector.resetExhausted();
      conn.reconnector.attempt();
    }
  }

  /**
   * Sends a user message and waits for the backend's explicit acceptance.
   *
   * A WebSocket write only proves that the browser handed bytes to its socket.
   * `user_message_accepted` is emitted only after the backend persists this
   * exact turn; an `error` frame or destroyed session rejects it. The composer
   * uses this before discarding its local draft and files.
   */
  async sendMessageAndWaitForAcceptance(
    sessionKey: string,
    content: string,
    toolsConfig: ToolsConfig,
    options: SendMessageOptions,
  ): Promise<boolean> {
    const conn = this.connections.get(sessionKey);
    if (!conn) {
      throw new Error(`WebSocketPool.sendMessageAndWaitForAcceptance: session "${sessionKey}" not found.`);
    }
    if (conn.pendingMessageAcceptance) {
      throw new Error(
        `WebSocketPool.sendMessageAndWaitForAcceptance rejected for conversation "${conn.conversationId}": another user turn is still awaiting backend acceptance.`,
      );
    }

    const acceptance = new Promise<boolean>((resolve) => {
      conn.pendingMessageAcceptance = resolve;
    });
    try {
      await this.sendMessage(sessionKey, content, toolsConfig, options);
    } catch (error) {
      this.resolvePendingMessageAcceptance(conn, false);
      throw error;
    }
    return acceptance;
  }

  /**
   * Demande au backend de compacter la mémoire de la conversation, HORS tour.
   *
   * La trame porte le même `case_id` et le même `tools_config` qu'un
   * `user_message` de ce composer : une socket fraîche ne connaît rien de la
   * conversation avant son premier tour, et le backend doit la reconstruire à
   * l'identique pour recompter les tokens.
   *
   * Tant que la socket courante n'a pas répondu sur la capacité (conversation
   * rouverte, authentification ou reconnexion en cours), la demande attend l'ACK
   * générique puis part — ou se solde par `refused` si ce backend ne connaît pas
   * la trame. Une socket fermée est relancée, comme pour un message en attente.
   *
   * ⚠️ Ne passe JAMAIS la session en `streaming` : l'état se lit dans
   * `context_compaction_changed` (`running` dès l'appel, puis un terminal suivi
   * de `idle`). Pendant la compaction le backend tient le verrou de conversation
   * — un `user_message` concurrent recevrait `conversation_busy` —, c'est
   * pourquoi l'envoi est refusé tant qu'elle n'est pas soldée.
   *
   * @returns `true` quand le pool prend la demande en charge : son issue sera
   * publiée, y compris un refus. `false` — journalisé avec le motif, rien de
   * publié — quand la session est inconnue, qu'un tour est en cours ou en
   * attente d'envoi, ou qu'une compaction n'est pas soldée.
   */
  compactContext(
    sessionKey: string,
    toolsConfig: ToolsConfig,
    options: { caseId?: string | null },
  ): boolean {
    const conn = this.connections.get(sessionKey);
    if (!conn) {
      console.warn(`[WebSocketPool] compactContext refusée — session ${sessionKey} inconnue du pool (fermée, évincée ou jamais ouverte).`);
      return false;
    }
    const refusal = this.describeCompactContextRefusal(conn);
    if (refusal) {
      this.log(sessionKey, conn.conversationId, 'COMPACT_CONTEXT_REFUSED', refusal);
      return false;
    }

    const caseId = this.resolveRequestedCaseId(conn, options.caseId);
    if (conn.socketCompactContext === 'unsupported') {
      this.log(sessionKey, conn.conversationId, 'COMPACT_CONTEXT_UNSUPPORTED', 'socket courante sans capacité compactContext');
      this.settleContextCompaction(conn, { status: 'refused', userMessage: COMPACTION_UNSUPPORTED_MESSAGE }, { historyStale: false });
      return true;
    }
    if (conn.socketCompactContext === 'supported' && conn.client.isConnected()) {
      this.sendCompactContextFrame(conn, toolsConfig, caseId);
      return true;
    }

    conn.contextCompaction = { phase: 'awaiting_socket', toolsConfig, caseId };
    // Le rond tourne et l'envoi se bloque dès le clic, y compris pendant la
    // connexion : un double clic enverrait sinon deux compactions.
    this.publishContextCompaction(conn, { status: 'running' });
    this.log(
      sessionKey,
      conn.conversationId,
      'COMPACT_CONTEXT_QUEUED',
      `capacité inconnue — attend l'ACK générique (status=${conn.status} connected=${conn.client.isConnected()})`,
    );
    if (!conn.client.isConnected() && conn.status !== 'connecting') {
      conn.reconnector.resetExhausted();
      void conn.reconnector.attempt();
    }
    return true;
  }

  /**
   * Cancel the active stream on a session.
   * Sends `user_interrupt` and marks in-progress tools as errored.
   *
   * @returns `true` si l'interruption est réellement partie. **Le lire quand
   * l'appelant a promis un arrêt à l'avocat** (#855) : sans connexion, cette
   * méthode ne fait rien du tout — rien n'est envoyé, le pin exact n'est pas libéré,
   * et un appelant qui suppose le succès laisse le canvas voilé au nom d'un
   * travail qu'il croit avoir arrêté. Le bouton « Arrêter la génération » du
   * chat, lui, peut l'ignorer : il ne promet pas d'état d'arrivée.
   */
  cancelStream(
    sessionKey: string,
    expectedCanvasWork?: CanvasWorkIdentity,
  ): boolean {
    const conn = this.connections.get(sessionKey);
    let turnIdToRelease = conn?.currentLocalTurnId ?? null;
    if (conn && expectedCanvasWork) {
      const binding = expectedCanvasWork.jobId === null
        ? null
        : redactionStreamHub.lireLiaison(expectedCanvasWork.jobId);
      // ⚠️ **C'est le TRAVAIL qui départage, ni un document ni un montage**
      // (#1336, arbitrage du 2026-09-10). Le fil ne peut plus changer de
      // document — la conversation EST sa clé —, donc « ce fil porte-t-il
      // encore le document que le geste a capturé ? » ne pouvait plus échouer.
      // L'époque de montage, essayée à sa place, confrontait une valeur figée
      // au montage COURANT : elle refusait un arrêt LÉGITIME dès qu'un
      // remontage — que l'écoute traverse par construction (#857) — s'était
      // produit sous un travail toujours vivant. Ce qui périme réellement un
      // geste d'arrêt, c'est que son TRAVAIL soit fini ou remplacé, et c'est
      // exactement ce que `redactionTurnIdByJob` sait dire. Le raisonnement
      // complet, et ce que l'époque gardait ailleurs, sont dans le TSDoc de
      // `CanvasWorkIdentity`.
      const hasCapturedTurn = expectedCanvasWork.jobId === null
        ? conn.currentLocalTurnId !== null
        : conn.redactionTurnIdByJob.has(expectedCanvasWork.jobId);
      const exactJob = expectedCanvasWork.jobId === null
        ? hasCapturedTurn
        : binding?.conversationId === expectedCanvasWork.conversationId
          && hasCapturedTurn;
      const exact = conn.conversationId === expectedCanvasWork.conversationId
        && exactJob;
      if (!exact) {
        this.log(
          sessionKey,
          conn.conversationId,
          'CANCEL_IDENTITY_REFUSED',
          `expected=${expectedCanvasWork.conversationId}/${expectedCanvasWork.jobId} `
          + `socket=${conn.conversationId} `
          + `bound=${binding?.conversationId ?? 'none'} `
          + `capturedTurn=${hasCapturedTurn ? 'yes' : 'no'}`,
        );
        return false;
      }
      turnIdToRelease = expectedCanvasWork.jobId === null
        ? conn.currentLocalTurnId
        : conn.redactionTurnIdByJob.get(expectedCanvasWork.jobId) ?? null;
    }
    if (!conn || !conn.client.isConnected()) {
      this.log(sessionKey, conn?.conversationId ?? 'unknown', 'CANCEL_IMPOSSIBLE',
        conn ? 'connexion perdue — aucune interruption envoyée' : 'session inconnue du pool');
      return false;
    }
    if (conn.status !== 'streaming') {
      // Le premier clic bascule synchroniquement la session à `ready`. Un
      // second événement DOM du même geste ne doit donc pas émettre une autre
      // interruption ni libérer une seconde fois le pin d'un tour plus récent.
      this.log(
        sessionKey,
        conn.conversationId,
        'CANCEL_IGNORED',
        `status=${conn.status} — aucun tour actif à interrompre`,
      );
      return false;
    }

    conn.client.send({
      type: 'user_interrupt',
      conversation_id: conn.conversationId,
      reason: 'user_cancelled',
    });

    const timeline = this.getTimeline(conn);
    const updated = conn.reducer.cancel(timeline);
    this.updateTimeline(conn, updated);
    // Un rattrapage encore en vol ne doit pas rouvrir un tour que l'avocat vient d'arrêter (#1888).
    conn.catchup = null;
    // Le job capture le tour à son démarrage. `currentLocalTurnId` est une
    // projection transport mutable : un terminal concurrent peut la vider
    // avant le clic Stop. On libère donc le handle figé, pas une session
    // résolue à nouveau ni une projection globale.
    this.releaseDocumentTurnPin(conn, turnIdToRelease, 'tour interrompu par l\'avocat');
    if (expectedCanvasWork?.jobId) conn.redactionTurnIdByJob.delete(expectedCanvasWork.jobId);
    this.setStatus(conn, 'ready');
    this.log(sessionKey, conn.conversationId, 'CANCEL', 'user cancelled stream');
    return true;
  }

  /**
   * Stops a Canvas turn from its immutable document identity, not from a
   * React/Zustand mirror of the WebSocket session key.
   *
   * Canvas residents survive navigation while the sidebar session projection
   * can briefly lag or be rebuilt. The pool owns the live connection map, so
   * resolving the key here keeps the UI out of transport bookkeeping. The
   * exact identity is still checked by `cancelStream` before any interrupt is
   * emitted, which prevents A's visible control from stopping B.
   */
  cancelCanvasWork(expectedCanvasWork: CanvasWorkIdentity): boolean {
    const entry = [...this.connections.entries()].find(
      ([, connection]) => connection.conversationId === expectedCanvasWork.conversationId,
    );
    if (!entry) return false;
    return this.cancelStream(entry[0], expectedCanvasWork);
  }

  /**
   * Send the user's validated jurisprudence questions back to the backend.
   * Transitions the form to progress at 0% locally.
   *
   * WHY no status change: JP form display is a sub-state of 'streaming' —
   * the session never left streaming when the form appeared.
   */
  sendJurisprudenceResponse(
    sessionKey: string,
    jobId: string,
    questions: LegalQuestion[],
    jurisdictions: Jurisdiction[],
    fondements: FondementJuridique[],
  ): void {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return;

    // Store questions for progress display
    conn.reducer.setJurisprudenceQuestions(jobId, questions);

    conn.client.send({
      type: 'jurisprudence_search_response',
      conversation_id: conn.conversationId,
      payload: { jobId, questions, jurisdictions, fondements },
    } as FrontendMessage);

    // Locally transition form -> progress at 0%
    const progressQuestions: QuestionProgress[] = questions.map(q => ({
      id: q.id,
      questionText: q.text,
      progress: 0,
      phase: 'fondement_enrichment' as const,
    }));

    const timeline = this.getTimeline(conn);
    const updated = timeline.map(item =>
      item.type === 'jurisprudence_form' && item.jobId === jobId
        ? { type: 'jurisprudence_progress' as const, id: item.id, jobId, questions: progressQuestions }
        : item,
    );
    this.updateTimeline(conn, updated);
    this.log(sessionKey, conn.conversationId, 'JP_SUBMIT', `jobId=${jobId} questions=${questions.length}`);
  }

  /**
   * Envoie le clic Lancer. La carte attend l'accusé autoritatif du backend
   * avant de muter, comme le lancement automatique.
   */
  sendRedactionPlanResponse(
    sessionKey: string,
    payload: RedactionPlanResponsePayload,
  ): boolean {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return false;

    if (payload.accepted) {
      // Same socket, ordered frames: the collector receives the newest edited
      // snapshot before it resolves the manual launch decision.
      this.flushRedactionPlanDraftUpdate(conn, payload.requestId, 'manual launch');
    } else {
      this.clearRedactionPlanDraftFlush(conn, payload.requestId);
    }

    conn.client.send({
      type: 'redaction_plan_response',
      conversation_id: conn.conversationId,
      payload,
    } as FrontendMessage);

    if (payload.accepted) {
      // NB : NE PAS ouvrir le canvas ici — c'est le moment du clic Lancer,
      // pendant lequel le worker n'a même pas démarré. L'auto-ouverture vit
      // dans `useRedactionStreamingState` au 1er bloc SSE non-vide.
      // NE PAS enregistrer ni écouter le travail ici :
      // L'endpoint SSE /api/jobs/:jobId/stream-plan-detaille refuse toute
      // connexion tant que job.status !== 'completed' (400 BadRequest). Ouvrir
      // EventSource immédiatement au clic Lancer → 400 → EventSource meurt →
      // le canvas reste vide pour toujours.
      // L'abonnement SSE doit attendre l'event `redaction_job_started` que le
      // backend émet APRÈS completion (cf. RedactionJobCollector.collect,
      // sendRedactionJobStarted déplacé post-waitForCompletion). Ce handler
      // `handler.on('redaction_job_started', ...)` plus bas acquiert sa session
      // puis ouvre le flux au bon moment.
      this.log(
        sessionKey,
        conn.conversationId,
        'REDACTION_LAUNCH',
        `requestId=${payload.requestId} canvas_admission=awaited_before_enqueue`,
      );
    } else {
      this.log(sessionKey, conn.conversationId, 'REDACTION_DECLINE', `requestId=${payload.requestId}`);
    }
    return true;
  }

  /** Sends the first draft revision immediately, then coalesces subsequent edits. */
  sendRedactionPlanDraftUpdate(
    sessionKey: string,
    requestId: string,
    snapshot: RedactionPlanDraftSnapshot,
  ): boolean {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return false;
    const timeline = this.getTimeline(conn);
    const form = timeline.find((item) => item.type === 'redaction_plan_form' && item.requestId === requestId);
    if (!form || form.type !== 'redaction_plan_form') return false;
    const hadUnacknowledgedDraft = form.autoLaunchDraftUpdatePending !== undefined;
    const revision = Math.max(form.autoLaunchRevision ?? 0, form.autoLaunchDraftUpdatePending?.revision ?? 0) + 1;
    const payload = { requestId, revision, snapshot };
    this.updateTimeline(conn, conn.reducer.applyRedactionPlanDraftUpdated(timeline, payload));
    if (!hadUnacknowledgedDraft) {
      this.sendRedactionPlanDraftUpdateFrame(conn, payload);
    } else {
      this.scheduleRedactionPlanDraftFlush(conn, payload);
    }
    return true;
  }

  /** Annulation via clic Annuler — backend l'attend pour rejeter le resolver. */
  sendRedactionPlanCancellation(sessionKey: string, requestId: string): boolean {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return false;

    this.clearRedactionPlanDraftFlush(conn, requestId);

    conn.client.send({
      type: 'redaction_plan_cancelled',
      conversation_id: conn.conversationId,
      payload: { requestId },
    } as FrontendMessage);

    const timeline = this.getTimeline(conn);
    const updated = conn.reducer.applyRedactionPlanCancelled(timeline, requestId);
    this.updateTimeline(conn, updated);
    this.log(sessionKey, conn.conversationId, 'REDACTION_CANCEL', `requestId=${requestId}`);
    return true;
  }

  /**
   * Emits a physical frame. Timeline state is updated before this call so a
   * dropped connection can replay exactly the revision the backend missed.
   */
  private sendRedactionPlanDraftUpdateFrame(
    conn: PoolConnection,
    payload: RedactionPlanDraftUpdatePayload,
    eventName = 'REDACTION_PLAN_DRAFT_UPDATED',
  ): void {
    const message: FrontendMessage = conn.supportsRedactionPlanDraftUpdates
      ? {
        type: 'redaction_plan_draft_updated',
        conversation_id: conn.conversationId,
        payload,
      }
      : {
        type: 'redaction_plan_interaction',
        conversation_id: conn.conversationId,
        payload: { requestId: payload.requestId },
      };
    conn.client.send(message);
    this.log(
      conn.sessionKey,
      conn.conversationId,
      eventName,
      `requestId=${payload.requestId} revision=${payload.revision}`,
    );
  }

  /** Holds only the newest snapshot; the first snapshot was already sent. */
  private scheduleRedactionPlanDraftFlush(
    conn: PoolConnection,
    payload: RedactionPlanDraftUpdatePayload,
  ): void {
    const existing = conn.redactionPlanDraftFlushes.get(payload.requestId);
    if (existing) {
      existing.payload = payload;
      clearTimeout(existing.trailingTimer);
      existing.trailingTimer = setTimeout(
        () => this.flushRedactionPlanDraftUpdate(conn, payload.requestId, 'trailing debounce'),
        REDACTION_PLAN_DRAFT_TRAILING_MS,
      );
      return;
    }

    const pending: PendingRedactionPlanDraftFlush = {
      payload,
      trailingTimer: setTimeout(
        () => this.flushRedactionPlanDraftUpdate(conn, payload.requestId, 'trailing debounce'),
        REDACTION_PLAN_DRAFT_TRAILING_MS,
      ),
      maxWaitTimer: setTimeout(
        () => this.flushRedactionPlanDraftUpdate(conn, payload.requestId, 'bounded continuous typing'),
        REDACTION_PLAN_DRAFT_MAX_WAIT_MS,
      ),
    };
    conn.redactionPlanDraftFlushes.set(payload.requestId, pending);
  }

  /** Flushes before a manual decision or after the short bounded batching window. */
  private flushRedactionPlanDraftUpdate(
    conn: PoolConnection,
    requestId: string,
    reason: 'manual launch' | 'trailing debounce' | 'bounded continuous typing',
  ): boolean {
    const pending = conn.redactionPlanDraftFlushes.get(requestId);
    if (!pending) return true;
    this.clearRedactionPlanDraftFlush(conn, requestId);
    if (!conn.client.isConnected()) return false;
    this.sendRedactionPlanDraftUpdateFrame(conn, pending.payload);
    this.log(conn.sessionKey, conn.conversationId, 'REDACTION_PLAN_DRAFT_FLUSHED', `requestId=${requestId} reason=${reason}`);
    return true;
  }

  private clearRedactionPlanDraftFlush(conn: PoolConnection, requestId: string): void {
    const pending = conn.redactionPlanDraftFlushes.get(requestId);
    if (!pending) return;
    clearTimeout(pending.trailingTimer);
    clearTimeout(pending.maxWaitTimer);
    conn.redactionPlanDraftFlushes.delete(requestId);
  }

  private clearAllRedactionPlanDraftFlushes(conn: PoolConnection): void {
    for (const requestId of conn.redactionPlanDraftFlushes.keys()) {
      this.clearRedactionPlanDraftFlush(conn, requestId);
    }
  }

  /**
   * Terminal HTTP/SSE observé par le frontend : le flux du canvas latéral
   * (/api/jobs/:jobId/stream-plan-detaille pour research,
   * /api/jobs/:jobId/stream pour generation) a atteint son end-of-stream. Un
   * succès reste PROVISOIRE : le dernier delta peut encore être mis en file par
   * React. Il ne devient `redaction_sse_complete` qu'après l'événement runtime
   * émis par `PointDEcritureCanvas.auRepos()`; erreur et annulation restent
   * immédiatement terminales.
   *
   * Cette même clôture termine la carte, libère l'admission et renvoie le
   * résultat au `RedactionJobCollector`, afin que l'écran et l'agent partagent
   * strictement la même définition de « terminé ».
   *
   * Appelé par `redactionStreamHub` quand une écoute se termine (#857).
   */
  notifyRedactionSseComplete(
    sessionKey: string,
    jobId: string,
    status: 'complete' | 'error' | 'cancelled' = 'complete',
  ): void {
    const conn = this.connections.get(sessionKey);
    if (!conn) return;
    const key = this.redactionSseCompletionKey(conn.conversationId, jobId);

    // Une erreur ou une annulation ne prétend jamais que l'insertion est
    // complète : il faut rendre la main immédiatement avec ce verdict. Le cas
    // réussi, lui, attend la preuve physique ci-dessous.
    if (status !== 'complete') {
      // Un terminal négatif ultérieur prévaut sur un `complete` réseau qui
      // attendait encore l'écrivain : ne jamais envoyer un second terminal si
      // une file abandonnée se signale après l'annulation.
      this.pendingRedactionSseCompletions.delete(key);
      this.completeRedactionSseTerminal(sessionKey, jobId, status);
      return;
    }

    if (canvasDocumentRuntime.wasStreamInsertionSettled(conn.conversationId, jobId)) {
      this.completeRedactionSseTerminal(sessionKey, jobId, status);
      return;
    }

    if (this.pendingRedactionSseCompletions.has(key)) return;
    this.pendingRedactionSseCompletions.set(key, {
      conversationId: conn.conversationId,
      jobId,
      status,
    });
    this.log(
      sessionKey,
      conn.conversationId,
      'REDACTION_SSE_WAITING_INSERTION',
      `jobId=${jobId} terminal HTTP reçu ; attente de PointDEcritureCanvas.auRepos()`,
    );
  }

  /** Réalise l'ancien terminal une fois l'insertion attestée ou en échec. */
  private completeRedactionSseTerminal(
    sessionKey: string,
    jobId: string,
    status: 'complete' | 'error' | 'cancelled',
  ): void {
    for (const [key, pin] of this.canvasAdmissionPins) {
      if (pin.jobId !== jobId) continue;
      pin.release();
      this.canvasAdmissionPins.delete(key);
    }
    const conn = this.connections.get(sessionKey);
    if (!conn) return;
    // 1) Update local timeline (flip progress card → results si verdict bufferisé)
    const timeline = this.getTimeline(conn);
    const updated = conn.reducer.applyRedactionPlanSseComplete(timeline, jobId);
    this.updateTimeline(conn, updated);
    // 2) Signal backend → débloque le tool result vers le LLM. Sans ça l'agent
    //    reprend la parole pendant que le canvas s'écrit (cf. RedactionJobCollector
    //    `waitForSseComplete`). Mirror Word add-in.
    if (conn.client.isConnected()) {
      const liaison = redactionStreamHub.lireLiaison(jobId);
      conn.client.send({
        type: 'redaction_sse_complete',
        conversation_id: conn.conversationId,
        payload: {
          jobId,
          status,
          // ⚠️ **Le COUPLE ou RIEN, jamais une moitié** (#1336). La forme
          // conversation exige `mountEpoch` : une conversation annoncée sans
          // époque est refusée par le contrat (`epoque_manquante`), et
          // l'attente du collector expirerait au bout de huit minutes.
          // Une identité totalement absente, elle, vaut « pas de contrainte »
          // côté `RedactionJobCollector` — ce qui est exact ici et seulement
          // ici : la liaison ne porte d'époque que lorsqu'un reçu d'admission
          // en a fourni une, et sans admission le collector n'a lui-même aucun
          // montage attendu à opposer.
          ...(liaison && liaison.mountEpoch !== undefined
            ? { conversationId: liaison.conversationId, mountEpoch: liaison.mountEpoch }
            : {}),
        },
      } as FrontendMessage);
    }
    this.log(sessionKey, conn.conversationId, 'REDACTION_SSE_COMPLETE', `jobId=${jobId} status=${status}`);
  }

  /**
   * Le Canvas vient de vider la file de CE job. On rejoue le terminal par la
   * recherche conversationnelle : la socket initiale a pu être reconnectée
   * pendant l'écriture, et seule une connexion vivante peut réveiller le
   * `RedactionJobCollector` backend.
   */
  private finishPendingRedactionSseCompletion(event: {
    conversationId: string;
    mountEpoch: number;
    jobId: string;
  }): void {
    const key = this.redactionSseCompletionKey(event.conversationId, event.jobId);
    const pending = this.pendingRedactionSseCompletions.get(key);
    if (!pending) return;
    this.pendingRedactionSseCompletions.delete(key);
    this.completeRedactionSseTerminalForConversation(
      pending.conversationId,
      pending.jobId,
      pending.status,
    );
  }

  private redactionSseCompletionKey(conversationId: string, jobId: string): string {
    return JSON.stringify([conversationId, jobId]);
  }

  /**
   * L'événement Canvas est déjà la preuve d'insertion : ne pas repasser par
   * `notifyRedactionSseComplete`, qui traite un terminal HTTP brut et le
   * remettrait à tort en attente. Une reconnexion reste admise ici.
   */
  private completeRedactionSseTerminalForConversation(
    conversationId: string,
    jobId: string,
    status: 'complete' | 'error' | 'cancelled',
  ): void {
    let repli: string | null = null;
    for (const [sessionKey, conn] of this.connections) {
      if (conn.conversationId !== conversationId) continue;
      if (conn.client.isConnected()) {
        this.completeRedactionSseTerminal(sessionKey, jobId, status);
        return;
      }
      repli = sessionKey;
    }
    if (repli) {
      this.completeRedactionSseTerminal(repli, jobId, status);
    }
    console.warn(
      `[redaction] insertion terminée pour le job ${jobId}, mais sa fin ne peut pas être annoncée au serveur : `
      + (repli
        ? `la connexion de la conversation ${conversationId} est coupée (reconnexion en cours). `
        : `aucune connexion pour la conversation ${conversationId} qui l'a lancé. `)
      + 'Le backend attendra son signal (RedactionJobCollector, 8 min) avant de rendre la '
      + "main à l'agent.",
    );
  }

  /**
   * La même annonce, adressée à une **conversation** plutôt qu'à une clé de
   * session (#857).
   *
   * C'est la forme dont l'écoute a besoin : elle ne connaît que la conversation
   * qui a lancé le travail, jamais la clé opaque du pool — laquelle peut de
   * toute façon avoir changé entre le début du flux et sa fin. Sans connexion
   * pour cette conversation, on trace : le backend attend ce signal huit
   * minutes avant d'abandonner, et son absence est le genre de silence qu'on ne
   * diagnostique pas sans trace.
   */
  private notifyRedactionSseCompleteForConversation(
    conversationId: string,
    jobId: string,
    status: 'complete' | 'error' | 'cancelled',
  ): void {
    // Une connexion **présente** ne veut pas dire une connexion **vivante** :
    // une session en cours de reconnexion reste dans la table, et l'envoi de la
    // trame y est silencieusement sauté. On cherche donc d'abord une connexion
    // réellement connectée. Depuis #857 c'est le cas courant et non plus le cas
    // rare : le flux se termine pendant que l'avocat est ailleurs, quand la
    // connexion de ce fil est au repos ou en train de se rétablir.
    let repli: string | null = null;
    for (const [sessionKey, conn] of this.connections) {
      if (conn.conversationId !== conversationId) continue;
      if (conn.client.isConnected()) {
        this.notifyRedactionSseComplete(sessionKey, jobId, status);
        return;
      }
      repli = sessionKey;
    }
    if (repli) {
      // La frise locale doit quand même basculer — la carte de progression de
      // l'avocat ne peut pas rester en attente pour une raison de transport.
      this.notifyRedactionSseComplete(repli, jobId, status);
    }
    console.warn(
      `[redaction] fin du job ${jobId} non annoncée au serveur : `
      + (repli
        ? `la connexion de la conversation ${conversationId} est coupée (reconnexion en cours). `
        : `aucune connexion pour la conversation ${conversationId} qui l'a lancé. `)
      + 'Le backend attendra son signal (RedactionJobCollector, 8 min) avant de rendre la '
      + "main à l'agent.",
    );
  }

  /**
   * Cancel a pending jurisprudence validation and remove the form from the timeline.
   */
  sendJurisprudenceCancellation(sessionKey: string, jobId: string): void {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return;

    conn.client.send({
      type: 'jurisprudence_search_cancelled',
      conversation_id: conn.conversationId,
      jobId,
    } as FrontendMessage);

    const timeline = this.getTimeline(conn);
    const updated = timeline.filter(
      item => !(item.type === 'jurisprudence_form' && item.jobId === jobId),
    );
    this.updateTimeline(conn, updated);
    conn.reducer.removeJurisprudenceQuestions(jobId);
    this.log(sessionKey, conn.conversationId, 'JP_CANCEL', `jobId=${jobId}`);
  }

  /**
   * Send the user's explicit decision for a destructive Case Wiki reset.
   *
   * INVARIANT: `requestId` is echoed verbatim from `case_wiki_reset_request`.
   * A stale card therefore cannot authorize a newer reset.
   *
   * WHY no status change / no timeline mutation : la transition « en cours » est
   * un état LOCAL de la carte (pas de barres de progression à amorcer comme JP) ;
   * la session reste 'streaming' (l'agent reprend dès réception de la validation).
   *
   * @returns `true` si le message a réellement été émis sur un socket ouvert ;
   *   `false` si la session est inconnue ou le WS déconnecté. La carte DOIT
   *   tester ce retour avant de passer en « En cours de traitement » : un envoi
   *   silencieusement abandonné (déconnexion + reconnexion qui ne rejoue PAS le
   *   reset, seulement `session_resume`) laisserait le collector backend
   *   attendre 5 min dans le vide pendant que l'utilisateur croit avoir lancé.
   */
  sendCaseWikiResetResponse(
    sessionKey: string,
    payload: {
      caseId: string;
      accepted: boolean;
      requestId: string;
      caseWikiContextUserInputs?: string;
    },
  ): boolean {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return false;

    conn.client.send({
      type: 'case_wiki_reset_response',
      conversation_id: conn.conversationId,
      payload,
    } as FrontendMessage);

    this.log(
      sessionKey,
      conn.conversationId,
      'WIKI_RESET_RESPONSE',
      `requestId=${payload.requestId} accepted=${payload.accepted}`,
    );
    return true;
  }

  /**
   * Répond à une demande de suppression de pièces. `false` signifie que le
   * socket n'a rien transmis : la carte reste alors cliquable et n'invente pas
   * une suppression en cours.
   */
  sendDeletePiecesConfirmationResponse(
    sessionKey: string,
    payload: { caseId: string; accepted: boolean; requestId: string },
  ): boolean {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return false;
    conn.client.send({
      type: 'delete_pieces_confirmation_response',
      conversation_id: conn.conversationId,
      payload,
    } as FrontendMessage);
    this.log(
      sessionKey,
      conn.conversationId,
      'DELETE_PIECES_RESPONSE',
      `requestId=${payload.requestId} accepted=${payload.accepted}`,
    );
    return true;
  }

  /** Send one exact shared skill consent frame; disconnected responses are not queued. */
  sendSkillChangeResponse(
    sessionKey: string,
    response: Omit<SkillChangeResponse, 'type'>,
  ): boolean {
    const conn = this.connections.get(sessionKey);
    if (!conn || !conn.client.isConnected()) return false;
    const frame: SkillChangeResponse = {
      type: 'skill_change_response',
      ...response,
    };
    conn.client.send(frame);
    this.log(
      sessionKey,
      conn.conversationId,
      'SKILL_CHANGE_RESPONSE',
      `requestId=${response.requestId} accepted=${response.accepted}`,
    );
    return true;
  }

  /**
   * Manually retry after reconnection exhaustion.
   */
  retryConnection(sessionKey: string): void {
    const conn = this.connections.get(sessionKey);
    if (!conn) return;
    console.info(`[WebSocketPool] retryConnection — status=${conn.status} connected=${conn.client.isConnected()} session=${sessionKey}`);
    conn.reconnector.resetExhausted();
    conn.reconnector.attempt();
  }

  /**
   * Close a single session: disconnect WS, clear timers, remove from pool.
   */
  closeSession(sessionKey: string): void {
    this.cleanup(sessionKey);
  }

  /**
   * Ferme la connexion d'une conversation SUPPRIMÉE, et ses reprises (#1334).
   *
   * `cleanup` est ce qui coupe réellement : il détruit le `SessionReconnector`,
   * donc les tentatives programmées, rend les pins de tour et les admissions
   * documentaires, et retire le slot du pool. Sans cet appel, la socket d'un fil
   * effacé en base restait ouverte, se reconnectait, et ses handlers pouvaient
   * encore acquérir une session documentaire.
   *
   * @returns `false` quand aucun slot ne portait ce fil — cas courant d'une
   * conversation jamais ouverte dans cet onglet, et pas une erreur.
   */
  closeConversation(conversationId: string): boolean {
    for (const [key, conn] of this.connections) {
      if (conn.conversationId !== conversationId) continue;
      this.log(key, conversationId, 'CLOSE', 'conversation supprimée par l’avocat');
      this.cleanup(key);
      return true;
    }
    return false;
  }

  /**
   * Close all sessions. Used on logout or navigation away.
   */
  closeAll(): void {
    for (const key of [...this.connections.keys()]) {
      this.cleanup(key);
    }
  }

  /**
   * Close all sessions and destroy the singleton.
   * Intended for HMR cleanup.
   */
  destroyAll(): void {
    this.closeAll();
    this.redactionCanvasOpenedForJobIds.clear();
    this.pendingRedactionSseCompletions.clear();
    this.signauxDEcritureCanvasEmis.clear();
    this.signauxDAdmissionCanvasEmis.clear();
    this.events.clear();
    this.unsubscribeCanvasReady?.();
    this.unsubscribeCanvasReady = null;
    this.unsubscribeCanvasInsertionSettled?.();
    this.unsubscribeCanvasInsertionSettled = null;
    for (const subscription of [...this.canvasResourceSubscriptions.values()]) subscription.unsubscribe();
    instance = null;
  }

  /**
   * Ouvre le Canvas au plus une fois pour un job de rédaction.
   *
   * La progression est le premier signal fiable après acceptation du job ; la
   * trame `redaction_job_started` reste le repli si cette progression n'atteint
   * pas cette connexion. Le hook SSE conserve son filet au premier bloc utile.
   */
  private openCanvasForRedactionJob(jobId: string, reason: string): void {
    if (this.redactionCanvasOpenedForJobIds.has(jobId)) return;
    this.redactionCanvasOpenedForJobIds.add(jobId);
    openCanvas(reason);
  }

  /**
   * Annonce que Hector prend le document de ce fil (#1588).
   *
   * Chaque appelant l'émet au dernier instant où il sait que ses refus
   * préparatoires sont levés, juste avant de déployer le canvas. L'ouverture
   * agent et l'admission de job attendent même que leur résidence soit prête :
   * un échec de recherche ou de montage ne doit jamais déplacer la scène vers
   * un fil qui n'a finalement rien pris. Le chargement DOCX l'émet avant
   * `openCanvas`, dont son montage dépend. Le dock y publie la scène de son fil
   * ; il ne doit pas la publier plus tôt (voir `SignalDAdmissionAgentCanvas`).
   */
  private signalerAdmissionAgentCanvas(
    conn: PoolConnection,
    options:
      | {
        readonly requiresVisibleSlot: true;
        readonly cause: 'canvas_load_template_request';
        readonly cleDIdempotence: string;
      }
      | { readonly requiresVisibleSlot?: never } = {},
  ): void {
    if (options.requiresVisibleSlot) {
      const cle = `${conn.conversationId}|${options.cleDIdempotence}`;
      if (this.signauxDAdmissionCanvasEmis.has(cle)) return;
      this.signauxDAdmissionCanvasEmis.add(cle);
      this.events.emit('agent_canvas_admission_started', {
        sessionKey: conn.sessionKey,
        conversationId: conn.conversationId,
        requiresVisibleSlot: true,
        cause: options.cause,
        cleDIdempotence: options.cleDIdempotence,
      });
      return;
    }
    this.events.emit('agent_canvas_admission_started', {
      sessionKey: conn.sessionKey,
      conversationId: conn.conversationId,
    });
  }

  /**
   * Annonce que Hector commence à écrire dans le canvas de ce fil (#1588).
   *
   * Le dock « Demander à Hector » n'offre aucun emplacement au canvas : sans ce
   * signal, l'agent y écrivait un acte que l'avocat ne voyait pas. Le dock
   * l'écoute et bascule vers l'assistant.
   *
   * Un signal de TRANSITION, et trois règles le font tel — ne pas les relâcher :
   * chaque appelant est placé APRÈS les refus de son transport (un canvas qui
   * n'a pas pu s'ouvrir n'a rien à montrer) ; aucune lecture n'émet (inspect,
   * état) ; et une clé déjà vue sur ce fil n'émet plus — une trame rejouée au
   * même `requestId`/`jobId` (doublon backend, reconnexion) ne rebascule pas
   * l'avocat qui est revenu au dock. L'hydratation REST d'un historique ne
   * passe par aucun handler.
   *
   * ⚠️ Une action et une génération émettent AVANT d'écrire, pour que l'avocat
   * voie l'écriture arriver : un refus de l'exécuteur ou du point d'écriture
   * tombe après le signal et laisse l'avocat devant le document inchangé. C'est
   * voulu, et c'est ce qui oblige `laisserLEcranRejoindreLeFil` à attendre
   * l'écran avant un remplacement.
   */
  private signalerEcritureAgentCanvas(
    conn: PoolConnection,
    cause: CauseDEcritureAgentCanvas,
    cleDIdempotence: string,
  ): void {
    const cle = `${conn.conversationId}|${cleDIdempotence}`;
    if (this.signauxDEcritureCanvasEmis.has(cle)) return;
    this.signauxDEcritureCanvasEmis.add(cle);
    this.events.emit('agent_canvas_write_started', {
      sessionKey: conn.sessionKey,
      conversationId: conn.conversationId,
      cause,
      cleDIdempotence,
    });
  }

  /**
   * Capture la graine depuis le scope exact de la conversation.
   *
   * Un job en arrière-plan ne doit hériter d'aucun document d'une autre
   * conversation : sans scope propre, le résident commence sur un document
   * vierge, et un nouveau document doit être explicitement chargé dans sa
   * session.
   *
   * Depuis #1333 il n'y a plus qu'une source possible — le scope du fil. Et
   * depuis #1337 la préparation d'une génération ne réécrit plus la
   * présentation en amont de la résidence : elle REMPLACE le document après la
   * capture du plan (`canvasAdmission.ts`). La graine lue ici est donc celle du
   * document courant, jamais d'un document à venir.
   */
  /**
   * La graine d'un fil qui écrit dans son propre document (`brouillon-du-fil`).
   *
   * L'amorce a déjà posé le brouillon enregistré s'il en existe un : une présentation absente
   * signifie donc que le fil n'a AUCUN document, et la page qui va naître est neuve. Elle part
   * alors du papier à en-tête demandé plutôt que du défaut du cabinet — sans jamais remplacer un
   * document qui existe.
   */
  private graineDuFilOuPageNeuve(conversationId: string, depart: CanvasDocumentDepart | undefined) {
    const graine = this.residentSeedForConversation(conversationId);
    if (graine.presentation !== null || depart === undefined) return graine;
    return { documentId: null, presentation: presentationDUnePageNeuve(depart) };
  }

  private residentSeedForConversation(conversationId: string) {
    const presentation = canvasDocumentRuntime.getScope(conversationId)?.getPresentation() ?? null;
    if (!presentation) return { documentId: null, presentation: null };
    return {
      documentId: presentation.templateMeta?.documentId ?? null,
      presentation,
    };
  }

  // ==========================================================================
  // Timeline cache — kept in sync by SessionBridge (Task 8)
  // ==========================================================================

  /**
   * Called by the SessionBridge to push the Zustand timeline back into the pool
   * so that handler callbacks can read the current timeline without depending
   * on React state.
   */
  updateTimelineCache(sessionKey: string, timeline: TimelineItem[]): void {
    this.timelineCache.set(sessionKey, timeline);
  }

  /** Get the cached timeline for a session. */
  getTimelineSnapshot(sessionKey: string): TimelineItem[] {
    return this.timelineCache.get(sessionKey) ?? [];
  }

  // ==========================================================================
  // Introspection
  // ==========================================================================

  /** Current status of a session. */
  getSessionStatus(sessionKey: string): SessionStatus | null {
    return this.connections.get(sessionKey)?.status ?? null;
  }

  /** Conversation ID for a session (always set — 1 WS = 1 conversation). */
  getConversationId(sessionKey: string): string | null {
    return this.connections.get(sessionKey)?.conversationId ?? null;
  }

  /** Number of slots currently occupied (connecting + streaming + ready). */
  getOccupiedSlotCount(): number {
    let count = 0;
    for (const conn of this.connections.values()) {
      const cat = getSessionCategory(conn.status);
      if (cat === 'occupied' || cat === 'standby') count++;
    }
    return count;
  }

  /** Whether any session is currently in a streaming state. */
  hasActiveStream(): boolean {
    for (const conn of this.connections.values()) {
      if (conn.status === 'streaming') return true;
    }
    return false;
  }

  /** All current session keys. */
  getSessionKeys(): string[] {
    return [...this.connections.keys()];
  }

  // ==========================================================================
  // Private — handler wiring
  // ==========================================================================

  /**
   * Wire all WebSocket message handlers for a session.
   * Each handler applies the appropriate reducer method and emits timeline updates
   * via PoolEventEmitter.
   *
   * Timer-free: no inactivity/idle/waiting timers. The backend owns all timeouts.
   */
  private wireHandlers(conn: PoolConnection): void {
    const { client, handler, sessionKey } = conn;

    // Pipe all WS messages to the typed handler.
    client.on('*', (message) => {
      // Safe cast: wildcard listeners receive the full parsed BackendMessage (not just payload)
      handler.handle(message as BackendMessage);
    });

    // -- Connection lifecycle --

    handler.on('connection_established', (_payload, message) => {
      // Lu AVANT d'armer le drapeau : `hasEstablished` encore faux = premier ACK de ce
      // slot, donc connexion initiale ; vrai = ce slot avait déjà une socket, donc
      // reconnexion, et le backend garde peut-être un MasterLoop à adopter.
      const isReconnection = conn.hasEstablished;
      conn.hasEstablished = true; // Tracks that this WS has authenticated at least once (for session_resume)
      // Capabilities belong to the current socket, not the session: reconnecting
      // during a rolling deploy may land on a different backend generation.
      conn.supportsRedactionPlanDraftUpdates = message.redaction_plan_draft_updates === true;
      conn.supportsUserMessageAcceptance = message.user_message_acceptance === true;
      // Seul l'ACK générique négocie : le verdict `{ resumed: false }` de
      // `session_resume` repasse par ce handler sur la MÊME socket, et rien ne
      // garantit qu'il répète les capacités — le lire effacerait une capacité acquise.
      if (message.resumed === undefined) {
        // La trame actuelle annonce au premier niveau. Le repli enveloppé
        // conserve la compatibilité ; un ACK sans capacité reste unsupported.
        this.setCompactContextCapability(
          conn,
          (message.capabilities ?? message.payload?.capabilities)?.compactContext === true ? 'supported' : 'unsupported',
        );
        conn.supportsCanvasOperationProgress = (message.capabilities ?? message.payload?.capabilities)?.canvasOperationProgress === true;
        conn.supportsCanvasOperationProbe = (message.capabilities ?? message.payload?.capabilities)?.canvasOperationProbe === true;
        // Une compaction lancée sur la socket perdue n'aura jamais de verdict sur
        // celle-ci. Le backend a pu la terminer ou non : retour au repos, et la
        // base REST tranche via le rechargement de l'historique.
        if (isReconnection && conn.contextCompaction.phase === 'awaiting_verdict') {
          this.abandonContextCompactionOnLostSocket(conn, 'socket reconnectée sans verdict');
        }
        // La demande faite avant que la socket ait répondu part maintenant — cet
        // ACK est la seule preuve que le backend écoute.
        const flight = conn.contextCompaction;
        if (flight.phase === 'awaiting_socket') {
          if (conn.socketCompactContext === 'supported') {
            this.sendCompactContextFrame(conn, flight.toolsConfig, flight.caseId);
          } else {
            this.log(sessionKey, conn.conversationId, 'COMPACT_CONTEXT_UNSUPPORTED', 'ACK générique sans capacité compactContext');
            this.settleContextCompaction(conn, { status: 'refused', userMessage: COMPACTION_UNSUPPORTED_MESSAGE }, { historyStale: false });
          }
        }
      }
      this.log(sessionKey, conn.conversationId, 'CONNECTED', `status=${conn.status} pending=${!!conn.pendingMessage}`);
      this.events.emit('connection_changed', sessionKey, true);

      // Demander la reprise du tour. WHY ici et pas dans `setOnConnectionChange`
      // (déplacé le 2026-09-11) : recevoir cet ACK prouve que le backend a fini son
      // authentification et attaché son listener de messages — une trame envoyée plus
      // tôt, dès `onopen`, se perd en silence.
      // WHY `message.resumed === undefined` : le backend répond lui-même à un
      // `session_resume` non adopté par un `connection_established { resumed: false }`,
      // qui repasse par ce handler. Sans cette garde, la réponse relancerait une
      // demande, et ainsi de suite — boucle infinie. Seul l'ACK générique déclenche.
      if ((isReconnection || conn.resumeOnEstablish) && message.resumed === undefined) {
        if (conn.resumeOnEstablish) {
          // Page neuve qui reprend un tour lancé ailleurs (openSessionForResume) : le
          // composer reste fermé tant que le backend n'a pas tranché.
          conn.resumeOnEstablish = false;
          this.setStatus(conn, 'streaming');
        }
        this.sendSessionResume(conn, sessionKey, isReconnection ? 'reconnexion' : 'chargement de page');
      }

      // Keep this acknowledgement after session_resume: on reconnect the
      // backend may replace the temporary SessionState with the adopted one.
      // The two frames remain ordered on this socket, so the capability is
      // latched on the session that owns any resumed Canvas operation.
      if (message.resumed === undefined && conn.supportsCanvasOperationProbe) {
        conn.client.send({
          type: 'canvas_operation_probe_capability',
          payload: { protocolVersion: 1 },
        } as FrontendMessage);
      }

      if (conn.status === 'connecting') {
        this.setStatus(conn, 'ready');
      } else if (
        isExchangeActive(conn.status)
        && !conn.pendingMessage
        && message.resumed === false
        && conn.resumeVerdictTurnId !== conn.currentLocalTurnId
      ) {
        // #1688 : le verdict répond au `session_resume` envoyé pour un AUTRE tour local
        // (un message en file est parti entre l'ACK générique et ce verdict, et c'est lui
        // qui streame maintenant). L'appliquer au tour vivant le déclarerait perdu.
        this.log(sessionKey, conn.conversationId, 'RESUME_VERDICT_STALE', `verdict for turn ${conn.resumeVerdictTurnId ?? 'none'}, current ${conn.currentLocalTurnId ?? 'none'}`);
      } else if (isExchangeActive(conn.status) && !conn.pendingMessage && message.resumed === false) {
        // WHY only on explicit `resumed:false`: during long legalresearch/JP work,
        // a reconnect first receives the generic auth ACK while `session_resume`
        // is still being processed. Marking the turn ready here made the UI stop
        // waiting even though the backend could still adopt the live MasterLoop.
        // `resumed:false` is the backend's authoritative verdict.
        if (message.turn_in_progress === true) {
          // Le tour tourne encore hors de portée de ce socket : l'autre instance
          // `hector-backend` (load balancer Render sans sticky session), ou la même
          // après sa fenêtre de replay (5 min). Rien à streamer ici. On reste en
          // `streaming` (composer fermé), on le dit discrètement, et `turn_settled`
          // dira de recharger l'historique. Rouvrir le composer ici est le bug vécu en
          // production le 2026-09-21 : l'avocat renvoie, reçoit `conversation_busy`,
          // et ne voit jamais la réponse pourtant persistée.
          // Le pin documentaire suit le tour : il est rendu à `turn_settled`.
          // L'accusé de persistance reste EN ATTENTE : le verrou prouve qu'UN tour (ou
          // une compaction) tourne sur la conversation, pas que CE message a été
          // persisté. Un `true` ici viderait le brouillon ; un `conversation_busy`
          // ultérieur arriverait sur une promesse déjà résolue et le texte serait perdu.
          // `turn_settled` (true), `error` (false) ou la fermeture trancheront.
          this.log(sessionKey, conn.conversationId, 'TURN_REMOTE', 'turn still running out of reach; waiting for turn_settled');
          conn.remoteTurnAwaited = true;
          // La note « déconnecté » n'est posée que si aucune chasse ne part : sinon elle
          // clignoterait 300–500 ms avant que les mots reprennent. Une chasse perdue la pose
          // à son issue (voir maybeHuntForTurn).
          if (!this.maybeHuntForTurn(conn, sessionKey, message)) {
            this.updateTimeline(conn, conn.reducer.applyServerFinishing(this.getTimeline(conn), serverFinishingCause(message.turn_owner_connected)));
          }
        } else {
          // « Rien à reprendre » (#1688, 2026-09-28) : le backend n'a plus de tour pour
          // cette conversation. Le plus souvent le tour s'est TERMINÉ pendant la coupure
          // et sa réponse est en base ; parfois il n'a jamais démarré. Dans les deux cas
          // la vérité est l'historique : on abandonne le fragment local et on recharge,
          // exactement comme à `turn_settled`. Avant : passage à `ready` sans rechargement,
          // `activeTurn` restait posé, la réponse persistée n'apparaissait jamais.
          // Accusé `false` : le brouillon revient au composer plutôt que d'être perdu si
          // le message n'avait pas été persisté ; s'il l'avait été, l'historique l'affiche.
          this.settleRemoteTurn(conn, sessionKey, { accepted: false, reason: 'rien à reprendre (resumed:false) — historique rechargé' });
        }
      } else if (conn.status === 'error') {
        // WHY: user clicked "Réessayer" after reconnection exhaustion.
        this.log(sessionKey, conn.conversationId, 'RECOVERED', 'from error via manual retry');
        this.setStatus(conn, 'ready');
      }

      // Send any message that was queued while waiting for auth confirmation
      if (conn.pendingMessage) {
        const pending = conn.pendingMessage;
        conn.pendingMessage = null;
        this.sendMessageDirect(
          conn,
          pending.content,
          pending.toolsConfig,
          {
            attachments: pending.attachedDocuments,
            modelPreference: pending.modelPreference,
            localTurnId: pending.localTurnId,
            caseId: pending.caseId,
            caseChanges: pending.caseChanges,
            caseIsAlreadyPrincipal: pending.caseIsAlreadyPrincipal,
            uiActionCapabilities: pending.uiActionCapabilities,
          },
        );
      }

      // An OPEN socket is not proof that the backend consumed the latest draft.
      // Replay the one pending revision after authenticated reconnects; the
      // collector answers idempotently without extending an already-ACKed edit.
      for (const item of this.getTimeline(conn)) {
        if (
          item.type !== 'redaction_plan_form'
          || !item.autoLaunchDraftUpdatePending
        ) continue;
        this.clearRedactionPlanDraftFlush(conn, item.requestId);
        this.sendRedactionPlanDraftUpdateFrame(
          conn,
          item.autoLaunchDraftUpdatePending,
          'REDACTION_PLAN_DRAFT_UPDATE_REPLAYED',
        );
      }

      // Reprise sur la même instance : cette réannonce part APRÈS `session_resume` (envoyé
      // plus haut, en synchrone ; l'annonce, elle, attend la qualification des capacités),
      // sur la même socket. Le backend traite `session_resume` et adopte la SessionState
      // sans aucun `await` avant `adoptResumedConnection`, et reçoit les trames dans l'ordre :
      // la réannonce est donc toujours admise par la SessionState adoptée, qui réarme le
      // circuit Canvas du tour. Hors reprise, elle répare `CANVAS_SESSION_REQUIRED` après
      // un redémarrage backend.
      this.reannounceMountedCanvas(conn);
    });

    // Backend confirms conversation row exists in DB after first user_message.
    // This is the signal for the sidebar to refresh — the phantom conversation
    // (optimistic Zustand entry) gets replaced by the real DB row.
    handler.on('conversation_created', (payload) => {
      if (payload.conversationId) {
        if (payload.conversationId !== conn.conversationId) {
          this.log(
            sessionKey,
            conn.conversationId,
            'CONVERSATION_IDENTITY_CONFLICT',
            `conversation_created received=${payload.conversationId}; socket kept=${conn.conversationId}`,
          );
          return;
        }
        this.events.emit('conversation_confirmed', sessionKey, conn.conversationId);
        // During a rolling deploy an old backend has no durable-turn ACK. Keep
        // its established behaviour rather than leaving the composer pending;
        // a recent backend always waits for `user_message_accepted` below.
        if (!conn.supportsUserMessageAcceptance) {
          this.resolvePendingMessageAcceptance(conn, true);
        }
        this.log(sessionKey, conn.conversationId, 'CONVERSATION_CREATED', 'DB row confirmed');
      }
    });

    // `conversation_created` only confirms the DB conversation row. A turn can
    // still fail while being persisted, so the composer waits for this distinct
    // acknowledgement before it clears its local text/files.
    handler.on('user_message_accepted', (payload) => {
      if (!payload.conversationId) return;
      if (payload.conversationId !== conn.conversationId) {
        this.log(
          sessionKey,
          conn.conversationId,
          'MESSAGE_ACCEPTANCE_IDENTITY_CONFLICT',
          `user_message_accepted received=${payload.conversationId}; socket kept=${conn.conversationId}`,
        );
        return;
      }
      this.resolvePendingMessageAcceptance(conn, true);
      this.log(sessionKey, conn.conversationId, 'USER_MESSAGE_ACCEPTED', 'Turn persisted');
    });

    // -- Streaming events --

    handler.on('agent_thinking', (payload) => {
      this.ensureStreaming(conn);
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyThinkingDelta(timeline, payload);
      this.updateTimeline(conn, updated);
    });

    handler.on('agent_message_delta', (payload) => {
      this.ensureStreaming(conn);
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyContentDelta(timeline, payload);
      this.updateTimeline(conn, updated);
    });

    handler.on('agent_message', (payload) => {
      this.ensureStreaming(conn);
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyContentSnapshot(timeline, payload.content);
      this.updateTimeline(conn, updated);
    });

    handler.on('backend_tool', (payload) => {
      this.ensureStreaming(conn);
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyToolEvent(timeline, payload);
      this.updateTimeline(conn, updated);
      // WHY: mutating pièce/folder tools change case-documents/folder state
      // server-side — set_piece_numbers (renumber), create_case_folders,
      // move_pieces, copy_pieces, rename_pieces, rename_folders, delete_pieces,
      // save_document, save_jurisprudence, compare_documents (when it files the
      // comparison in the dossier). save_document also completes when it adds a
      // template to the library: the extra refresh is harmless. Notify
      // the bridge so it can invalidate the case-documents / folder queries
      // without the lawyer needing to reload. A locked host must keep its
      // immutable dossier anchor even if a
      // late backend payload points elsewhere; general conversations still use the
      // payload first because the agent may intentionally target another dossier.
      if (
        payload.status === 'completed' &&
        (payload.tool === 'set_piece_numbers' ||
          payload.tool === 'create_case_folders' ||
          payload.tool === 'move_pieces' ||
          payload.tool === 'copy_pieces' ||
          payload.tool === 'rename_pieces' ||
          payload.tool === 'rename_folders' ||
          payload.tool === 'save_document' ||
          payload.tool === 'save_jurisprudence' ||
          payload.tool === 'compare_documents' ||
          payload.tool === 'split_document' ||
          (payload.tool === 'delete_pieces' && payload.data?.deleted === true))
      ) {
        const effectiveCaseId = conn.lockedCaseId ?? payload.data?.case_id ?? conn.caseId;
        this.events.emit('case_documents_mutated', sessionKey, effectiveCaseId);
      }
      // `create_case` a rendu un dossier (créé, ou existant repris) : les listes
      // de dossiers de l'avocat ont changé. Le `completed` (ou l'`error` d'une
      // liaison refusée : le dossier existe quand même) porte `case_id` ; un
      // refus ou un échec d'avant l'écriture n'en porte pas. Pas de sélection du
      // composeur ici : le serveur tient la liste de la conversation et la
      // pousse par `conversation_cases`. WHY dans le gestionnaire de trame et
      // pas dans le réducteur : un tour repris après rechargement rejoue ses
      // `backend_tool` dans le réducteur seul, sans effet de bord.
      if (
        payload.tool === 'create_case' &&
        (payload.status === 'completed' || payload.status === 'error') &&
        typeof payload.data?.case_id === 'string'
      ) {
        this.events.emit('cases_mutated', sessionKey);
      }
      // WHY: manage_quick_actions mutates the same REST-backed personal library
      // displayed by the prompt grid. The existing backend_tool completion is the
      // authoritative success signal, so no additional backend WS frame is needed.
      if (
        payload.tool === 'manage_quick_actions' &&
        payload.status === 'completed'
      ) {
        this.events.emit('quick_actions_mutated', sessionKey);
      }
    });

    // -- Jurisprudence interactive flow --
    // WHY no status change to 'waiting_user_action': JP form is a sub-state
    // of 'streaming', detected via timeline items (type === 'jurisprudence_form').

    handler.on('jurisprudence_search_request', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyJurisprudenceForm(timeline, payload);
      this.updateTimeline(conn, updated);
      // Status stays 'streaming' — JP form is a sub-state detected via timeline
      this.log(sessionKey, conn.conversationId, 'JP_FORM', `jobId=${payload.jobId}`);
    });

    handler.on('jurisprudence_search_progress', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyJurisprudenceProgress(timeline, payload);
      this.updateTimeline(conn, updated);
    });

    handler.on('jurisprudence_search_results', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyJurisprudenceResults(timeline, payload);
      this.updateTimeline(conn, updated);
    });

    // -- Case Wiki destructive reset flow --
    // Normal launches are autonomous; only a reset can suspend the agent on a card.
    handler.on('case_wiki_reset_request', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyCaseWikiResetRequest(timeline, payload);
      this.updateTimeline(conn, updated);
      this.log(sessionKey, conn.conversationId, 'WIKI_RESET', `requestId=${payload.requestId} caseId=${payload.caseId}`);
    });

    // The correlated backend verdict, never the socket write, is authoritative.
    handler.on('case_wiki_reset_result', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyCaseWikiResetResult(timeline, payload);
      this.updateTimeline(conn, updated);
      this.log(
        sessionKey,
        conn.conversationId,
        'WIKI_RESET_RESULT',
        `requestId=${payload.requestId} launched=${payload.launched}`,
      );
    });

    handler.on('delete_pieces_confirmation_request', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyDeletePiecesConfirmationRequest(timeline, payload);
      this.updateTimeline(conn, updated);
      this.log(sessionKey, conn.conversationId, 'DELETE_PIECES_REQUEST', `requestId=${payload.requestId} pieces=${payload.pieces.length}`);
    });

    handler.on('delete_pieces_confirmation_result', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyDeletePiecesConfirmationResult(timeline, payload);
      this.updateTimeline(conn, updated);
      this.log(sessionKey, conn.conversationId, 'DELETE_PIECES_RESULT', `requestId=${payload.requestId} deleted=${payload.deleted}`);
    });

    // -- Admission Canvas avant enqueue --
    //
    // Consentement utilisateur et capacité SuperDoc sont deux contrats
    // différents. Cette trame arrive après le clic, mais AVANT queue.add : le
    // reçu ne part que lorsque le résident exact est prêt, épinglé, son plan
    // capturé — et, si le travail remplace le document, le REMPLAÇANT monté et
    // prêt. L'ordre et ses raisons vivent dans `canvasAdmission.ts` (#1337).
    handler.on('canvas_admission_prepare', async (payload) => {
      const admissionPinKey = this.canvasAdmissionPinKey(conn.conversationId, payload.requestId);
      const admissionAbortController = new AbortController();
      this.canvasAdmissionAbortControllers.get(admissionPinKey)?.controller.abort('replaced by a newer admission request');
      this.canvasAdmissionAbortControllers.set(admissionPinKey, {
        conversationId: conn.conversationId,
        controller: admissionAbortController,
      });
      const refuse = (error: string): void => {
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_admission_refused',
          conversation_id: conn.conversationId,
          payload: {
            requestId: payload.requestId,
            jobId: payload.jobId,
            conversationId: payload.conversationId,
            error,
          },
        } as FrontendMessage);
      };

      if (payload.conversationId !== conn.conversationId) {
        this.canvasAdmissionAbortControllers.delete(admissionPinKey);
        admissionAbortController.abort('admission payload does not own this connection');
        refuse(`Canvas admission refused: payload conversationId=${payload.conversationId} does not own this connection.`);
        return;
      }

      // UNE échéance pour tout le gestionnaire, sous la borne backend
      // (`CANVAS_ADMISSION_ACK_TIMEOUT_MS`) : amorce, montage à froid et
      // remplacement se partagent son budget. Seules ses lectures (`resteMs`)
      // servent ici : `avant` laisserait l'admission courir après l'échéance et
      // poser une épingle que personne ne rendrait.
      const echeance = ouvrirUneEcheance(CANVAS_ADMISSION_BROWSER_DEADLINE_MS);
      try {
        canvasDocumentRuntime.acquireForConversation(conn.conversationId);
        // L'amorce AVANT la résidence (#1586) : sans elle, un plan serait streamé
        // dans une page vierge puis enregistré sous un nouveau `draftId` — donc la
        // purge du brouillon du fil — et une génération refuserait de capturer un
        // plan vide alors que le brouillon en porte un.
        const amorce = await this.amorcerLeBrouillonDuFil(conn.conversationId, 'canvas_admission_prepare');
        if (!amorce.ok) {
          refuse(amorce.error);
          return;
        }
        const issue = await preparerLAdmissionDuResident({
          conversationId: conn.conversationId,
          requestId: payload.requestId,
          kind: payload.kind,
          capturePlanMarkdown: payload.capturePlanMarkdown,
          seed: this.residentSeedForConversation(conn.conversationId),
          // UNE épingle par tour et par document : celle que ce tour tient déjà
          // sur le document de cette connexion est réutilisée, jamais doublée —
          // le bail exclusif exige `pinCount === 1`.
          epingleDuTour: conn.documentTurnPins.get(this.documentTurnPinKey(conn.currentLocalTurnId))?.runtimePin ?? null,
          aDejaRedigeIci: redactionStreamHub.derniereEcoutePourConversation(conn.conversationId) !== null,
          signal: admissionAbortController.signal,
          // Le papier à en-tête du travail : une recherche démarre un document neuf dessus, une
          // génération le reprend du plan (`canvasAdmission.ts`).
          depart: payload.depart,
          budgetRestantMs: () => echeance.resteMs(),
        });
        if (issue.statut === 'abandonnee') return;
        if (!conn.client.isConnected()) {
          issue.releasePin?.();
          return;
        }
        // L'amorce ET la résidence ont réussi. Publier avant cet instant ferait
        // prendre la scène au fil malgré un refus, et publier après l'ACK
        // laisserait le worker déployer l'ancienne scène (#1588).
        this.signalerAdmissionAgentCanvas(conn);
        // Une épingle posée par une tentative précédente de la MÊME demande est
        // rendue dans tous les cas ; seule celle que cette admission a posée est
        // retenue jusqu'au terminal du flux. Celle du tour appartient au tour.
        this.canvasAdmissionPins.get(admissionPinKey)?.release();
        this.canvasAdmissionPins.delete(admissionPinKey);
        if (issue.releasePin) {
          this.canvasAdmissionPins.set(admissionPinKey, {
            conversationId: conn.conversationId,
            jobId: payload.jobId,
            release: issue.releasePin,
          });
        }
        conn.client.send({
          type: 'canvas_admission_prepared',
          conversation_id: conn.conversationId,
          payload: {
            requestId: payload.requestId,
            // ⚠️ `conversationId` porte DEUX rôles ici, et c'est le contrat
            // (`CanvasAdmissionPreparedConversationPayloadSchema`) : la
            // corrélation de la demande d'admission, et — depuis #1336 —
            // l'identité du montage, avec `mountEpoch`. Ce qui distingue la
            // forme conversation de la legacy est la seule ABSENCE de
            // `documentSessionId`, que le `.strict()` du schéma refuserait.
            conversationId: payload.conversationId,
            // Le montage ADMIS — le remplaçant quand le travail remplace le
            // document (#1337) : `redaction_job_started` le confrontera au
            // montage physique, qui est désormais celui-là.
            mountEpoch: issue.mountEpoch,
            revision: issue.revision,
            jobId: payload.jobId,
            ...(issue.planMarkdown ? { planMarkdown: issue.planMarkdown } : {}),
          },
        } as FrontendMessage);
        this.log(sessionKey, conn.conversationId, 'CANVAS_ADMISSION_PREPARED', `requestId=${payload.requestId} jobId=${payload.jobId} kind=${payload.kind} mountEpoch=${issue.mountEpoch}`);
      } catch (error: unknown) {
        const detail = error instanceof Error ? error.message : String(error);
        const userMessage = error instanceof CanvasDraftContentUnreadableError
          ? refusDeContenuDeBrouillonIllisible(
            `Canvas admission refused before enqueue for conversationId=${conn.conversationId}`,
            error,
          )
          : error instanceof Error
            && error.name === 'CanvasResidenceUnavailableError'
            && (error as { kind?: unknown }).kind === 'capacity'
            ? 'Canvas admission refused before enqueue: all three document editors are occupied. '
              + 'Ask the lawyer to close a Canvas or wait for a writing task to finish, then retry.'
            : `Canvas admission failed before enqueue: ${detail}`;
        refuse(userMessage);
      } finally {
        echeance.clore();
        if (this.canvasAdmissionAbortControllers.get(admissionPinKey)?.controller === admissionAbortController) {
          this.canvasAdmissionAbortControllers.delete(admissionPinKey);
        }
      }
    });

    handler.on('canvas_admission_release', (payload) => {
      const admissionPinKey = this.canvasAdmissionPinKey(conn.conversationId, payload.requestId);
      this.canvasAdmissionAbortControllers.get(admissionPinKey)?.controller.abort(`backend released admission: ${payload.reason}`);
      const pin = this.canvasAdmissionPins.get(admissionPinKey);
      if (!pin || pin.jobId !== payload.jobId) return;
      pin.release();
      this.canvasAdmissionPins.delete(admissionPinKey);
      this.log(sessionKey, conn.conversationId, 'CANVAS_ADMISSION_RELEASED', `requestId=${payload.requestId} jobId=${payload.jobId} reason=${payload.reason}`);
    });

    // -- Job de rédaction démarré (research-only OU generation-only) --
    //
    // V2 architecture 3-canaux : c'est ce qui pilote la lecture du flux HTTP
    // hors React. Le tool backend a déjà enqueue le job ; le hub lie d'abord
    // son identité documentaire, puis consomme le corps `fetch` une seule fois.
    handler.on('redaction_job_started', (payload) => {
      // La session logique est acquise AVANT que le hub ne puisse ouvrir le
      // flux HTTP. Le job ne peut donc jamais produire un bloc sans posséder
      // déjà son identité documentaire immuable.
      //
      // ⚠️ Ce handler est SYNCHRONE et hors `try` : une exception y casserait le
      // `forEach` de dispatch de `WebSocketClient` — les autres écouteurs de la
      // même trame sauteraient — et remonterait dans `ws.onmessage`. Le seul
      // refus qui lève ici est celui d'un fil en cours de suppression (#1334) ;
      // ne rien lier est alors exactement ce qu'il faut faire.
      try {
        canvasDocumentRuntime.acquireForConversation(conn.conversationId);
      } catch (error) {
        console.warn(
          `[canvas] redaction_job_started ignoré pour conversationId=${conn.conversationId} `
          + `jobId=${payload?.jobId ?? 'inconnu'} : ${error instanceof Error ? error.message : String(error)}`,
        );
        return;
      }
      if (payload?.kind !== 'research' && payload?.kind !== 'generation') return;
      // `kind` conserve la sémantique du parcours de consentement : un acte
      // complet lancé en un temps arrive volontairement avec `kind=research`
      // pour garder la sélection des sources. Le flux HTTP, lui, doit suivre
      // le workflow réellement produit par le worker. Sans cette séparation,
      // `full_document` était envoyé vers `/stream-plan-detaille`, qui refuse
      // correctement les jobs `workflow_type=full_document` en HTTP 400.
      const streamKind = payload.full_document === true ? 'generation' : payload.kind;
      const admitted = payload.canvasAdmission;
      const admittedSession = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
      if (admitted) {
        // L'écho porte les DEUX formes d'identité de montage (#1335). Comparer
        // un champ NU — `admitted.documentSessionId` — refusait en silence tout
        // job admis en forme conversation : la valeur y est `undefined`, donc
        // toujours différente. On discrimine d'abord, puis on compare deux
        // identités de MÊME forme, `memeMontage` n'en croisant jamais deux
        // différentes (aucune table de correspondance n'existe).
        const identiteAdmise = identiteDeMontageDuneAdmission(admitted);
        // ⚠️ **Ce bundle n'annonce plus que la forme conversation** (#1336), et
        // il n'a donc plus de `documentSessionId` à opposer à un écho legacy :
        // un tel écho ne peut venir que d'un montage qu'un AUTRE bundle a
        // annoncé, jamais de celui-ci. Le refuser est la bonne conduite —
        // inventer une correspondance serait l'alias que la spec interdit.
        // L'époque, elle, vient du scope PHYSIQUE et non de l'écho : comparer
        // une valeur à elle-même ne prouverait rien.
        const identiteLocale: CanvasMountIdentity | null =
          admittedSession === null || identiteAdmise.forme !== 'conversation'
            ? null
            : {
              forme: 'conversation',
              conversationId: conn.conversationId,
              mountEpoch: admittedSession.mountEpoch,
            };
        // `memeMontageEpoqueExigee` et non `memeMontage` : l'écho impose
        // `mountEpoch` dans ses deux formes, donc l'époque attendue est
        // toujours connue et une époque muette ne prouve rien.
        if (identiteLocale === null || !memeMontageEpoqueExigee(identiteAdmise, identiteLocale)) {
          console.error(
            `[canvas] redaction_job_started refused: admission mismatch jobId=${payload.jobId} `
            + `admis=${decrireIdentiteDeMontage(identiteAdmise)} `
            + `monté=${identiteLocale === null ? 'aucun montage physique en forme conversation' : decrireIdentiteDeMontage(identiteLocale)}`,
          );
          return;
        }
      }
      const demande: DemandeDEcoute = {
        kind: streamKind,
        jobId: payload.jobId,
        conversationId: conn.conversationId,
        ...(payload.nom_acte?.trim() ? { documentTitle: payload.nom_acte.trim() } : {}),
        ...(admitted ? { mountEpoch: admitted.mountEpoch } : {}),
      };
      // Figer AVANT tout effet : `lierTravail` ne fait aucun réseau. Un même job
      // rejoué sur une autre connexion est donc refusé avant admission, toast ou
      // remplacement du brouillon hydraté.
      if (!this.lierTravailDeRedaction(sessionKey, demande)) return;
      if (!conn.redactionTurnIdByJob.has(payload.jobId)) {
        conn.redactionTurnIdByJob.set(payload.jobId, conn.currentLocalTurnId);
      }
      const ecouteExistante = redactionStreamHub.lire(payload.jobId);
      const repriseSansNouveauFlux = ecouteExistante !== null
        && !(ecouteExistante.statut === 'echouee' && ecouteExistante.blocs.length === 0);
      if (repriseSansNouveauFlux) {
        // A reconnect or duplicated backend callback may replay job_started.
        // Rejoining is useful for identity checking, but replaying the turn
        // side effects would pin a terminal document forever and could mount a
        // cold resident that no work will ever write into.
        this.ecouterTravailDeRedaction(sessionKey, demande);
        // La carte, elle, doit suivre : une timeline reconstruite après une
        // reconnexion est restée en « Préparation » alors que le flux écrit.
        // Sans effet sur une carte déjà en insertion, terminée ou en erreur.
        this.updateTimeline(conn, conn.reducer.applyRedactionWritingStarted(this.getTimeline(conn), payload.jobId));
        this.log(sessionKey, conn.conversationId, 'REDACTION_JOB_REJOINED', `jobId=${payload.jobId}`);
        return;
      }

      // B/C peuvent rester en arrière-plan : avant le premier octet SSE, le
      // job capture donc la session logique qu'il vient de lier. Ce pin ne
      // dépend pas du document affiché et tient jusqu'au terminal du tour.
      if (!this.ensureDocumentTurnPinned(conn).ok) return;
      if (!this.ecouterTravailDeRedaction(sessionKey, demande)) return;
      // Le flux est réellement lié et écouté : ce fil va déployer son canvas.
      // `ensureDocumentTurnPinned` ne signale rien seul, car une étape suivante
      // peut encore refuser le travail.
      this.signalerAdmissionAgentCanvas(conn);
      // Le flux est lié et écouté : l'acte va s'écrire. Avant l'ouverture et le
      // toast, pour que le dock bascule avant que le canvas ne se déploie ici.
      this.signalerEcritureAgentCanvas(conn, 'redaction_job_started', `job:${payload.jobId}`);
      // La carte du chat ne dit « Insertion » qu'à partir d'ici : le flux du job est lié et
      // écouté, le premier mot va arriver. Avant, elle reste sur « Préparation ».
      this.updateTimeline(conn, conn.reducer.applyRedactionWritingStarted(this.getTimeline(conn), payload.jobId));

      // (#1592, chemin 2) Chercher d'abord le brouillon du fil, PUIS ouvrir le
      // canvas. Sans cette amorce, le premier autosave d'une page vierge appelle
      // `deleteOthersForConversation` et supprime le vrai brouillon en base.
      // Miroir exact de `openCanvasForAgentWrite` sur les cinq autres entrées agent.
      void (async () => {
        const jobReason = streamKind === 'research'
          ? `redaction_job_started research — job ${payload.jobId}`
          : `redaction_job_started generation — job ${payload.jobId}`;
        const amorce = await this.amorcerLeBrouillonDuFil(conn.conversationId, jobReason);
        if (!amorce.ok) {
          // Le job tourne déjà côté backend : on ne peut pas le refuser, seulement
          // refuser d'ouvrir un canvas vierge par-dessus un brouillon qu'on n'a pas
          // su lire. L'avocat DOIT l'apprendre — sans ce toast, l'acte s'écrirait
          // sans que rien n'apparaisse à l'écran et il attendrait devant le vide,
          // ce qui est exactement le défaut que #1586 corrige par ailleurs.
          toast.error(
            "Le document enregistré de cette conversation n'a pas pu être relu : la rédaction "
            + "continue mais le canvas ne s'ouvre pas, pour ne pas écraser ce document. "
            + 'Rouvre la conversation dans un instant.',
            { ...toastDuration(8000) },
          );
          this.log(sessionKey, conn.conversationId, 'REDACTION_JOB_AMORCE_REFUSEE', `jobId=${payload.jobId} ${amorce.error}`);
          return;
        }
        if (streamKind === 'research') {
          // Repli si aucune progression n'a atteint cette connexion avant
          // `redaction_job_started`. Le hook SSE garde un filet au premier bloc.
          this.openCanvasForRedactionJob(payload.jobId, jobReason);
          const label = payload.nom_acte ? ` (${payload.nom_acte})` : '';
          toast(`Plan en cours d'élaboration${label}…`);
          this.log(sessionKey, conn.conversationId, 'REDACTION_JOB_RESEARCH', `jobId=${payload.jobId}`);
        } else {
          this.openCanvasForRedactionJob(payload.jobId, jobReason);
          toast('Rédaction de l\'acte…');
          this.log(sessionKey, conn.conversationId, 'REDACTION_JOB_GENERATION', `jobId=${payload.jobId} origin=${payload.originalJobId ?? ''}`);
        }
      })().catch((error: unknown) => {
        // `redaction_job_started` est un handler SYNCHRONE : une promesse rejetée
        // et non capturée ici ferait tomber le processus. On trace sans lier, la
        // même issue que les autres refus de ce handler.
        this.log(
          sessionKey,
          conn.conversationId,
          'REDACTION_JOB_OUVERTURE_ECHOUEE',
          `jobId=${payload.jobId} ${error instanceof Error ? error.message : String(error)}`,
        );
      });
    });

    // -- Protocole de lancement rédaction : plan temporisé / génération directe --
    //
    // Le plan initial affiche `redaction_plan_request`; clic ou échéance produit
    // le même `redaction_plan_launch_started`. La génération depuis le plan saute
    // la carte et commence directement par ce signal autoritatif.

    handler.on('redaction_plan_request', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyRedactionPlanRequest(timeline, payload);
      this.updateTimeline(conn, updated);
      // NB : NE PAS ouvrir le canvas ici — le worker n'a pas démarré.
      // L'ouverture attend `redaction_job_started`, après sa complétion.
      // Le temps de décision de l'avocat absorbe le téléchargement de l'éditeur :
      // sans cela il part avec l'admission et consomme son échéance. Un échec ici
      // est rejoué et nommé par l'admission elle-même.
      void chargerLEditeurDeRedaction().catch(() => undefined);
      this.log(sessionKey, conn.conversationId, 'REDACTION_PLAN_REQUEST', `requestId=${payload.requestId} kind=${payload.kind}`);
    });

    handler.on('redaction_plan_draft_update_acknowledged', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyRedactionPlanDraftUpdateAcknowledged(timeline, payload);
      this.updateTimeline(conn, updated);
      this.log(
        sessionKey,
        conn.conversationId,
        'REDACTION_PLAN_DRAFT_UPDATE_ACKNOWLEDGED',
        `requestId=${payload.requestId} revision=${payload.revision} autoLaunchAt=${payload.autoLaunchAt}`,
      );
    });

    handler.on('redaction_plan_interaction_acknowledged', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyLegacyRedactionPlanInteractionAcknowledged(
        timeline,
        payload.requestId,
      );
      this.updateTimeline(conn, updated);
      this.log(
        sessionKey,
        conn.conversationId,
        'REDACTION_PLAN_INTERACTION_ACKNOWLEDGED',
        `requestId=${payload.requestId} protocol=legacy`,
      );
    });

    handler.on('redaction_plan_launch_started', (payload) => {
      this.clearRedactionPlanDraftFlush(conn, payload.requestId);
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyRedactionPlanLaunchStarted(timeline, payload);
      this.updateTimeline(conn, updated);
      this.log(
        sessionKey,
        conn.conversationId,
        'REDACTION_PLAN_LAUNCH_STARTED',
        `requestId=${payload.requestId} jobId=${payload.jobId} origin=${payload.origin}`,
      );
    });

    handler.on('redaction_plan_progress', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyRedactionPlanProgress(timeline, payload);
      this.updateTimeline(conn, updated);
      this.openCanvasForRedactionJob(
        payload.jobId,
        `redaction_plan_progress ${payload.kind} — job ${payload.jobId}`,
      );
    });

    handler.on('redaction_plan_results', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyRedactionPlanResults(timeline, payload);
      this.updateTimeline(conn, updated);
      this.redactionCanvasOpenedForJobIds.delete(payload.jobId);
      this.log(sessionKey, conn.conversationId, 'REDACTION_PLAN_RESULTS', `requestId=${payload.requestId} verdict=${payload.verdict}`);
    });

    // -- Canvas round-trips (M2 read + M3 mutate) --
    //
    // Le backend (tools `get_canvas_*` / `canvas_*`) demande au front de lire
    // ou modifier l'état SuperDoc. On exécute via `canvasBridge` et on
    // renvoie le résultat via `canvas_state_result` / `canvas_actions_result`.
    // Si l'instance SuperDoc n'est pas montée (editor null), on renvoie un
    // payload vide — le backend a un timeout 8-15s et retournera une erreur
    // LLM-readable.

    // ⚠️ Surface VIVANTE, pas morte — ne pas refaire l'analyse qui a failli la
    // supprimer (#936, 2026-08-14). Deux familles d'appelants distinctes de
    // ce handler, toutes deux réelles :
    //   1. L'agent, via la chaîne LLM backend — CETTE chaîne-là est bien
    //      morte (module d'outils legacy inutilisé, part avec #935).
    //   2. Le harnais de test Playwright, qui émet `canvas_state_request`
    //      lui-même (kind `paragraphs`) pour LIRE le document avant de
    //      calculer un index cible — jamais un numéro codé en dur, cf.
    //      `tests/CLAUDE.md`. C'est un appelant de première classe, pas un
    //      raccourci de test.
    // « Aucun outil LLM ne l'appelle » (famille 1 morte) ne prouve RIEN sur
    // la famille 2 : c'est exactement l'erreur d'analyse qui a fait
    // requalifier ce ticket en « documenter et protéger » plutôt qu'en
    // « supprimer ». Détail complet, y compris `canvas_inspect` (l'autre
    // trame de lecture, distincte) et son successeur V2 :
    // `apps/legalworkspace/src/features/redaction/CLAUDE.md`.
    handler.on('canvas_state_request', async (payload) => {
      // ⚠️ Cette identité voyage DANS `result`, que le contrat de la trame
      // laisse en `z.unknown()` : elle est lue par le modèle, jamais par la
      // fence de `SessionState` — `canvas_state_result` ne porte aucune
      // identité de montage au niveau du payload, et le backend n'en attend
      // aucune (`ConnectionManager`, cas `canvas_state_result`). Ne PAS la
      // remonter d'un cran « pour faire pareil » : la clé serait retirée en
      // silence par Zod à la frontière et la fence lirait « identité absente »,
      // qu'elle traite comme « pas de contrainte ».
      const repondreErreurDeRoutage = (error: string, mountEpoch: number | null): void => {
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_state_result',
          conversation_id: conn.conversationId,
          payload: {
            requestId: payload.requestId,
            result: {
              code: 'CANVAS_SESSION_CHANGED',
              error,
              conversationId: conn.conversationId,
              mountEpoch,
            },
          },
        } as FrontendMessage);
      };
      // ⚠️ « Cette trame de lecture répond TOUJOURS » est un invariant gardé
      // (#940). L'acquisition LÈVE sur un fil en cours de suppression (#1334) et
      // ce handler vit hors `try` : sans cette conversion, aucune trame ne
      // partirait et le backend attendrait son propre délai.
      try {
        canvasDocumentRuntime.acquireForConversation(conn.conversationId);
      } catch (error) {
        repondreErreurDeRoutage(
          error instanceof Error ? error.message : String(error),
          null,
        );
        return;
      }

      // Cette trame legacy ne porte pas l'identité sur le wire. Elle la résout
      // donc depuis SA connexion, puis refuse si le montage visible appartient
      // à un autre travail. Résoudre le scope sélectionné au lieu de celui de
      // l'enveloppe transformerait un changement d'écran A→B en lecture de B
      // au nom de A.
      // Une lecture MONTE aussi le document : une page vierge montée ici ferait
      // sauter la recherche du brouillon à toutes les écritures suivantes (#1586).
      const opened = await this.openCanvasForAgentWrite(
        sessionKey,
        conn.conversationId,
        'canvas_state_request',
        { amorce: 'brouillon-du-fil' },
      );
      if (!opened.ok) {
        repondreErreurDeRoutage(opened.error, null);
        return;
      }
      // ⚠️ Deux des quatre contrôles d'avant étaient des tautologies (#1336) :
      // la session est TROUVÉE par la conversation de la connexion, donc son
      // propriétaire et son identité documentaire ne peuvent plus diverger de
      // ce qu'on a demandé. Reste ce qui tranche — un montage existe-t-il, et
      // est-il prêt ?
      const sessionAttendue = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
      if (!sessionAttendue || sessionAttendue.state !== 'ready') {
        repondreErreurDeRoutage(
          `canvas_state_request refused before Document API read: conversationId=${conn.conversationId} `
          + `has no ready mount (state=${sessionAttendue?.state ?? 'aucun montage'}). `
          + 'Remount the routed document and retry.',
          sessionAttendue?.mountEpoch ?? null,
        );
        return;
      }

      // La lecture passe par le Document API (`activeEditor.doc`), plus par
      // l'état ProseMirror de l'éditeur : `superdoc@2` ne le rend plus (#940).
      // Elle est donc ASYNCHRONE, et l'ouverture de la connexion se re-contrôle
      // après l'attente — une déconnexion pendant la lecture ferait sinon
      // partir une trame sur un canal mort.
      // Le `.d.ts` de SuperDoc type `doc` avec des entrées strictes par
      // opération ; la surface de lecture du canvas n'en consomme qu'un
      // sous-ensemble volontairement lâche (`SuperDocDocumentApi`), le même
      // que l'exécuteur canonique.
      const doc =
        (sessionAttendue.editor?.doc as unknown as
          | SuperDocDocumentApi
          | undefined) ?? null;
      const fallbackByKind = (kind: string): unknown => {
        if (kind === 'paragraphs' || kind === 'context_range' || kind === 'structure') return [];
        if (kind === 'paragraph_format') return null;
        return { has_selection: false };
      };
      if (!doc) {
        conn.client.send({
          type: 'canvas_state_result',
          conversation_id: conn.conversationId,
          payload: { requestId: payload.requestId, result: fallbackByKind(payload.kind) },
        } as FrontendMessage);
        return;
      }
      if (!conn.client.isConnected()) return;

      // Une lecture qui REJETTE doit quand même produire une trame. Depuis le
      // portage au Document API (#940) ces cinq appels sont attendus, et en V2
      // ils peuvent rejeter (document en reconstruction, moteur documentaire en
      // erreur) : sans ce `catch`, le handler rendrait une promesse rejetée que
      // personne n'attend — aucune trame ne partirait, l'attente corrélée du
      // backend expirerait, et le rejet non capturé ferait tomber le processus
      // (doctrine #939). Le symptôme serait MUET : ni erreur à l'écran, ni code
      // d'échec. Même patron que `canvas_superdoc_request` ci-dessous, qui
      // répond toujours. Le repli est le même que celui du canvas non monté :
      // une forme vide du bon type, que le backend sait lire.
      let result: unknown;
      try {
        switch (payload.kind) {
          case 'paragraphs':
            result = await extractParagraphs(doc);
            break;
          case 'selection':
            result = await extractSelection(doc);
            break;
          case 'context_range': {
            const p = payload as { kind: 'context_range'; start_index?: number; end_index?: number };
            result = await extractContextRange(doc, p.start_index ?? 0, p.end_index ?? 0);
            break;
          }
          case 'structure':
            result = await extractDocumentStructure(doc);
            break;
          case 'paragraph_format': {
            const p = payload as { kind: 'paragraph_format'; paragraph_index?: number };
            result = await extractParagraphFormat(doc, p.paragraph_index ?? 0);
            break;
          }
          default: {
            const unknownPayload = payload as unknown as { kind?: string };
            result = fallbackByKind(unknownPayload.kind ?? 'unknown');
          }
        }
      } catch (err) {
        result = fallbackByKind(payload.kind);
        this.log(
          sessionKey,
          conn.conversationId,
          'CANVAS_STATE',
          `kind=${payload.kind} lecture en échec, repli émis: ${err instanceof Error ? err.message : String(err)}`,
        );
      }
      const sessionCourante = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
      if (
        !memeEpoque(
          { conversationId: sessionAttendue.conversationId, mountEpoch: sessionAttendue.mountEpoch },
          sessionCourante && sessionCourante.state === 'ready'
            ? { conversationId: sessionCourante.conversationId, mountEpoch: sessionCourante.mountEpoch }
            : null,
        )
      ) {
        repondreErreurDeRoutage(
          'canvas_state_request result discarded after Document API await: expected conversationId='
          + `${sessionAttendue.conversationId} mountEpoch=${sessionAttendue.mountEpoch}, `
          + `now ${sessionCourante?.mountEpoch ?? 'aucun montage prêt'}. Retry after canvas_inspect.`,
          sessionAttendue.mountEpoch,
        );
        return;
      }
      if (!conn.client.isConnected()) return;
      conn.client.send({
        type: 'canvas_state_result',
        conversation_id: conn.conversationId,
        payload: { requestId: payload.requestId, result },
      } as FrontendMessage);
      this.log(sessionKey, conn.conversationId, 'CANVAS_STATE', `kind=${payload.kind}`);
    });

    handler.on('canvas_open_request', async (payload) => {
      // Chemin d'ouverture agent (tool `open_canvas`, 2026-08-12). Il rétablit
      // ce que la suppression d'`enable_redaction_mode` avait retiré, mais
      // **corrélé** : l'ancien chemin basculait l'interrupteur et n'accusait
      // jamais réception, donc l'agent ne savait pas s'il avait ouvert quoi
      // que ce soit.
      //
      // On passe par `openCanvasForAgentWrite` et pas par `openCanvas` nu : le
      // l'admission exacte doit réussir AVANT — ouvrir puis refuser d'écrire ferait
      // surgir un panneau vide au nom d'une conversation qui n'a pas le droit
      // d'écrire — et l'accusé ne doit partir qu'une fois l'éditeur ÉCRIVABLE,
      // parce que l'agent enchaîne sur `canvas_inspect` dans la foulée.
      // Le chemin de l'incident #1586 : un fil rouvert dans le dock n'a encore aucun
      // document dans l'onglet, et sans amorce une page VIERGE était montée.
      // `depart` ne sert qu'au fil sans aucun document : `open_canvas` ne remplace rien.
      //
      // UNE ÉCHÉANCE, comme les deux autres ouvertures : recherche du brouillon (8 s),
      // hydratation (14 s) et éditeur (14 s) dépassaient la borne du backend, qui
      // rendait « non confirmé » pendant que l'accusé était en route.
      const echeance = ouvrirUneEcheance(CANVAS_OPEN_BROWSER_DEADLINE_MS);
      const refuser = (error: string): void => {
        conn.client.send({
          type: 'canvas_open_result',
          conversation_id: conn.conversationId,
          payload: { requestId: payload.requestId, ok: false, error },
        } as FrontendMessage);
      };
      try {
        const opened = await this.openCanvasForAgentWrite(
          sessionKey, conn.conversationId, 'open_canvas', { amorce: 'brouillon-du-fil', depart: payload.depart, echeance },
        );
        if (!conn.client.isConnected()) return;
        if (!opened.ok) {
          refuser(opened.error);
          return;
        }

        // L'ANNONCE PART AVANT L'ACCUSÉ, et l'ordre est load-bearing : les trames
        // arrivent dans l'ordre d'émission, donc le broker backend connaît la
        // session au moment où l'attente corrélée se dénoue. Sans cela,
        // `open_canvas` rendrait `sessionPrete:false` sur un canvas qu'il vient
        // lui-même d'ouvrir — et l'agent conclurait qu'il ne peut pas inspecter.
        // Même geste que la réhydratation de `connection_established`.
        // EXPLICITE, pas par chance d'ordonnancement (#939) : la promotion du
        // montage est asynchrone depuis que la lecture de révision l'est, donc
        // lire la session sans attendre la rendrait encore `loading` et l'annonce
        // ne partirait tout simplement pas.
        if (!conn.client.isConnected()) return;
        // Attendu, pas lancé : l'accusé `canvas_open_result` porte `sessionPrete`,
        // et le backend enchaînera sur `canvas_inspect`. Répondre avant que
        // l'annonce soit partie rendrait ce `sessionPrete` vrai devant un broker
        // qui ne connaît encore aucune session.
        let mounted: CanvasRuntimeSession | null;
        try {
          mounted = await echeance.avant('announcing the mounted canvas to the server', (async () => {
            await canvasDocumentRuntime.attendreLaPromotionEnCours(conn.conversationId);
            const session = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
            // Passé l'échéance le refus est parti : une annonce tardive réarmerait le
            // broker sur une ouverture que l'agent vient d'apprendre non confirmée.
            if (session && session.state === 'ready' && !echeance.signal.aborted) {
              await this.announceCanvasDocumentState(session);
            }
            return session;
          })());
        } catch (err) {
          if (!(err instanceof EcheanceDepasseeError)) throw err;
          if (conn.client.isConnected()) {
            refuser(`Canvas open_canvas could not confirm conversationId=${conn.conversationId}: ${err.message}. `
              + 'The canvas may be open: read canvas_status before any other Canvas tool.');
          }
          return;
        }

        // `alreadyOpen` est la seule donnée du succès : elle empêche l'agent
        // d'annoncer à l'avocat une ouverture qui n'a rien changé dans le canvas.
        // Son écran, lui, peut changer : le signal ci-dessous fait basculer le dock.
        conn.client.send({
          type: 'canvas_open_result',
          conversation_id: conn.conversationId,
          payload: {
            requestId: payload.requestId,
            ok: true,
            alreadyOpen: opened.alreadyOpen,
            ...(mounted ? { mountEpoch: mounted.mountEpoch } : {}),
          },
        } as FrontendMessage);
        this.signalerEcritureAgentCanvas(conn, 'canvas_open_request', `requete:${payload.requestId}`);
        this.log(sessionKey, conn.conversationId, 'CANVAS_OPEN', `alreadyOpen=${opened.alreadyOpen}`);
      } finally {
        echeance.clore();
      }
    });

    handler.on('canvas_open_document_request', async (payload) => {
      // Chemin de POSE d'un autre document (tool `open_canvas_document`, #1338).
      //
      // ⚠️ **À ne pas confondre avec `canvas_open_request` juste au-dessus** : lui
      // est idempotent et ne remplace rien. Celui-ci écrase ce que l'avocat a sous
      // les yeux — c'est le geste « repars d'un document neuf », et c'est la
      // réponse attendue quand l'avocat demande de TOUT réécrire (réécrire en
      // suivi des modifications produirait un document illisible).
      const { requestId, source } = payload;
      const repondre = (
        reponse:
          | { ok: true; source: 'blank' | 'draft'; mountEpoch?: number }
          | {
            ok: false;
            error: string;
            // Le vocabulaire du CONTRAT, pas une copie : une copie locale avait
            // déjà pris du retard sur l'état « ouvert comme une copie ».
            etatDeLEcran: Extract<CanvasOpenDocumentResultPayload, { ok: false }>['etatDeLEcran'];
          },
      ): void => {
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_open_document_result',
          conversation_id: conn.conversationId,
          payload: { requestId, ...reponse },
        } as FrontendMessage);
      };

      // UNE ÉCHÉANCE POUR TOUT LE GESTIONNAIRE (2026-10-09). Le backend attend
      // l'accusé `CANVAS_OPEN_DOCUMENT_ACK_TIMEOUT_MS` ; passé ce délai l'outil
      // rend « état de l'écran inconnu » et un accusé tardif, même `ok:true`,
      // n'est lu par personne. Chaque étape avait sa propre borne (résidence
      // 14 s, éditeur 14 s, montage 14 s, capacités 1,5 s) et l'enregistrement du
      // sortant n'en avait aucune : à froid leur somme dépassait la borne. Toutes
      // les attentes passent donc par `echeance.avant`, celles qui prennent un
      // délai reçoivent ce qui reste, et à l'échéance l'accusé d'échec nomme
      // l'étape en cours. `clore()` est dans le `finally` : une minuterie
      // survivante avorterait une résidence que plus personne n'attend.
      const echeance = ouvrirUneEcheance(CANVAS_OPEN_DOCUMENT_BROWSER_DEADLINE_MS);
      try {
        // Le canvas doit être ouvert ET son éditeur écrivable AVANT tout
        // remplacement : `openCanvasForAgentWrite` porte l'admission exacte, le
        // bail et l'attente. Remplacer dans un canvas fermé ne montrerait rien à
        // l'avocat tout en détruisant son document.
        // `blank` : la page neuve est VOULUE, aucune recherche (sa purge du brouillon
        // précédent reste une décision produit, voir plus bas). `draft` : le rollback
        // D4 exporte A avant de poser B — un A vierge monté faute d'amorce serait
        // enregistré sous un nouveau `draftId`, donc la même purge (#1586).
        const opened = await this.openCanvasForAgentWrite(
          sessionKey,
          conn.conversationId,
          'open_canvas_document',
          source.kind === 'blank'
            // Le papier à en-tête nommé par l'agent voyage jusqu'à la graine : sur un fil froid,
            // c'est ce premier montage qui lit le papier, pas le remplacement plus bas.
            ? { amorce: 'aucune', depart: source.depart, echeance }
            : { amorce: 'brouillon-du-fil', echeance },
        );
        if (!conn.client.isConnected()) return;
        if (!opened.ok) {
          repondre({ ok: false, error: opened.error, etatDeLEcran: 'rien-na-bouge' });
          return;
        }

        const conversationId = conn.conversationId;
        // LE TOUR QUI A DEMANDÉ CETTE POSE EST-IL ENCORE EN COURS ? Son épingle
        // de document, posée par `openCanvasForAgentWrite`, est rendue à sa fin
        // (`agent_done`, Stop de l'avocat, session expirée, connexion détruite).
        // Elle est capturée ICI et comparée par identité : `currentLocalTurnId`
        // est une projection mutable, nulle pour un tour repris au chargement.
        const cleDuTour = this.documentTurnPinKey(conn.currentLocalTurnId);
        const epingleDuTour = conn.documentTurnPins.get(cleDuTour);
        const leTourEstArrete = (): boolean => (
          epingleDuTour === undefined
          || this.connections.get(sessionKey) !== conn
          || conn.documentTurnPins.get(cleDuTour) !== epingleDuTour
        );
        /** Un tour arrêté ne pose, n'annonce et n'accuse plus rien : plus personne n'attend. */
        const tracerLeTourArrete = (etape: string): void => {
          this.log(
            sessionKey, conversationId, 'CANVAS_OPEN_DOCUMENT',
            `source=${source.kind} requestId=${requestId} abandoned: the requesting turn ended ${etape}`,
          );
        };
        const annoncerEtRepondre = async (
          kind: 'blank' | 'draft',
          pose: {
            /** L'époque que CE remplacement a allouée : la seule dont le succès peut être accusé. */
            readonly epoqueAllouee: number;
            /** Pourquoi cette époque n'est pas devenue prête ; `null` si elle l'est. */
            readonly echecDuMontage: string | null;
          },
        ): Promise<void> => {
          // MÊME ORDRE QUE `canvas_open_request` : l'annonce de session part AVANT
          // l'accusé, sinon le courtier backend ignore encore le montage quand
          // l'attente corrélée se dénoue, et l'agent croit ne pas pouvoir inspecter.
          if (!conn.client.isConnected()) return;
          if (leTourEstArrete()) {
            tracerLeTourArrete('before the mount could be announced');
            return;
          }
          let monte: CanvasRuntimeSession | null = null;
          let annonce: CanvasDocumentStateAnnouncement | null = null;
          // LA PREMIÈRE CAUSE EST GARDÉE. Un montage qui n'est pas devenu prêt
          // n'est pas annoncé du tout : annoncer « le montage courant » à sa
          // place accusait un succès sur un AUTRE montage quand l'époque allouée
          // avait été supplantée, et une levée de l'annonce écrasait la cause.
          let cause: string | null = pose.echecDuMontage;
          let annonceTentee = false;
          if (cause === null) {
            try {
              await echeance.avant(
                'waiting for the mount promotion',
                canvasDocumentRuntime.attendreLaPromotionEnCours(conversationId),
              );
              monte = canvasDocumentRuntime.getSessionForConversation(conversationId);
              if (monte && monte.mountEpoch !== pose.epoqueAllouee) {
                cause = `the mount allocated to this request (mountEpoch=${pose.epoqueAllouee}) was superseded by `
                  + `mountEpoch=${monte.mountEpoch} before it could be announced`;
              } else if (monte && monte.state === 'ready') {
                annonceTentee = true;
                annonce = await echeance.avant(
                  'announcing the mounted document to the backend',
                  this.announceCanvasDocumentStateProof(monte),
                );
              }
            } catch (error: unknown) {
              cause = error instanceof Error ? error.message : String(error);
            }
          }
          // Stop pendant l'annonce : l'accusé et le signal d'écriture partiraient
          // sur un tour que l'avocat vient d'arrêter.
          if (leTourEstArrete()) {
            tracerLeTourArrete('while the mount was being announced');
            return;
          }
          const montageCourant = canvasDocumentRuntime.getSessionForConversation(conversationId);
          const presentationCourante = canvasDocumentRuntime.getScope(conversationId)?.getPresentation() ?? null;
          const documentIdCourant = presentationCourante?.templateMeta?.documentId
            ?? montageCourant?.documentId
            ?? null;
          if (
            !annonce
            || !annonce.sessionKeys.includes(sessionKey)
            || !conn.client.isConnected()
            || !montageCourant
            || montageCourant.state !== 'ready'
            || montageCourant.conversationId !== annonce.conversationId
            || annonce.mountEpoch !== pose.epoqueAllouee
            || montageCourant.mountEpoch !== annonce.mountEpoch
            || documentIdCourant !== annonce.documentId
            || montageCourant.revision !== annonce.revision
          ) {
            // The replacement commit already happened. Do not claim that nothing
            // changed: the previous document was checkpointed before replacement,
            // but neither the browser nor the broker can now prove which mount is
            // current. The backend must block save-to-case until a fresh state.
            repondre({
              ok: false,
              etatDeLEcran: 'document-precedent-perdu',
              error:
                `the document replacement on conversationId=${conversationId} may be visible, but the current `
                + `browser could not confirm that the live mount still matches the state it announced `
                + `(allocatedMountEpoch=${pose.epoqueAllouee}; `
                + `mountEpoch=${annonce?.mountEpoch ?? monte?.mountEpoch ?? 'unavailable'}; `
                + `revision=${annonce?.revision ?? 'unavailable'}->${montageCourant?.revision ?? 'unavailable'}; `
                + `announcement=${annonce ? 'sent' : annonceTentee ? 'refused' : 'not-attempted'}`
                + `${cause ? `; cause=${cause}` : ''}). `
                + 'The backend will verify the announced mount before reporting success to the agent. The previous '
                + 'draft was checkpointed before replacement and remains available; do not claim the '
                + 'requested document is confirmed. Refresh the Canvas state before any further edit or save.',
            });
            this.signalerEcritureAgentCanvas(conn, 'canvas_open_document_request', `requete:${requestId}`);
            this.log(sessionKey, conversationId, 'CANVAS_OPEN_DOCUMENT', `source=${kind} mount=unconfirmed`);
            return;
          }
          repondre({ ok: true, source: kind, mountEpoch: annonce.mountEpoch });
          this.signalerEcritureAgentCanvas(conn, 'canvas_open_document_request', `requete:${requestId}`);
          this.log(sessionKey, conversationId, 'CANVAS_OPEN_DOCUMENT', `source=${kind}`);
        };

        if (source.kind === 'blank') {
          // MIROIR DU GESTE HUMAIN « Nouveau » (`RedactionPanel`). Le commit est
          // SYNCHRONE : tout refus AVANT lui signifie « rien n'a bougé », et le
          // rollback visuel de D4 n'a pas d'objet (une page vierge n'a pas de
          // contenu qui puisse échouer à se charger). L'ACCUSÉ, lui, attend le
          // montage que ce commit a alloué — voir plus bas.
          try {
            // Le document SORTANT est enregistré — et la place dans le fil
            // vérifiée — AVANT la page neuve : le `clearDraft` plus bas avorterait
            // un enregistrement encore en vol, et avec lui ce que l'avocat venait
            // de taper (revue du 2026-09-23). Dernière attente du chemin : bail,
            // commit et oubli d'identité s'enchaînent ensuite sans rendre la main.
            const sortie = await echeance.avant(
              'saving the outgoing document',
              preparerLaSortieDuDocumentCourant(conversationId, { creeUnDocument: true }),
            );
            if (!sortie.ok) {
              repondre({
                ok: false,
                etatDeLEcran: 'rien-na-bouge',
                error: decrireLeRefusDeSortiePourLAgent(conversationId, sortie),
              });
              return;
            }
            // Le tour s'est arrêté pendant l'enregistrement du sortant : on ne
            // remplace pas le document d'un avocat qui vient de dire Stop.
            if (leTourEstArrete()) {
              tracerLeTourArrete('before the replacement; nothing was replaced');
              return;
            }
            const target = captureCanvasResidentReplacementTarget({ conversationId });
            // L'épingle du tour est PRÉSENTÉE, jamais rendue (#1354/D10) : la rendre
            // puis la reprendre ouvrirait la fenêtre où ce même tour annule son
            // propre remplacement.
            const bail = canvasDocumentRuntime.tryBeginExclusiveReplacement(
              conversationId, epingleDuTour?.runtimePin,
            );
            if (!bail) {
              repondre({
                ok: false,
                etatDeLEcran: 'rien-na-bouge',
                error:
                  `a blank document cannot replace the canvas of conversationId=${conversationId}: `
                  + 'another documentary job, a lawyer gesture or a second pin holds this document '
                  + 'session. Nothing was replaced and the lawyer still sees the previous document. '
                  + 'Retry once that work is released.',
              });
              return;
            }
            let remplacement;
            try {
              remplacement = startBlankCanvasResident({ target, replacementLease: bail, depart: source.depart });
            } finally {
              bail.release();
            }
            if (!remplacement.accepted) {
              repondre({
                ok: false,
                etatDeLEcran: 'rien-na-bouge',
                error:
                  `the mounted editor refused the blank document (reason=${remplacement.reason}) for `
                  + `conversationId=${conversationId}. Nothing was replaced; the lawyer still sees the `
                  + 'previous document.',
              });
              return;
            }
            // `clearDraft()` remet `draftId` à null : la page neuve s'enregistrera
            // sous un NOUVEL identifiant, sur la MÊME conversation.
            //
            // ⚠️ **Le brouillon précédent SURVIT désormais** (2026-09-22). Jusque-là
            // `upsertDraft` appelait `deleteOthersForConversation`, et le premier
            // autosave de la page neuve supprimait l'ancien brouillon avec son `.docx` ;
            // cet appel a été retiré, un fil pouvant porter plusieurs documents.
            // L'avocat retrouve donc l'ancien dans « Documents de cette conversation ».
            canvasDocumentRuntime.getDraftController(conversationId)?.clearDraft();
            // LE COMMIT N'EST PAS LE MONTAGE (défaut de production du 2026-10-09).
            // Il alloue une époque neuve et laisse la session en `loading` jusqu'à
            // ce que SuperDoc ait remonté. `annoncerEtRepondre` n'annonce qu'un
            // montage `ready`, et `attendreLaPromotionEnCours` n'attend qu'une
            // promotion DÉJÀ commencée — or juste après le commit elle ne l'est
            // pas. Sans cette attente, l'accusé partait donc à chaque fois en
            // « montage non confirmé / document précédent perdu » alors que la
            // page vierge se peignait une seconde plus tard. On attend exactement
            // l'époque allouée, comme le fait le chemin `draft`
            // (`replaceResidentCanvasDocument`) : ni l'ancien éditeur encore prêt,
            // ni un remontage postérieur. Le bail est déjà rendu ; l'épingle du
            // tour protège le remontage pendant l'attente.
            const epoqueDeLaPageNeuve = remplacement.session.mountEpoch;
            let echecDuMontage: string | null = null;
            try {
              await echeance.avant(
                `mounting the blank page (mountEpoch=${epoqueDeLaPageNeuve})`,
                canvasDocumentRuntime.waitForReady(
                  { conversationId, mountEpoch: epoqueDeLaPageNeuve, documentId: null },
                  Math.min(CANVAS_DOCUMENT_REPLACEMENT_TIMEOUT_MS, echeance.resteMs()),
                ),
              );
            } catch (error: unknown) {
              // Le commit a eu lieu : plus question de répondre « rien n'a bougé ».
              // `annoncerEtRepondre` n'annonce alors rien — surtout pas le
              // montage qui aurait supplanté cette époque — et rend l'issue
              // « non confirmé », avec cette cause.
              echecDuMontage = `the blank document committed at mountEpoch=${epoqueDeLaPageNeuve} never became ready: `
                + `${error instanceof Error ? error.message : String(error)}`;
            }
            await annoncerEtRepondre('blank', { epoqueAllouee: epoqueDeLaPageNeuve, echecDuMontage });
          } catch (error: unknown) {
            repondre({
              ok: false,
              etatDeLEcran: 'rien-na-bouge',
              error:
                `the blank document could not be opened on conversationId=${conversationId}: `
                + `${error instanceof Error ? error.message : String(error)}. Nothing was replaced.`,
            });
          }
          return;
        }

        // ── Brouillon existant ──
        // Le contenu se télécharge AVANT toute mutation d'écran : un téléchargement
        // qui échoue après un commit laisserait l'avocat devant un document vide.
        let contenu: Blob;
        try {
          contenu = await echeance.avant(
            `downloading the draft draftId=${source.draftId}`,
            canvasDraftApi.getDraftContent(source.draftId),
          );
        } catch (error: unknown) {
          repondre({
            ok: false,
            etatDeLEcran: 'rien-na-bouge',
            error:
              `the draft draftId=${source.draftId} could not be downloaded: `
              + `${error instanceof Error ? error.message : String(error)}. Nothing was replaced and the `
              + 'lawyer still sees the previous document.',
          });
          return;
        }

        // Brouillon de CE fil : on l'ADOPTE, rien ne se crée. Brouillon d'un AUTRE
        // fil : on en COPIE le contenu dans un document NEUF de ce fil — un
        // brouillon ne change jamais de conversation (le serveur le refuse).
        const copieDUnAutreFil = source.conversationId !== conversationId;
        // Le document SORTANT est enregistré APRÈS le téléchargement : l'adoption
        // comme le détachement avortent un enregistrement encore en vol (revue du
        // 2026-09-23). Une copie crée un document, donc le plafond est vérifié.
        let sortie: Awaited<ReturnType<typeof preparerLaSortieDuDocumentCourant>>;
        try {
          sortie = await echeance.avant(
            'saving the outgoing document',
            preparerLaSortieDuDocumentCourant(conversationId, { creeUnDocument: copieDUnAutreFil }),
          );
        } catch (error: unknown) {
          // Avant tout commit : l'écran est intact. Une levée ici est l'échéance
          // (la sortie rend ses refus métier en issue) ou un défaut de programme.
          repondre({
            ok: false,
            etatDeLEcran: 'rien-na-bouge',
            error:
              `the draft draftId=${source.draftId} was not opened on conversationId=${conversationId}: `
              + `${error instanceof Error ? error.message : String(error)}. Nothing was replaced and the `
              + 'lawyer still sees the previous document.',
          });
          return;
        }
        if (!sortie.ok) {
          repondre({
            ok: false,
            etatDeLEcran: 'rien-na-bouge',
            error: decrireLeRefusDeSortiePourLAgent(conversationId, sortie),
          });
          return;
        }

        const visibleConversationId = canvasDocumentRuntime.getSessionsSnapshot().visibleConversationId;
        const reveleALAvocat = !visibleConversationId || visibleConversationId === conversationId;
        const controleur = new AbortController();
        // Passé le commit, le remplacement ne lève plus : il rend l'une de ses
        // trois issues. L'échéance, elle, peut tomber PENDANT le montage de B ou
        // la restauration de A — l'écran est alors dans un état que personne n'a
        // encore constaté, et c'est ce que l'accusé doit dire. Le remplacement
        // continue en arrière-plan : son montage sera annoncé par `onSessionReady`.
        let remplacementEnCours = false;
        try {
          remplacementEnCours = true;
          const issue = await echeance.avant('replacing the mounted document with the draft', this.replaceResidentCanvasDocument({
            conn,
            identiteDuDocumentPose: copieDUnAutreFil
              ? { kind: 'nouveau-document' }
              : { kind: 'brouillon-du-fil', draftId: source.draftId, integrityAttemptId: source.integrityAttemptId },
            // ⚠️ `null`, JAMAIS le `draftId` : ce champ est un uuid de la table
            // `documents`, que l'agent rejoue dans `load_document_into_canvas`. Un
            // identifiant de brouillon posé là l'enverrait chercher une pièce qui
            // n'existe pas (invariant de `CanvasTemplateMeta.documentId`).
            documentId: null,
            blob: contenu,
            meta: {
              documentId: null,
              documentTitle: source.title,
              conversationId,
              // La nature RÉELLE du brouillon, transportée par la trame depuis le
              // 2026-09-22 : la poser en dur à `generated` faisait changer de
              // nature une page blanche ou une pièce importée au premier autosave.
              kind: source.draftKind,
            },
            reveleALAvocat,
            signal: controleur.signal,
          }));
          remplacementEnCours = false;
          if (issue.issue === 'b-pret') {
            // L'identité du document posé (adoption du brouillon du fil, ou
            // détachement d'une copie) a déjà été donnée au contrôleur, juste
            // après le commit dans `replaceResidentCanvasDocument` : le faire ici,
            // après `ready`, laissait le premier enregistrement s'armer sous
            // l'identité du document quitté (revue du 2026-09-23).
            if (!issue.identiteAdoptee) {
              repondre({
                ok: false,
                etatDeLEcran: 'document-ouvert-comme-copie',
                error:
                  `the draft draftId=${source.draftId} is on screen in conversationId=${conversationId}, `
                  + 'but its identity could not be adopted, so it was opened as a COPY: further edits '
                  + 'go to a new document, and the original draft is unchanged.',
              });
              return;
            }
            await annoncerEtRepondre('draft', {
              epoqueAllouee: issue.session.mountEpoch, echecDuMontage: null,
            });
            return;
          }
          this.log(
            sessionKey, conversationId, 'CANVAS_OPEN_DOCUMENT_ROLLBACK',
            `issue=${issue.issue} draftId=${source.draftId} candidateMountEpoch=${issue.epoqueDeB}`,
          );
          repondre({
            ok: false,
            error: issue.cause,
            // Les trois issues du remplacement chaud se traduisent une pour une :
            // c'est ce qui permet à l'agent de dire à l'avocat ce qu'il a sous les
            // yeux, au lieu de lui annoncer une perte qui n'a pas eu lieu.
            etatDeLEcran: issue.issue === 'a-restaure'
              ? 'document-precedent-restaure'
              : 'document-precedent-perdu',
          });
        } catch (error: unknown) {
          if (remplacementEnCours && error instanceof EcheanceDepasseeError) {
            repondre({
              ok: false,
              etatDeLEcran: 'document-precedent-perdu',
              error:
                `the draft draftId=${source.draftId} may be visible on conversationId=${conversationId}, but `
                + `${error.message}: the browser stopped waiting before the replacement or its rollback `
                + 'settled, so it cannot say which document is on screen. The previous draft was saved before '
                + 'the replacement and remains available. Do not claim the requested document is confirmed; '
                + 'refresh the Canvas state before any further edit or save.',
            });
            return;
          }
          repondre({
            ok: false,
            // Une levée AVANT le commit laisse l'écran intact — c'est l'invariant
            // de `replaceResidentCanvasDocument`, qui ne lève jamais après avoir
            // commité B (#1396).
            etatDeLEcran: 'rien-na-bouge',
            error:
              `the draft draftId=${source.draftId} could not be mounted on `
              + `conversationId=${conversationId}: `
              + `${error instanceof Error ? error.message : String(error)}. Nothing was replaced.`,
          });
        }
      } finally {
        echeance.clore();
      }
    });

    handler.on('canvas_load_template_request', async (payload) => {
      const operation = {
        turnId: conn.currentLocalTurnId,
        controller: new AbortController(),
      };
      let libererReservationDeSlot: (() => void) | null = null;
      // UNE ÉCHÉANCE POUR TOUT LE GESTIONNAIRE, comme `canvas_open_document_request`
      // (2026-10-09). Enregistrement du sortant (non borné), emplacement (14 s),
      // montage (14 s, + 10 s de restauration à chaud) et annonce (1,5 s)
      // additionnaient plus que les 30 s du backend : l'agent recevait un timeout
      // aveugle, puis l'accusé tardif n'était lu par personne (prod 2026-10-19 →
      // 23, réponses reçues 33 à 93 s après la demande).
      const echeance = ouvrirUneEcheance(CANVAS_LOAD_TEMPLATE_BROWSER_DEADLINE_MS);
      // Un montage expiré ne doit pas reprendre l'écran après l'accusé d'échec :
      // l'échéance annule l'opération, donc la résidence froide et le
      // remplacement chaud (qui restaure alors le document précédent).
      echeance.signal.addEventListener('abort', () => operation.controller.abort(), { once: true });
      let admissionVisuelleEnAttente = false;
      // LE ROLLBACK VISUEL DE D4 VIT SUR LE SEUL CHEMIN CHAUD (#1338, D-C) :
      // `replaceResidentCanvasDocument` capture A avant de commiter B et le
      // restaure si B ne devient pas prêt. Les gestes humains d'ouverture et
      // l'admission d'un travail commitent sans ce filet (#1431).
      // ⚠️ Ces deux aides ne disent RIEN de l'écran : l'ancien texte promettait
      // « The previous document will be retained » sans condition, y compris
      // après le commit qui venait de détruire A. Seul le site qui SAIT ce qui
      // est affiché l'écrit (`requireCurrentOperation(consequence)`,
      // `decrireUnChargementChaudEchoue`).
      const requireCurrentOperation = (consequence: string) => {
        if (
          operation.controller.signal.aborted
          || conn.canvasDocumentLoad !== operation
          || this.connections.get(sessionKey) !== conn
          || !conn.client.isConnected()
          || conn.currentLocalTurnId !== operation.turnId
        ) {
          throw new Error(
            `Canvas load requestId=${payload.requestId} lost its owning connection or turn. ${consequence}`,
          );
        }
      };
      const waitForOperation = <T>(pending: Promise<T>): Promise<T> => attendreSaufAnnulation(
        pending,
        operation.controller.signal,
        () => `Canvas load requestId=${payload.requestId} was cancelled before the cold mount of `
          + `documentId=${payload.documentId ?? 'none'} settled; nothing is acknowledged.`,
      );
      // Décode le .docx base64, remplace le document du résident, et le
      // RedactionCanvas remonte sous l'époque neuve (#1337). Le succès n'est ACK
      // qu'après le ready joint du document exact, sinon le backend pourrait
      // lancer `canvas_inspect` sur l'ancien Word encore monté.
      //
      // Chemin d'ouverture 3/5. Ouvre AVANT d'attendre le mount : panneau non
      // monté, `waitForReady` expirait à 30 s et l'agent recevait une erreur de
      // transport aveugle (5 occurrences en replay 12-07, dont une conversation
      // abandonnée après 4 tentatives). Règle commune depuis #860.
      // Acquisition and pinning may throw before hydration. MessageHandler
      // does not await async handlers: leaving these outside the try would
      // suppress the correlated reply and turn a known refusal into a timeout.
      try {
        if (conn.canvasDocumentLoad) {
          throw new Error(
            `another Canvas document load is already running for conversationId=${conn.conversationId}. `
            + 'Wait for its correlated result before requesting another document.',
          );
        }
        conn.canvasDocumentLoad = operation;
        requireCurrentOperation('Nothing was loaded; the canvas is unchanged.');
        const documentSession = canvasDocumentRuntime.acquireForConversation(conn.conversationId);
        // Retain this exact session before any await: a document load replaces
        // its resident, so eviction must not race the download, mount or ACK.
        const epingle = this.ensureDocumentTurnPinned(conn);
        if (!epingle.ok) {
          if (!conn.client.isConnected()) return;
          conn.client.send({
            type: 'canvas_load_template_result',
            conversation_id: conn.conversationId,
            payload: {
              requestId: payload.requestId,
              success: false,
              error: `load_document_into_canvas refused for conversationId=${conn.conversationId}: ${epingle.cause}`,
            },
          } as FrontendMessage);
          return;
        }
        // `docxBase64` est la source de montage. `documentId` n'est que la
        // provenance optionnelle d'une ligne `documents` : un modèle de
        // bibliothèque ou une pièce jointe a donc légitimement `null` ici.
        // Le runtime isole toujours le montage par (conversationId, mountEpoch).
        const binary = atob(payload.docxBase64);
        const len = binary.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], {
          type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        });
        const meta = {
          documentId: payload.documentId,
          documentTitle: payload.documentTitle,
          conversationId: conn.conversationId,
          caseId: payload.caseId,
          sourceFilePath: payload.sourceFilePath,
          // La nature est DÉCLARÉE, pas devinée (#1334/D8). `deciderLeMetaDuDocument`
          // ne peut la déduire que de `sourceFilePath`, un champ facultatif du
          // contrat : un émetteur qui l'omettrait ferait enregistrer une pièce
          // du cabinet en `blank`, avec le mauvais badge et la mauvaise
          // provenance annoncée au modèle. Cette trame charge toujours un
          // document existant — c'est un import, quel que soit le chemin S3.
          kind: 'imported' as const,
        };
        // ⚠️ `getCurrentSession()` a disparu (#1336, lot H) : elle projetait le
        // résident VISIBLE et servait de destination implicite. Ce qu'on veut
        // savoir ici n'est pas « quel document est monté » mais « lequel
        // l'avocat regarde-t-il ? » — une question de projection, à laquelle
        // seul le registre répond, et sans destination de repli.
        const visibleConversationId = canvasDocumentRuntime.getSessionsSnapshot().visibleConversationId;
        // An agent opening a Word in its currently selected conversation
        // still reveals that document. A different visible conversation is
        // never displaced: its resident keeps the physical slot.
        const reveleALAvocat = !visibleConversationId || visibleConversationId === conn.conversationId;
        // TROIS TEMPÉRATURES, TROIS RÉPONSES (#1345). `CanvasSessionTemperature`
        // en vaut trois — `hot | cold | hydrating` — et le défaut du ticket vit
        // dans CHACUNE de celles que ce chemin ne traite pas nommément :
        // `requestResident` ne sait que faire ENTRER un document dans un éditeur
        // libre, et sur toute autre situation il **jette la graine**, donc le
        // .docx que l'agent vient d'envoyer. L'accusé repartait alors en succès
        // sur l'ancien document (mesuré : `revision: "0"`, celle du gabarit
        // blanc encore monté), l'avocat gardait sa page blanche et le modèle
        // enchaînait ses lectures et ses écritures sur un document qui n'était
        // pas le sien.
        if (documentSession.temperature === 'hydrating') {
          // REFUS FRANC PLUTÔT QU'ATTENTE. Le coordinateur de résidence ne tient
          // délibérément aucune file d'ouvertures futures — « a prompt verdict,
          // not a stale spinner » (`canvasResidenceCapacity.ts`) — et une
          // demande posée sur une cible en hydratation JOINT la transaction en
          // vol (`requestResident`, jonction de l'opération en cours) avant même
          // les tests de température, et seule une MÊME source (`documentId`) y
          // est admise (#1338) : ce refus franc ne dépend pas de ce départage. Attendre puis remplacer nous
          // ferait au contraire réimplémenter cette file ici, sur un état que
          // seul le coordinateur arbitre. Le refus, lui, dit la vérité.
          throw new Error(
            `the editor of conversationId=${conn.conversationId} is still being built `
            + '(temperature=hydrating), so this DOCX cannot be mounted without silently joining that '
            + 'mount and being dropped. Nothing was loaded and the lawyer still sees the previous '
            + 'document. Retry this same load once the mount settled — canvas_status reports when the '
            + 'document session is ready.',
          );
        }
        // La pièce chargée devient un document NEUF du fil (détaché juste après
        // le commit, dans `replaceResidentCanvasDocument`) : le document
        // SORTANT est donc enregistré d'abord, sans quoi le détachement
        // avorterait son enregistrement en vol — et le plafond du fil est
        // vérifié avant de rien montrer (revue du 2026-09-23). Un refus lève :
        // le `catch` le rend en échec corrélé, rien n'a été chargé.
        const sortie = await echeance.avant(
          'saving the outgoing document',
          preparerLaSortieDuDocumentCourant(conn.conversationId, { creeUnDocument: true }),
        );
        requireCurrentOperation('Nothing was loaded; the canvas is unchanged.');
        if (!sortie.ok) throw new Error(decrireLeRefusDeSortiePourLAgent(conn.conversationId, sortie));
        // Les refus synchrones (concurrence, acquisition, épingle, décodage,
        // hydratation déjà en cours) sont tous levés. Le premier montage DOCX
        // est le seul qui ne puisse pas naître au parking : SuperDoc mémorise
        // alors une géométrie hors écran et l'acte reste blanc après le relais.
        // La réservation suspend le repli vierge de CanvasResidence jusqu'à ce
        // que cet appelant soumette SA graine exacte au coordinateur.
        const montageFroid = documentSession.temperature === 'cold';
        if (montageFroid) {
          libererReservationDeSlot = canvasDocumentRuntime.reserveColdMountForVisibleSlot(
            conn.conversationId,
          );
          useCanvasVisibiliteStore.getState().beginResidenceRequest(
            conn.conversationId,
            'agent-document-load',
          );
          admissionVisuelleEnAttente = true;
        }
        // Le dock rejoint immédiatement le fil pour ce seul chemin : l'annonce
        // douce reste réservée aux écritures ordinaires, qui peuvent commencer
        // hors écran. Le handler attend ensuite le slot réel avant le montage.
        this.signalerAdmissionAgentCanvas(conn, montageFroid ? {
          requiresVisibleSlot: true,
          cause: 'canvas_load_template_request',
          cleDIdempotence: `requete:${payload.requestId}`,
        } : {});
        openCanvas(`canvas_load_template_request — documentId=${payload.documentId ?? 'none'}`);
        if (montageFroid) {
          const slotPret = await echeance.avant('waiting for the canvas slot', waitForOperation(attendreQueLeSlotCanvasSoitPret(
            conn.conversationId,
            Math.min(CANVAS_MOUNT_WAIT_MS, echeance.resteMs()),
          )));
          if (!slotPret) {
            throw new Error(
              `Canvas assistant slot did not mount for conversationId=${conn.conversationId} before the DOCX mount. `
              + 'Nothing was loaded, so retry this exact load after returning to the assistant.',
            );
          }
        }
        // LES DEUX ATTENTES DE MONTAGE COURENT CONTRE LE SIGNAL (#1354/D9). Le
        // remontage se borne à ~14 s, et cette borne est la SEULE que le
        // coordinateur connaisse : si le tour se termine ou si la connexion
        // meurt entre-temps, la promesse ne se dénoue pas d'elle-même. Le chemin
        // CHAUD porte sa propre course, parce qu'une annulation y déclenche la
        // restauration de A (D-E) au lieu d'abandonner l'écran à B.
        let ready: CanvasRuntimeSession;
        if (documentSession.temperature === 'hot') {
          const issue = await echeance.avant('replacing the mounted document with the DOCX', this.replaceResidentCanvasDocument({
            conn,
            identiteDuDocumentPose: { kind: 'nouveau-document' },
            documentId: payload.documentId,
            blob,
            meta,
            reveleALAvocat,
            signal: operation.controller.signal,
          }));
          if (issue.issue !== 'b-pret') {
            this.log(
              sessionKey,
              conn.conversationId,
              'CANVAS_LOAD_TEMPLATE_ROLLBACK',
              `issue=${issue.issue} documentId=${payload.documentId ?? 'none'} candidateMountEpoch=${issue.epoqueDeB}`
              + (issue.issue === 'a-restaure' ? ` restoredMountEpoch=${issue.restaure.mountEpoch}` : ''),
            );
            if (!conn.client.isConnected()) return;
            conn.client.send({
              type: 'canvas_load_template_result',
              conversation_id: conn.conversationId,
              payload: {
                requestId: payload.requestId,
                success: false,
                error: decrireUnChargementChaudEchoue({
                  documentId: payload.documentId,
                  conversationId: conn.conversationId,
                  issue,
                }),
              },
            } as FrontendMessage);
            return;
          }
          ready = issue.session;
        } else {
          ready = await echeance.avant('mounting the DOCX', waitForOperation(this.mountColdCanvasDocument({
            conn,
            documentId: payload.documentId,
            documentTitle: payload.documentTitle,
            blob,
            meta,
            reveleALAvocat,
            signal: operation.controller.signal,
            hydrationBudgetMs: echeance.resteMs,
          })));
        }
        requireCurrentOperation(
          `documentId=${payload.documentId ?? 'none'} is mounted at mountEpoch=${ready.mountEpoch}, but it is not `
          + 'acknowledged to a turn that no longer owns it.',
        );
        // LE DOCUMENT MONTÉ DOIT ÊTRE CELUI QU'ON A DEMANDÉ, et c'est un
        // invariant IMPOSÉ, pas commenté. Le défaut du ticket n'a pas une seule
        // porte d'entrée : toute coalescence du coordinateur (résident déjà
        // chaud, graine logique préférée à celle de la demande) rend une
        // session prête et étrangère. Comparer l'identité documentaire est
        // la seule chose qui distingue « monté » de « rien ne s'est passé »,
        // quel que soit le chemin emprunté — `revision`, elle, ne discrimine
        // rien : elle valait « 0 » sur le chargement qui marche comme sur celui
        // qui ment.
        if (ready.documentId !== payload.documentId) {
          throw new Error(
            `the canvas runtime resolved conversationId=${ready.conversationId} mountEpoch=${ready.mountEpoch} on `
            + `documentId=${ready.documentId ?? 'none'} while documentId=${payload.documentId ?? 'none'} was requested; `
            + 'the seed carrying this DOCX was dropped, so the lawyer is still looking at the previous '
            + 'document. Do not report this piece as opened: re-inspect the canvas, then retry the load.',
          );
        }
        if (!ready.revision) {
          throw new Error(
            `Canvas runtime resolved documentId=${payload.documentId ?? 'none'} mountEpoch=${ready.mountEpoch} without a revision; ` +
            'success ACK requires editor.doc.info({}).revision.',
          );
        }

        // LE SCOPE VIVANT DOIT ENCORE PORTER CE MONTAGE AU MOMENT DE L'ACCUSÉ
        // (#1354). `ready` est un instantané résolu par `waitForReady` ; entre
        // sa résolution et l'envoi de la trame il reste des microtâches pendant
        // lesquelles un autre geste peut avoir remonté, refroidi ou détruit le
        // scope. Relire l'état COURANT est la seule chose qui distingue « le
        // document est ouvert » de « il l'a été ». L'époque se compare à celle
        // du montage résolu, et non à une valeur calculée en amont : les deux
        // branches (chaude, froide) n'allouent pas leur montage au même
        // endroit, et seul celui qui a réellement monté fait foi.
        // ⚠️ ADRESSÉ PAR LA CONVERSATION (#1336) : `getScope` n'accepte plus
        // d'identifiant de document. Le couple `(conversationId, mountEpoch)`
        // porte exactement la même intention — « ce montage-ci, pas un autre » —
        // et `mountEpoch` reste le seul discriminant d'un REMONTAGE.
        const readyScope = canvasDocumentRuntime.getScope(ready.conversationId);
        const readySnapshot = readyScope?.getSnapshot();
        if (
          !readyScope
          || readySnapshot?.state !== 'ready'
          || readySnapshot.mountEpoch !== ready.mountEpoch
          || readySnapshot.documentId !== payload.documentId
        ) {
          throw new Error(
            `Canvas load requestId=${payload.requestId} lost exact documentId=${payload.documentId ?? 'none'} `
            + `before it could be acknowledged (conversationId=${ready.conversationId} `
            + `mountEpoch=${ready.mountEpoch}). `
            + 'The document is not open; retry after the current Canvas settles.',
          );
        }
        // The load result is consumed by the backend tool immediately. Send
        // the matching mount state first on this same WebSocket so its broker
        // cannot interpret a successful load against the previous document.
        // onSessionReady normally starts this announcement already; the
        // in-flight promise coalesces the common race instead of repeating the
        // asynchronous capability read.
        const readinessAnnounced = await echeance.avant(
          'announcing the mounted document',
          this.announceCanvasDocumentState(ready),
        );
        const readyAfterAnnouncement = canvasDocumentRuntime.getSessionForConversation(ready.conversationId);
        if (
          !readinessAnnounced
          || !readyAfterAnnouncement
          || readyAfterAnnouncement.state !== 'ready'
          || readyAfterAnnouncement.mountEpoch !== ready.mountEpoch
          || readyAfterAnnouncement.documentId !== payload.documentId
          || !readyAfterAnnouncement.revision
        ) {
          throw new Error(
            `documentId=${payload.documentId ?? 'none'} was mounted at mountEpoch=${ready.mountEpoch}, but its current `
            + 'Canvas state could not be announced before the load acknowledgement. The document may already be visible; '
            + 'do not load it again until the current Canvas mount has been inspected.',
          );
        }
        // Un retour de montage ne suffit pas : les gardes ci-dessus prouvent
        // que le DOCX attendu est toujours le scope vivant. Ne solder qu'ici
        // pour que tout échec de postcondition garde son explication dans le
        // split déjà ouvert par le tool.
        if (admissionVisuelleEnAttente) {
          useCanvasVisibiliteStore.getState().settleResidenceRequest(conn.conversationId);
          admissionVisuelleEnAttente = false;
        }
        // ⚠️ NI `configureContext` NI `markDirty` ICI, et pour deux raisons
        // distinctes. L'identité de brouillon (titre, nature, pièce d'origine)
        // est DITE UNE SEULE FOIS par `canvasDocumentMeta` depuis #1333 — la
        // reposer ici recréerait la seconde source qui faisait annoncer
        // `titre: null` au modèle sur l'acte que le panneau nommait. Et la
        // persistance du premier état d'une source ouverte est armée par
        // `installerLaConfirmationDuCheckpointInitial` au seam `onSessionReady`
        // (#1334/D2), qui couvre les TROIS chemins d'ouverture — froid, chaud
        // et le geste humain « Ouvrir dans le Canvas » — là où un `markDirty()`
        // posé ici ne couvrirait que le chemin agent.
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_load_template_result',
          conversation_id: conn.conversationId,
          payload: {
            requestId: payload.requestId,
            success: true,
            // Le montage RÉELLEMENT produit, en forme conversation (#1336) : le
            // backend le lit pour vérifier que le chargement a bien abouti sur
            // le montage qu'il attendait (`LoadDocumentIntoCanvas`).
            conversationId: readyAfterAnnouncement.conversationId,
            mountEpoch: readyAfterAnnouncement.mountEpoch,
            revision: readyAfterAnnouncement.revision,
          },
        } as FrontendMessage);
        this.signalerEcritureAgentCanvas(conn, 'canvas_load_template_request', `requete:${payload.requestId}`);
        this.log(sessionKey, conn.conversationId, 'CANVAS_LOAD_TEMPLATE', `documentId=${payload.documentId ?? 'none'} size=${blob.size}b`);
      } catch (err) {
        if (admissionVisuelleEnAttente) {
          useCanvasVisibiliteStore.getState().failResidenceRequest(conn.conversationId);
          admissionVisuelleEnAttente = false;
        }
        if (!conn.client.isConnected()) return;
        const message = err instanceof Error ? err.message : String(err);
        // Une étape perdue continue en arrière-plan : le document peut encore
        // s'afficher. Sans cette phrase, l'agent relançait le même chargement.
        const suite = err instanceof EcheanceDepasseeError
          ? '. The document may still appear once its mount settles: call canvas_status before loading it again.'
          : '';
        conn.client.send({
          type: 'canvas_load_template_result',
          conversation_id: conn.conversationId,
          payload: {
            requestId: payload.requestId,
            success: false,
            error:
              `load_document_into_canvas could not confirm the final Canvas state for documentId=${payload.documentId ?? 'none'} ` +
              `for conversationId=${conn.conversationId}: ${message}${suite}`,
          },
        } as FrontendMessage);
      } finally {
        echeance.clore();
        libererReservationDeSlot?.();
        // L'OPÉRATION SE REND TOUJOURS, y compris sur un jet précoce : sans ce
        // `finally`, un refus laisserait `canvasDocumentLoad` posé et tout
        // chargement suivant serait refusé pour « une opération est déjà en
        // cours » sur une opération qui n'existe plus (#1354/D9).
        if (conn.canvasDocumentLoad === operation) conn.canvasDocumentLoad = undefined;
      }
    });

    /**
     * Answers from the request registry only. This must stay apart from
     * `canvas_superdoc_request`: opening a resident, reading SuperDoc, or
     * consulting writer/autosave state would turn a passive liveness question
     * into an unsafe concurrent document operation.
     */
    handler.on('canvas_operation_probe_request', (payload) => {
      if (!conn.client.isConnected()) return;
      const scope = canvasDocumentRuntime.getScope(conn.conversationId);
      const snapshot = scope?.getSnapshot();
      const response: CanvasOperationProbeResultPayload = (
        payload.conversationId !== conn.conversationId
        || snapshot?.conversationId !== payload.conversationId
        || snapshot?.mountEpoch !== payload.mountEpoch
      )
        ? {
          probeId: payload.probeId,
          requestId: payload.requestId,
          conversationId: payload.conversationId,
          mountEpoch: payload.mountEpoch,
          ...(payload.expectedRevision !== undefined ? { expectedRevision: payload.expectedRevision } : {}),
          status: 'mount_changed',
        }
        : conn.canvasOperationProgress.probe(payload);
      // A close after the metadata lookup must not be relabelled as
      // `not_running`: no response lets the backend classify the route as
      // unavailable, which is the only safe conclusion.
      if (!conn.client.isConnected()) return;
      conn.client.send({
        type: 'canvas_operation_probe_result',
        conversation_id: conn.conversationId,
        payload: response,
      } as FrontendMessage);
    });

    handler.on('canvas_superdoc_request', async (payload, message) => {
      // requestId est la clé commune avec la métrique Render côté backend. Ces
      // événements séparent réception, calcul du résultat et tentative d'envoi ;
      // le navigateur ne peut pas prouver que le backend a reçu cette réponse.
      const transportStartedAt = performance.now();
      const traceContext = {
        requestId: payload.requestId,
        tool: payload.tool,
        action: payload.tool === 'canvas_inspect' ? 'inspect' as const : payload.action.action,
        ...(typeof payload.mountEpoch === 'number' ? { mountEpoch: payload.mountEpoch } : {}),
      };
      captureCanvasAgentTransportDiagnostic({ stage: 'request_received', ...traceContext });
      const dureeTransportMs = (): number => Math.max(0, Math.round(performance.now() - transportStartedAt));
      const tracerLaTentativeDenvoi = (envelopeOutcome: 'success' | 'failure'): void => {
        captureCanvasAgentTransportDiagnostic({
          stage: 'response_send_attempted',
          ...traceContext,
          envelopeOutcome,
          durationMs: dureeTransportMs(),
        });
      };
      const tracerTransportIndisponible = (envelopeOutcome: 'success' | 'failure'): void => {
        captureCanvasAgentTransportDiagnostic({
          stage: 'transport_unavailable',
          ...traceContext,
          executionState: 'completed',
          envelopeOutcome,
          durationMs: dureeTransportMs(),
        });
      };

      // #44 (2026-10-20) : un onglet gelé (veille, économiseur de mémoire) reçoit
      // en rafale, des minutes plus tard, les trames écrites pendant son sommeil.
      // Le serveur a déjà classé cette écriture « effet inconnu » et rendu la main
      // à l'agent ; l'exécuter maintenant changerait le document à l'insu de
      // l'agent, et son reçu partirait sur une socket qui ne l'attend plus. Une
      // lecture, elle, ne change rien : elle s'exécute quel que soit son âge.
      if (payload.tool === 'canvas_perform_action') {
        const frameAgeMs = conn.client.serverFrameAgeMs(message.timestamp);
        if (frameAgeMs !== null && frameAgeMs > CANVAS_WRITE_MAX_DELIVERY_AGE_MS) {
          captureCanvasAgentTransportDiagnostic({ stage: 'delivered_too_late', ...traceContext, frameAgeMs });
          if (!conn.client.isConnected()) return;
          conn.client.send({
            type: 'canvas_superdoc_result',
            conversation_id: conn.conversationId,
            payload: {
              requestId: typeof payload.requestId === 'string' ? payload.requestId : 'canvas-superdoc-delivered-too-late',
              ...describeCanvasReceiptIdentity(payload),
              ok: false as const,
              code: 'CANVAS_WRITE_DELIVERED_TOO_LATE',
              capability: 'canvas_delivery_age_guard',
              error: `canvas_perform_action ${payload.action.action} (requestId=${payload.requestId}) reached the browser `
                + `${Math.round(frameAgeMs / 1000)} s after the server sent it (the lawyer's tab was asleep or offline). `
                + 'It was NOT applied: the document is unchanged by this command. Re-inspect the canvas before writing again.',
            },
          } as FrontendMessage);
          tracerLaTentativeDenvoi('failure');
          return;
        }
      }

      // Chemin d'ouverture 5/5 — surface agent canonique (`canvas_inspect` /
      // `canvas_perform_action`). Même règle que les quatre autres (#860).
      // L'identité ANNONCÉE par l'enveloppe, dans la seule forme que ce bundle
      // émet et reçoit (#1336). Elle est lue AVANT le schéma — la garde de
      // requête validera l'enveloppe pour de bon — parce que ce refus-ci doit
      // partir avant toute ouverture de canvas.
      const epoqueAnnoncee = typeof payload.mountEpoch === 'number' ? payload.mountEpoch : null;
      const conversationAnnoncee = typeof payload.conversationId === 'string' ? payload.conversationId : null;
      const exactSession = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
      // Cette enveloppe est déjà routée à un montage précis. Un écart ne doit
      // surtout pas passer par le chemin d'ouverture, qui monterait un résident
      // puis ferait du mauvais document le repli implicite (#1205). Le code de
      // protocole indique au backend qu'il doit relire ses références.
      //
      // ⚠️ C'est l'ÉPOQUE qui tranche : le fil ne peut plus changer de document,
      // mais il peut le REMONTER, et une commande autorisée sur le montage
      // d'avant n'adresse plus rien. La conversation est comparée elle aussi,
      // et ce n'est PAS une tautologie ici : elle vient du fil (le backend
      // pourrait router une commande vers la mauvaise connexion), pas du
      // registre.
      if (
        (conversationAnnoncee !== null && conversationAnnoncee !== conn.conversationId)
        || (epoqueAnnoncee !== null && exactSession?.mountEpoch !== epoqueAnnoncee)
      ) {
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_superdoc_result',
          conversation_id: conn.conversationId,
          payload: {
            requestId: typeof payload.requestId === 'string' ? payload.requestId : 'canvas-superdoc-session-changed',
            ...describeCanvasReceiptIdentity(payload),
            ok: false as const,
            code: 'DOCUMENT_SESSION_CHANGED',
            capability: 'canvas_session_router',
            error: `Canvas mount changed before ${payload.tool}: the command was routed to `
              + `conversationId=${conversationAnnoncee ?? 'non annoncée'} mountEpoch=${epoqueAnnoncee ?? 'non annoncée'}, `
              + `but this connection carries conversationId=${conn.conversationId} `
              + `mountEpoch=${exactSession?.mountEpoch ?? 'aucun montage'}. `
              + 'NEVER use the currently visible document as a fallback; re-inspect the routed document before retrying.',
          },
        } as FrontendMessage);
        tracerLaTentativeDenvoi('failure');
        return;
      }

      let progress: CanvasOperationProgressHandle | null = null;
      let unsubscribeProgressScope: (() => void) | null = null;
      const finishProgress = (): void => {
        unsubscribeProgressScope?.();
        unsubscribeProgressScope = null;
        progress?.finish();
        progress = null;
      };
      // The newer progress protocol has only the conversation/epoch identity.
      // Do not guess a legacy mount from the current visible document: silence
      // is safer than rearming a fence for the wrong Canvas.
      if (
        (conn.supportsCanvasOperationProgress || conn.supportsCanvasOperationProbe)
        && typeof payload.requestId === 'string'
        && conversationAnnoncee === conn.conversationId
        && epoqueAnnoncee !== null
      ) {
        const scope = canvasDocumentRuntime.getScope(conn.conversationId);
        if (scope?.getSnapshot().conversationId === conversationAnnoncee && scope.getSnapshot().mountEpoch === epoqueAnnoncee) {
          const currentProgress = conn.canvasOperationProgress.start({
            requestId: payload.requestId,
            conversationId: conversationAnnoncee,
            mountEpoch: epoqueAnnoncee,
            ...(payload.tool === 'canvas_perform_action' ? { expectedRevision: payload.expectedRevision } : {}),
          });
          progress = currentProgress;
          unsubscribeProgressScope = scope.subscribeSnapshot((snapshot) => {
            if (snapshot.conversationId !== conversationAnnoncee || snapshot.mountEpoch !== epoqueAnnoncee) {
              // A remount must not continue producing liveness for an operation
              // whose exact browser target has ceased to exist.
              finishProgress();
            }
          });
        }
      }

      try {
      const opened = await this.openCanvasForAgentWrite(
        sessionKey,
        conn.conversationId,
        typeof payload.tool === 'string' ? payload.tool : 'canvas_superdoc_request',
        { amorce: 'brouillon-du-fil' },
      );
      if (!opened.ok) {
        // Répondre ICI plutôt que laisser l'exécuteur échouer plus bas : lui ne
        // sait dire que « pas d'instance », sans nommer document ni conversation.
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_superdoc_result',
          conversation_id: conn.conversationId,
          payload: {
            requestId: typeof payload.requestId === 'string' ? payload.requestId : 'canvas-superdoc-unavailable',
            // `action` doit rester DANS l'énumération du verbe demandé : le
            // contrat partagé n'accepte `'inspect'` que pour `canvas_inspect`.
            // Un `'inspect'` posé sur un `canvas_perform_action` fait rejeter la
            // trame à la frontière — l'agent n'aurait alors reçu aucun message,
            // ce qui annule tout l'intérêt de répondre ici.
            ...describeCanvasReceiptIdentity(payload),
            ok: false as const,
            code: 'canvas_unavailable',
            capability: 'openCanvasForAgentWrite',
            error: opened.error,
          },
        } as FrontendMessage);
        tracerLaTentativeDenvoi('failure');
        return;
      }
      // The request is now ready to enter the guarded executor. Receiving the
      // frame or opening a host is not equivalent to invoking SuperDoc.
      progress?.queued();

      // Une ÉCRITURE seulement : `canvas_inspect` lit sans rien changer à l'écran.
      // Après la garde d'époque et l'ouverture réussie, avant l'exécution — le
      // dock bascule pendant que l'action s'applique, pas après son reçu. Les
      // refus de l'exécuteur tombent donc APRÈS le signal : l'avocat rejoint un
      // document que l'agent devait modifier, même quand la modification est
      // refusée (révision périmée, remplacement invisible…).
      if (payload.tool === 'canvas_perform_action' && typeof payload.requestId === 'string') {
        this.signalerEcritureAgentCanvas(conn, 'canvas_perform_action', `requete:${payload.requestId}`);
        if (remplaceLeDocument(payload.action)) {
          await this.laisserLEcranRejoindreLeFil(sessionKey, conn.conversationId);
        }
      }

      let result: CanvasSuperDocResultPayload;
      try {
        result = await executeCanvasAgentRequest(
          payload,
          canvasDocumentRuntime,
          // Seule l'identité métier (documentId/titre) vient du contexte UI.
          // La session du receipt reste celle de l'enveloppe routée et la garde
          // refuse tout changement de montage avant de laisser partir le résultat.
          (instance, envelope, { garde, resources }) => executeSuperDocCanvasRequest(instance, envelope, {
            garde,
            resources,
            contexte: this.buildCanvasExecutionContext(envelope.conversationId, {
              onExecutionStarted: () => progress?.running(),
              onUnitCompleted: (unit) => progress?.completedUnit(unit),
            }),
          }),
        );
      } catch (err) {
        // Sans cast : l'affectation à `result` fait vérifier l'appariement
        // `(tool, action)` par le compilateur (union corrélée de
        // `describeCanvasReceiptIdentity`, #1336 critère 5).
        result = {
          requestId: typeof payload.requestId === 'string' ? payload.requestId : 'canvas-superdoc-error',
          ...describeCanvasReceiptIdentity(payload),
          ok: false as const,
          code: 'frontend_executor_error',
          capability: 'executeSuperDocCanvasRequest',
          error: `${payload.tool} failed in frontend executor for requestId=${payload.requestId}: ${
            err instanceof Error ? err.message : String(err)
          }. Re-read/re-search canvas refs before retrying if the document changed.`,
        };
      }

      if (!conn.client.isConnected()) {
        // Le navigateur a terminé l'exécuteur, mais le courtier a pu dépasser
        // sa borne avant ce retour. Ne pas confondre cette issue avec une
        // mutation annulée ni affirmer que le backend a reçu un reçu tardif.
        tracerTransportIndisponible(result.ok ? 'success' : 'failure');
        return;
      }
      // ⚠️ **L'ÉCHO EST LOAD-BEARING, et son échec serait PERMISSIF** (#1336).
      // `SessionState.waitForCanvasOpResult` fige l'identité que le tool a
      // envoyée, et `resolveCanvasOpResult` ne compare que ce que le
      // navigateur RÉÉNONCE : une identité amputée en route n'y produit aucune
      // erreur visible, elle désarme simplement la corrélation. Les deux clés
      // sont déclarées par `canvasResultSuccessSchema` / `…Failure…` du contrat
      // partagé — le schéma est ré-appliqué côté backend, et Zod retire d'un
      // `z.object` toute clé non déclarée.
      //
      // Réénoncé depuis la REQUÊTE, jamais relu du runtime : la fence compare
      // ce que le tool a demandé à ce que le navigateur a fait ; y substituer
      // le montage courant ferait toujours coïncider les deux.
      conn.client.send({
        type: 'canvas_superdoc_result',
        conversation_id: conn.conversationId,
        payload: {
          ...result,
          ...(conversationAnnoncee !== null ? { conversationId: conversationAnnoncee } : {}),
          ...(typeof payload.mountEpoch === 'number' ? { mountEpoch: payload.mountEpoch } : {}),
          ...(payload.tool === 'canvas_perform_action' ? { expectedRevision: payload.expectedRevision } : {}),
        },
      } as FrontendMessage);
      tracerLaTentativeDenvoi(result.ok ? 'success' : 'failure');
      this.log(sessionKey, conn.conversationId, 'CANVAS_SUPERDOC', `tool=${payload.tool} action=${result.action} ok=${result.ok}`);
      } finally {
        finishProgress();
      }
    });

    handler.on('canvas_actions_request', async (payload) => {
      // Chemin d'ouverture 4/5 — 1er lot d'actions de la cascade
      // orchestrator-driven (M5 plan 2026-06-24 + UX 2026-06-29).
      //
      // WHY l'ouverture ici : la cascade tourne 5-10 min en silence (fan-out
      // moyen_builders → assembly inline → enqueue render GLM). Le panneau
      // s'ouvre PILE quand le contenu rendu arrive — signal visuel fort qu'on
      // bascule du raisonnement à la matérialisation.
      const opened = await this.openCanvasForAgentWrite(
        sessionKey,
        conn.conversationId,
        'canvas_actions_request',
        { amorce: 'brouillon-du-fil' },
      );
      if (!opened.ok) {
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_actions_result',
          conversation_id: conn.conversationId,
          payload: {
            requestId: payload.requestId,
            // Le même refus nommé sur chaque action du lot : le backend relaie
            // `results[]` tel quel au LLM, qui ne lira peut-être qu'une ligne.
            results: payload.actions.map((a: unknown) => ({ action: a, success: false, error: opened.error })),
          },
        } as FrontendMessage);
        return;
      }
      const superDocInstance = opened.instance;
      const editor = opened.editor;
      if (!conn.client.isConnected()) return;

      // Validation Zod à la réception (contrat @hector/shared-types/canvas) :
      // une action inconnue/malformée devient un résultat d'erreur PAR ACTION
      // (le batch continue — parité avec le try/catch par action du bridge),
      // jamais un crash du handler.
      const rawActions = Array.isArray(payload.actions) ? (payload.actions as unknown[]) : [];
      const invalidResults: CanvasActionResult[] = [];
      const validActions: CanvasAction[] = [];
      for (const raw of rawActions) {
        const parsed = CanvasActionSchema.safeParse(raw);
        if (parsed.success) {
          validActions.push(parsed.data);
        } else {
          invalidResults.push({
            // echo brut pour corrélation côté backend (le shape exact importe
            // peu ici : le backend relaie `results[]` tel quel au LLM).
            action: raw as CanvasAction,
            success: false,
            error: `action canvas invalide: ${parsed.error.issues
              .map((i) => `${i.path.join('.') || '(racine)'}: ${i.message}`)
              .join(' ; ')}`,
          });
        }
      }

      // Le pont ne bascule PAS le mode de l'éditeur (#1838) : ses écritures
      // suivent le régime de suivi du scope documentaire, comme la surface
      // canonique. Régime activé, l'éditeur est en `suggesting` et tout ce qui
      // s'y applique devient une révision ; régime coupé, il est en `editing` et
      // l'écriture est directe. Ouvrir une fenêtre `suggesting` autour du lot
      // faisait écrire en révision bouton éteint, suivait les frappes de
      // l'avocat pendant le lot, et écrasait au retour un clic sur son bouton.
      // ⚠️ LES GARDES DE #939 S'APPLIQUENT AU PONT (#979 / I1). Elles n'y étaient
      // pas : le pont attendait à chaque opération sans porter ni révision
      // attendue ni jeton d'époque, alors que la surface canonique fait les deux
      // depuis #939. C'est exactement ce que #939 annonçait comme la condition à
      // revoir — « le jour où ce pont attend quoi que ce soit ».
      //
      // Ce que le pont peut faire, et ce qu'il ne peut PAS :
      //  - ✅ **époque** : capturée ici, ré-assertée avant chaque appel mutant
      //    par `muterSousFences`. Un avocat qui ouvre un autre document en cours
      //    de lot interrompt le lot au lieu d'écrire dans l'éditeur d'avant.
      //  - ✅ **révision attendue** : RELUE avant chaque mutation, donc une
      //    frappe entre la lecture des index et l'écriture fait échouer l'appel
      //    plutôt que de poser la marque au mauvais endroit.
      //  - ✅ **sérialisation** : le lot passe par la file de mutations du
      //    runtime, comme la surface canonique, donc il ne s'entrelace pas avec
      //    une action agent concurrente.
      //  - ❌ **refus PRÉALABLE sur révision périmée** (`STALE_CANVAS_REVISION`,
      //    `canvasAgentRequestGuard`) : impossible, et c'est un fait du CONTRAT,
      //    pas un oubli. `CanvasActionsRequestPayloadSchema` ne porte ni
      //    `documentSessionId` ni `expectedRevision` — le pont n'a donc aucune
      //    valeur du demandeur à comparer. La garde optimiste par appel est ce
      //    qui s'en rapproche le plus, et elle protège le même défaut.
      //
      const doc = editor.doc as unknown as CanvasWriteDocumentApi;
      const sessionCanvas = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
      const epoqueAttendue: JetonDEpoque | null = sessionCanvas && sessionCanvas.state === 'ready'
        ? { conversationId: sessionCanvas.conversationId, mountEpoch: sessionCanvas.mountEpoch }
        : null;
      const etatMutation = { tentee: false };
      const contexte = {
        doc,
        queryRoot: sessionCanvas
          ? canvasDocumentRuntime.getScope(conn.conversationId)?.getDomResources().host
          : undefined,
        verifierEpoque: epoqueAttendue
          ? creerVerificateurDEpoque(
              epoqueAttendue,
              () => {
                const courante = canvasDocumentRuntime.getSessionForConversation(epoqueAttendue.conversationId);
                return courante && courante.state === 'ready'
                  ? { conversationId: courante.conversationId, mountEpoch: courante.mountEpoch }
                  : null;
              },
              etatMutation,
            )
          : // Sans session prête, il n'y a pas d'époque à comparer. On ne
            // fabrique PAS un jeton qui « ressemble » : le vérificateur devient
            // un no-op explicite, et c'est le seul cas où le lot n'est pas
            // protégé par cette garde-là (la révision attendue, elle, s'applique
            // toujours).
            () => undefined,
        mutate: async <T,>(operation: () => T | Promise<T>): Promise<T> => {
          etatMutation.tentee = true;
          return operation();
        },
      } as unknown as CanvasBridgeContext;

      // Sérialisé sur la file du runtime, comme la surface canonique : deux
      // écritures agent concurrentes sur le même document s'entrelaceraient
      // sinon entre les `await` de chaque lot.
      // L'hôte d'interface porte DEUX surfaces, et elles ne vivent pas au même
      // endroit : `editor.commands` (V1, `null` en V2) et `superdoc.ui` — la
      // surface publique d'ÉTAT D'INTERFACE de la V2, portée par l'INSTANCE.
      // C'est elle qui a levé les deux TODO(#980) de `navigate_to_paragraph` et
      // `set_active_comment` : #979 les avait cherchés sur `doc.*`, qui décrit
      // le document et non la vue.
      const hoteUi: HoteUiCanvas = {
        commands: (editor as unknown as HoteUiCanvas).commands,
        ui: (superDocInstance as unknown as HoteUiCanvas | null | undefined)?.ui,
      };
      const bridgeResults: CanvasActionResult[] = epoqueAttendue
        ? await canvasDocumentRuntime.enqueueMutation(
            epoqueAttendue.conversationId,
            () => applyActions(contexte, validActions, hoteUi),
          )
        : await applyActions(contexte, validActions, hoteUi);
      if (
        epoqueAttendue
        && bridgeResults.some((result) => result.success && canvasActionChangesDocument(result.action))
      ) {
        // Ne jamais attendre le seul événement SuperDoc ici : l'éviction peut
        // arriver avant lui. Ce contrôleur est le contrat de persistance que la
        // capacité consulte avant de refroidir le résident.
        canvasDocumentRuntime.getDraftController(epoqueAttendue.conversationId)?.markDirty();
      }
      const results = [...invalidResults, ...bridgeResults];
      // Re-contrôle APRÈS l'attente (#979 / M2) : le lot attend désormais, donc
      // la connexion a pu mourir entre-temps — même patron que les handlers
      // voisins, qui vérifient juste avant d'émettre.
      if (!conn.client.isConnected()) return;
      conn.client.send({
        type: 'canvas_actions_result',
        conversation_id: conn.conversationId,
        payload: { requestId: payload.requestId, results },
      } as FrontendMessage);
      this.log(sessionKey, conn.conversationId, 'CANVAS_ACTIONS', `count=${results.length} ok=${results.filter((r) => r.success).length}`);
    });

    handler.on('canvas_generation_request', async (payload) => {
      /**
       * LA GÉNÉRATION ORCHESTRATEUR — même parseur, même écriture que le flux
       * (#938).
       *
       * L'acte rendu par le job worker de la cascade arrive ici en **markdown
       * verbatim**. Il traverse le `StructuralParser` de `@hector/shared-types`
       * — la même classe que `redactionStreamHub` instancie pour la génération
       * en flux — puis le point d'écriture unique du canvas. Il n'existe donc
       * plus qu'un seul chemin entre « le modèle a produit l'acte » et « l'acte
       * est dans le canvas », et les sauts de page, les tables et le gras
       * arrivent des deux côtés.
       *
       * Chemin d'ouverture : la cascade tourne 5-10 min en silence, le panneau
       * s'ouvre PILE quand le contenu rendu arrive.
       */
      const requestId = payload.requestId;
      const repondre = (
        reponse:
          | { ok: true; blocsEcrits: number; canvasAgentStateSynchronized?: boolean }
          | { ok: false; error: string },
      ): void => {
        if (!conn.client.isConnected()) return;
        conn.client.send({
          type: 'canvas_generation_result',
          conversation_id: conn.conversationId,
          payload: { requestId, ...reponse },
        } as FrontendMessage);
      };

      if (!payload.jobId) {
        repondre({
          ok: false,
          error:
            `canvas_generation_request refused before canvas acquisition: requestId=${requestId} has no jobId. `
            + 'The render worker must send its stable jobId so the browser can bind the write to one conversation and document session.',
        });
        return;
      }
      const jobId = payload.jobId;

      // Le rendu orchestrateur n'ouvre aucun flux HTTP : il lui faut pourtant
      // la même liaison immuable que les jobs streamés. La poser avant l'admission
      // et l'ouverture empêche un job rejoué sur une autre connexion de muter
      // le document visible de cette dernière.
      // Reply even if session acquisition fails before the generation binding:
      // the render worker is already waiting for this exact requestId.
      try {
        canvasDocumentRuntime.acquireForConversation(conn.conversationId);
      } catch (err) {
        repondre({
          ok: false,
          error: `canvas_generation_request could not resolve the canvas session for conversationId=${conn.conversationId} `
            + `jobId=${jobId}: ${err instanceof Error ? err.message : String(err)}. Nothing was written.`,
        });
        return;
      }
      const liaison = redactionStreamHub.lierTravail({
        kind: 'generation',
        jobId,
        conversationId: conn.conversationId,
      });
      if (liaison.statut === 'conflit') {
        const error = `canvas_generation_request refused for jobId=${jobId}: already bound to `
          + `conversationId=${liaison.liaisonExistante.conversationId}; received `
          + `conversationId=${conn.conversationId}. `
          + 'Resume the owning conversation instead of retrying against the visible canvas.';
        repondre({ ok: false, error });
        this.log(sessionKey, conn.conversationId, 'CANVAS_GENERATION_CONFLICT', error);
        return;
      }

      // Avec un papier à en-tête, le rendu DÉMARRE un document : aucune amorce du brouillon du fil,
      // qu'on remplacerait aussitôt.
      const opened = await this.openCanvasForAgentWrite(
        sessionKey,
        conn.conversationId,
        'canvas_generation_request',
        payload.depart ? { amorce: 'aucune', depart: payload.depart } : { amorce: 'brouillon-du-fil' },
      );
      if (!opened.ok) {
        repondre({ ok: false, error: opened.error });
        return;
      }
      if (payload.depart) {
        // Le rendu s'écrit sur une page NEUVE de ce modèle, jamais à la suite du document ouvert
        // (Word importé, modèle de bibliothèque, autre brouillon), qui reste dans le fil. Même
        // geste que « Nouveau », sous l'épingle du tour posée par l'ouverture.
        const epingleDuTour = conn.documentTurnPins.get(this.documentTurnPinKey(conn.currentLocalTurnId))?.runtimePin;
        const pageNeuve = await ouvrirUnDocumentVierge(
          conn.conversationId, undefined, payload.depart, epingleDuTour,
        );
        if (pageNeuve.issue !== 'vierge') {
          repondre({
            ok: false,
            error: pageNeuve.issue === 'refus-de-sortie'
              ? decrireLeRefusDeSortiePourLAgent(conn.conversationId, pageNeuve.refus)
              : `canvas_generation_request refused before writing jobId=${jobId}: a new document could not `
                + `replace the open one in conversationId=${conn.conversationId} (${pageNeuve.issue}). Nothing `
                + 'was written and the lawyer still sees the previous document.',
          });
          return;
        }
        try {
          await canvasDocumentRuntime.waitForReady(
            { conversationId: conn.conversationId, mountEpoch: pageNeuve.mountEpoch, documentId: null },
            CANVAS_DOCUMENT_REPLACEMENT_TIMEOUT_MS,
          );
        } catch (err) {
          repondre({
            ok: false,
            error: `canvas_generation_request refused before writing jobId=${jobId}: the new document `
              + `(mountEpoch=${pageNeuve.mountEpoch}) never became ready: `
              + `${err instanceof Error ? err.message : String(err)}. Nothing was written.`,
          });
          return;
        }
      }

      const sessionAttendue = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
      if (!sessionAttendue || sessionAttendue.state !== 'ready') {
        repondre({
          ok: false,
          error:
            `canvas_generation_request refused before writing jobId=${jobId}: conversationId=`
            + `${conn.conversationId} has no ready mount (state=${sessionAttendue?.state ?? 'aucun montage'}). `
            + 'Remount the routed document; never use the visible canvas as fallback.',
        });
        return;
      }
      const epoqueAttendue: JetonDEpoque = {
        conversationId: sessionAttendue.conversationId,
        mountEpoch: sessionAttendue.mountEpoch,
      };
      const travailEstToujoursRoute = (): boolean => {
        const courante = canvasDocumentRuntime.getSessionForConversation(epoqueAttendue.conversationId);
        const liaisonCourante = redactionStreamHub.lireLiaison(jobId);
        return memeEpoque(
          epoqueAttendue,
          courante && courante.state === 'ready'
            ? { conversationId: courante.conversationId, mountEpoch: courante.mountEpoch }
            : null,
        )
          && liaisonCourante?.conversationId === conn.conversationId;
      };

      const blocs: StructuredBlock[] = [];
      try {
        const parseur = new StructuralParser({ onBlock: (bloc) => blocs.push(bloc) });
        parseur.push(payload.markdown);
        // Un rendu de cascade n'a AUCUN délimiteur `[/PARA]` : sans `finalize()`,
        // `drain()` n'émettrait rien du tout (#931).
        parseur.finalize();
      } catch (err) {
        repondre({
          ok: false,
          error:
            `Le parseur structurel a échoué sur le markdown rendu (${payload.markdown.length} caractères, `
            + `job ${payload.jobId ?? 'inconnu'}) : ${err instanceof Error ? err.message : String(err)}.`,
        });
        return;
      }

      if (blocs.length === 0) {
        // Mode d'échec réel, pas théorique : le parseur jette tout ce qui précède
        // le premier `#` ou `[Hector-`, donc un rendu sans titre ni tag de style
        // produit zéro bloc — et le canvas resterait vide pendant que l'agent
        // annonce un plan livré.
        repondre({
          ok: false,
          error:
            `Le markdown rendu (${payload.markdown.length} caractères, job ${payload.jobId ?? 'inconnu'}) n'a produit `
            + 'AUCUN bloc structuré : rien n\'a été écrit dans le canvas. Le parseur partagé ignore tout ce qui précède '
            + 'le premier titre markdown (`# …`) — vérifie que le rendu commence bien par un titre.',
        });
        return;
      }

      const ecrire = canvasDocumentRuntime.getScope(epoqueAttendue.conversationId)
        ?.getPhysicalResources().writer ?? null;
      if (!ecrire) {
        repondre({
          ok: false,
          error:
            'Le canvas est ouvert mais son point d\'écriture n\'est pas encore posé (montage en cours). '
            + `Les ${blocs.length} blocs de l'acte rendu n'ont PAS été écrits.`,
        });
        return;
      }

      // Liaison, ouverture, montage prêt, au moins un bloc, point d'écriture posé :
      // plus rien ne refuse avant l'écriture elle-même.
      this.signalerEcritureAgentCanvas(conn, 'canvas_generation_request', `job:${jobId}`);
      const resultat = await ecrire(blocs);
      // `ecrire` est asynchrone et le document de ce fil peut être remonté avec
      // une époque neuve pendant son exécution. Son point d'écriture garde les
      // mutations internes ; ce second fence interdit surtout d'accuser le
      // succès de l'ancien travail sur la connexion désormais montée ailleurs.
      if (!travailEstToujoursRoute()) {
        repondre({
          ok: false,
          error:
            `canvas_generation_request result discarded for jobId=${jobId}: expected `
            + `conversationId=${epoqueAttendue.conversationId} mountEpoch=${epoqueAttendue.mountEpoch}, `
            + 'but the routed mount changed during the write. '
            + 'Inspect the owning conversation before any retry.',
        });
        return;
      }
      if (resultat.statut === 'ecrit') {
        // Generation writes bypass the agent action receipt path. Refresh the
        // live revision and announce it before ACK so the next model call does
        // not receive the pre-insertion Canvas state. The insertion result and
        // this synchronization proof remain separate facts (#1717).
        let revisionApresEcriture: string | null = null;
        let agentStateSynchronized = false;
        let synchronisationEnErreur = false;
        try {
          revisionApresEcriture = await canvasDocumentRuntime.syncRevisionFromEditor(conn.conversationId);
          const sessionApresEcriture = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
          agentStateSynchronized = revisionApresEcriture !== null
            && sessionApresEcriture?.state === 'ready'
            && memeEpoque(epoqueAttendue, {
              conversationId: sessionApresEcriture.conversationId,
              mountEpoch: sessionApresEcriture.mountEpoch,
            })
            && sessionApresEcriture.revision === revisionApresEcriture
            ? await this.announceCanvasDocumentState(sessionApresEcriture)
            : false;
        } catch {
          // The writer has already applied the insertion. A failed revision
          // refresh must not turn that success into a retryable write failure.
          synchronisationEnErreur = true;
        }
        if (!agentStateSynchronized) {
          console.warn('[canvas] insertion appliquée, mais état agent non synchronisé', {
            conversationId: conn.conversationId,
            mountEpoch: epoqueAttendue.mountEpoch,
            jobId,
            revisionApresEcritureConnue: revisionApresEcriture !== null,
            synchronisationEnErreur,
          });
        }
        repondre({
          ok: true,
          blocsEcrits: resultat.blocsEcrits,
          canvasAgentStateSynchronized: agentStateSynchronized,
        });
        this.log(sessionKey, conn.conversationId, 'CANVAS_GENERATION', `blocs=${resultat.blocsEcrits}`);
        return;
      }
      // Chaque refus dit ce qui le lève. Un message qui nomme seulement la cause
      // laisse l'agent relancer à l'identique : soit il boucle sur une file que
      // rien ne rouvrira, soit il retente une écriture qui restera refusée tant
      // que le flux vit. La sortie fait donc partie du message.
      const detail =
        resultat.statut === 'echec'
          ? resultat.erreur.message
          : resultat.statut === 'refusee'
            ? {
                'flux-en-cours':
                  'une génération en flux écrit DÉJÀ dans ce canvas. Les deux chemins partagent le même '
                  + 'point d\'écriture : écrire par-dessus tronquerait l\'acte en cours puis le dupliquerait. '
                  + 'Le markdown rendu reste récupérable dans le résultat du job — attends la fin de la '
                  + 'rédaction en cours avant de le pousser.',
                condamnee:
                  'le point d\'écriture de ce canvas est CONDAMNÉ après un échec ou un dépassement de borne. '
                  + 'Seul un remontage du canvas le lève : ne relance pas, aucune écriture n\'aboutira. '
                  + 'Demande à l\'avocat de recharger la page.',
                abandonnee:
                  'le canvas a été démonté pendant l\'écriture : le document visé n\'est plus à l\'écran. '
                  + 'Ne relance pas sur cette session.',
                'travail-remplace':
                  'un autre travail a pris la place de celui-ci dans le canvas. Ne relance pas : '
                  + 'ré-inspecte pour voir ce que le document porte maintenant.',
              }[resultat.raison]
            : 'le point d\'écriture n\'a rien trouvé de neuf à écrire';
      repondre({
        ok: false,
        error: `L'acte rendu (${blocs.length} blocs) n'a pas été écrit dans le canvas — ${detail}`,
      });
      this.log(sessionKey, conn.conversationId, 'CANVAS_GENERATION_ECHEC', `${resultat.statut} — ${detail}`);
    });

    // -- Liste des dossiers de la conversation (spec #2592) --
    //
    // La trame REMPLACE la liste. Validée ici parce qu'elle pilote le dossier du
    // composeur, donc le `case_id` des messages suivants : une forme inattendue
    // doit être ignorée, pas appliquée.
    handler.on('conversation_cases', (payload) => {
      const parsed = ConversationCasesPayloadSchema.safeParse(payload);
      if (!parsed.success) {
        console.error(
          `[WebSocketPool] conversation_cases ignorée pour la conversation ${conn.conversationId} : trame hors contrat`,
          parsed.error.issues,
        );
        return;
      }
      if (parsed.data.conversation_id !== conn.conversationId) {
        this.log(
          sessionKey,
          conn.conversationId,
          'CONVERSATION_CASES_IDENTITY_CONFLICT',
          `received=${parsed.data.conversation_id}; socket kept=${conn.conversationId}`,
        );
        return;
      }
      this.events.emit('conversation_cases_changed', sessionKey, parsed.data);
      this.log(sessionKey, conn.conversationId, 'CONVERSATION_CASES', `cases=${parsed.data.cases.length}`);
    });

    // -- Sorties cliquables proposées par l'agent --
    //
    // Fire-and-forget : aucun agent n'attend de réponse (l'agent a fini son tour),
    // donc pas de frame `_response`/`_result` en miroir et pas de sous-état
    // 'streaming'. Le clic est routé par l'hôte qui monte le chat.
    handler.on('agent_ui_actions', (payload) => {
      const timeline = this.getTimeline(conn);
      const updated = conn.reducer.applyAgentUiActions(timeline, payload);
      this.updateTimeline(conn, updated);
      this.log(
        sessionKey,
        conn.conversationId,
        'UI_ACTIONS',
        payload.actions.map((a) => a.id).join(','),
      );
    });

    // -- Personal skill validation flow (exact top-level shared frames) --

    handler.on('skill_change_request', (request) => {
      const timeline = this.getTimeline(conn);
      this.updateTimeline(conn, conn.reducer.applySkillChangeRequest(timeline, request));
      this.log(
        sessionKey,
        conn.conversationId,
        'SKILL_CHANGE_REQUEST',
        `requestId=${request.requestId} operation=${request.operation}`,
      );
    });

    handler.on('skill_change_result', (result) => {
      const timeline = this.getTimeline(conn);
      this.updateTimeline(conn, conn.reducer.applySkillChangeResult(timeline, result));
      // Only the backend success verdict proves a write occurred. Errors/refusals
      // keep caches untouched and, critically, never replay the original mutation.
      if (result.status === 'success') {
        this.events.emit('skills_mutated', sessionKey);
      }
      this.log(
        sessionKey,
        conn.conversationId,
        'SKILL_CHANGE_RESULT',
        `requestId=${result.requestId} status=${result.status}`,
      );
    });

    // -- Context usage --

    handler.on('context_usage_update', (payload) => {
      this.events.emit('memory_usage_updated', sessionKey, payload);
    });

    // -- Compaction manuelle --
    //
    // ⚠️ Aucun `ensureStreaming` / `setStatus` ici, à dessein : la compaction a
    // lieu HORS tour. La faire passer par le statut de session ouvrirait la plume,
    // le bouton « Arrêter » et le toast « Réponse prête » d'un tour qui n'existe
    // pas — c'est exactement pourquoi le contrat lui donne une trame dédiée au
    // lieu de `backend_tool`.
    handler.on('context_compaction_status', (payload) => {
      this.log(
        sessionKey,
        conn.conversationId,
        'COMPACT_CONTEXT_STATUS',
        `status=${payload.status} reason=${payload.reason ?? 'none'} phase=${conn.contextCompaction.phase} before=${payload.tokensBefore ?? '?'} after=${payload.tokensAfter ?? '?'}`,
      );
      switch (payload.status) {
        case 'started':
          // `running` est publié dès l'envoi. Un `started` tardif, après le
          // garde-fou client, ne doit pas rebloquer un composer sans minuteur.
          return;
        case 'completed':
          // Même tardive (après le garde-fou), une réussite reste une information
          // vraie : l'historique a changé et doit être relu.
          this.settleContextCompaction(conn, {
            status: 'completed',
            ...(payload.tokensBefore !== undefined ? { tokensBefore: payload.tokensBefore } : {}),
            ...(payload.tokensAfter !== undefined ? { tokensAfter: payload.tokensAfter } : {}),
          }, { historyStale: true });
          return;
        case 'error':
        case 'refused':
          if (conn.contextCompaction.phase === 'idle') {
            // Déjà soldée côté client (garde-fou) : un second toast d'échec
            // contredirait le premier sans rien apprendre de plus à l'avocat.
            return;
          }
          this.settleContextCompaction(conn, {
            status: payload.status,
            ...(payload.userMessage ? { userMessage: payload.userMessage } : {}),
          }, { historyStale: false });
          return;
        default: {
          // Une génération de backend plus récente peut ajouter une phase : ne
          // rien deviner, garder l'état courant et le laisser visible au journal.
          const unknownStatus: never = payload.status;
          this.log(sessionKey, conn.conversationId, 'COMPACT_CONTEXT_STATUS_UNKNOWN', `status=${String(unknownStatus)}`);
        }
      }
    });

    handler.on('agent_status_note', (payload) => {
      this.updateTimeline(conn, conn.reducer.applyStatusNote(this.getTimeline(conn), payload.message));
    });

    // -- Rattrapage d'un tour repris au chargement de page (#1888) --

    handler.on('turn_catchup', (payload) => {
      const pending = conn.catchup;
      if (!pending || pending.turnId !== conn.currentLocalTurnId) {
        // Une reconnexion sans rechargement tient déjà le début du tour, et un tour fini ou
        // remplacé n'a plus rien à rattraper : l'appliquer doublerait ou écraserait l'écran.
        this.log(
          sessionKey,
          conn.conversationId,
          'TURN_CATCHUP_IGNORED',
          `part=${payload.part} entries=${payload.entries.length} requestedFor=${pending?.turnId ?? 'none'} currentTurn=${conn.currentLocalTurnId ?? 'none'}`,
        );
        return;
      }
      // La trame 0 ouvre un rattrapage : les trames d'une socket tombée avant sa dernière
      // trame appartiennent à un rattrapage abandonné, que la nouvelle demande reprend en entier.
      if (payload.part === 0) pending.entries = [];
      pending.entries = pending.entries.concat(payload.entries);
      // Rien de visible avant la dernière trame : un début de tour posé par morceaux
      // monterait un texte court que la suite animerait caractère par caractère.
      if (!payload.last) return;

      conn.catchup = null;
      const timeline = this.getTimeline(conn);
      this.updateTimeline(
        conn,
        payload.incomplete
          // Journal plafonné côté backend : le début n'existe plus. Le tour est adopté quand
          // même, donc la note d'attente « hors de portée » est fausse ; la base rendra la
          // réponse entière à la fin du tour (`resumedOnLoad`).
          ? conn.reducer.applyStatusNote(conn.reducer.removeServerFinishing(timeline), TURN_CATCHUP_INCOMPLETE_NOTE)
          : conn.reducer.rebuildFromCatchup(timeline, pending.entries),
      );
      this.ensureStreaming(conn);
      this.log(
        sessionKey,
        conn.conversationId,
        'TURN_CATCHUP_APPLIED',
        `entries=${pending.entries.length} parts=${payload.part + 1} incomplete=${payload.incomplete}`,
      );
    });

    // -- Turn settled out of reach (suite de `turn_in_progress`) --

    handler.on('turn_settled', (_payload, message) => {
      if (message.conversation_id && message.conversation_id !== conn.conversationId) {
        this.log(
          sessionKey,
          conn.conversationId,
          'CONVERSATION_IDENTITY_CONFLICT',
          `turn_settled received=${message.conversation_id}; socket kept=${conn.conversationId}`,
        );
        return;
      }
      // Garde d'état : `turn_settled` ne suit jamais autre chose qu'un `turn_in_progress`
      // (sonde `watchTurnSettlement` côté backend). Une trame tardive qui arriverait alors
      // qu'un tour LOCAL streame sur cette même socket (fenêtre étroite : watch armé pour
      // une compaction, puis nouveau tour) rollbackerait le tour vivant et rouvrirait le
      // composer. La note `server_finishing` est le témoin de l'état attendu.
      // Le témoin est double : la note (absente pendant une chasse d'instance) OU le drapeau
      // posé au verdict — sans lui, un `turn_settled` reçu pendant la chasse serait ignoré.
      if (!conn.remoteTurnAwaited && !this.getTimeline(conn).some((item) => item.type === 'server_finishing')) {
        this.log(sessionKey, conn.conversationId, 'TURN_SETTLED_IGNORED', 'no remote turn was being awaited on this socket');
        return;
      }
      // Le tour est réglé : plus rien à chasser. Une gagnante tardive n'aurait rien à
      // rejouer ; on ferme les candidates avant de rendre la session.
      conn.client.abortHunt();
      // Le backend a persisté en entier un tour qu'il n'a pas pu nous streamer. Le tour
      // local n'est qu'un fragment (tokens reçus avant la coupure) : on l'abandonne et on
      // recharge l'historique. C'est le bridge (`handleTurnSettled`) qui fait les deux,
      // dans l'ordre qu'impose le store : rollback AVANT réhydratation.
      // Fin de tour ⇒ mêmes libérations qu'`agent_done` (pin documentaire, jobs liés).
      // Après un `conversation_busy`, le tour local n'a JAMAIS été persisté : accusé
      // `false` pour rendre le brouillon. Sinon, un verrou tenu puis relâché prouve
      // que le message a franchi la persistance : accusé `true`.
      this.settleRemoteTurn(conn, sessionKey, {
        accepted: !conn.busyAwaitingSettle,
        reason: conn.busyAwaitingSettle ? 'turn_settled après conversation_busy — brouillon rendu' : 'turn_settled (tour terminé hors de portée)',
      });
    });

    // -- Agent done --

    handler.on('agent_done', (payload) => {
      // Une trame terminale d'une autre conversation ne peut jamais toucher
      // cette connexion : libérer son pin avant ce contrôle refroidirait le
      // mauvais résident malgré l'identité exacte capturée au démarrage.
      if (payload.conversationId && payload.conversationId !== conn.conversationId) {
        this.log(
          sessionKey,
          conn.conversationId,
          'CONVERSATION_IDENTITY_CONFLICT',
          `agent_done received=${payload.conversationId}; socket kept=${conn.conversationId}`,
        );
        return;
      }
      // Un `agent_done` clôt le tour de cette socket : si la session avait été adoptée
      // après `conversation_busy` ou par une chasse gagnée, l'attente est soldée par ce
      // terminal, pas par turn_settled.
      conn.busyAwaitingSettle = false;
      conn.remoteTurnAwaited = false;

      // A replayed terminal frame still proves that the saved user turn was
      // durable, even if the one-shot acknowledgement was lost with the socket.
      this.resolvePendingMessageAcceptance(conn, true);
      conn.pendingMessage = null;
      // Fin de tour = libération du pin capturé, dans TOUTES les branches y
      // compris `server_shutdown`. Le stream SSE peut finir avant le tour : la
      // borne est donc ce terminal, jamais la fin du flux de rédaction.
      this.releaseDocumentTurnPin(conn, conn.currentLocalTurnId, `agent_done ${payload.reason}`);
      for (const [jobId, turnId] of conn.redactionTurnIdByJob) {
        if (turnId === conn.currentLocalTurnId) conn.redactionTurnIdByJob.delete(jobId);
      }

      if (payload.memoryUsage) {
        this.events.emit('memory_usage_updated', sessionKey, payload.memoryUsage);
      }

      const timeline = this.getTimeline(conn);
      let updated = conn.reducer.finalize(timeline, payload.reason);

      // Server restarting — stay ready, don't show error, don't emit session_done
      if (payload.reason === 'server_shutdown') {
        this.updateTimeline(conn, updated);
        this.setStatus(conn, 'ready');
        this.log(sessionKey, conn.conversationId, 'SHUTDOWN', 'server restarting');
        return;
      }

      if (payload.reason === 'error') {
        // WHY: si `details` est vide, le backend a déjà émis un agent_status_note
        // (rate_limit_note) suffisamment explicite — un agent_error en plus ferait
        // doublon avec un ton contradictoire (note rassurante vs bandeau amber).
        if (payload.details) {
          updated = [
            ...updated,
            { type: 'agent_error' as const, id: `err-${Date.now()}`, message: payload.details, source: 'agent_done' },
          ];
        }
        this.updateTimeline(conn, updated);
        this.setStatus(conn, 'error');
        this.log(sessionKey, conn.conversationId, 'AGENT_ERROR', payload.details);
        this.emitTerminalSnapshot(conn, 'error', updated, payload.messageId, payload.citationRegistry);
        return;
      }

      // Arrêt volontaire (clic Stop) : note discrète, jamais le bandeau amber
      // d'`agent_error`. Le backend envoie désormais reason='stopped' au lieu d'une
      // erreur de service quand l'avocat interrompt en plein stream (cf. MasterLoop).
      if (payload.reason === 'stopped') {
        updated = [
          ...updated,
          { type: 'user_stopped' as const, id: `stopped-${Date.now()}` },
        ];
      }

      this.updateTimeline(conn, updated);

      this.emitTerminalSnapshot(conn, payload.reason, updated, payload.messageId, payload.citationRegistry);

      // Transition to ready — session stays alive for follow-up messages
      this.setStatus(conn, 'ready');
      this.log(sessionKey, conn.conversationId, 'AGENT_DONE', `reason=${payload.reason}`);
    });

    // -- Session expired (backend inactivity timeout 30min) --

    handler.on('session_expired', () => {
      this.releaseDocumentTurnPin(conn, conn.currentLocalTurnId, 'session_expired');
      this.resolvePendingMessageAcceptance(conn, false);
      this.log(sessionKey, conn.conversationId, 'EXPIRED', 'inactivity 30min');
      this.setStatus(conn, 'error');
      this.cleanup(sessionKey);
    });

    // -- Errors --

    handler.on('error', (payload) => {
      conn.pendingMessage = null;
      const backendError = normalizeBackendErrorPayload(payload);
      // Avant de rendre le verdict d'acceptation : l'envoi qui l'attend doit déjà
      // connaître le refus pour relire la liste et rendre le bon dossier au composeur.
      const caseScopeRefusal = readConversationCaseRefusal(payload, backendError.message);
      if (caseScopeRefusal) this.events.emit('case_scope_refused', sessionKey, caseScopeRefusal);
      const isConversationBusy = backendError.code === 'conversation_busy';
      // `conversation_busy` : l'accusé reste EN ATTENTE (voir busyAwaitingSettle) ;
      // tout autre refus rend le brouillon tout de suite.
      if (!isConversationBusy) this.resolvePendingMessageAcceptance(conn, false);
      // L'échec annoncé libère le pin exact — SAUF `conversation_busy`, qui n'est pas
      // terminal : ce code dit qu'un envoi a été rejeté PARCE QU'un autre tour
      // de la même conversation est déjà en cours. Ce tour-là rédige peut-être
      // en ce moment même ; lui retirer son pin le priverait du canvas au
      // milieu de sa séquence.
      if (backendError.code !== 'conversation_busy') {
        this.releaseDocumentTurnPin(
          conn, conn.currentLocalTurnId,
          `error ${backendError.code ?? 'sans code'}`,
        );
      }
      const timeline = this.getTimeline(conn);

      // WHY no ErrorService.handle here: this is a backend protocol frame, not
      // a frontend exception. Reporting it as `new Error(payload.error)` created
      // noisy PostHog issues while the user saw no actionable chat feedback.
      const updated: TimelineItem[] = [
        ...timeline,
        {
          type: 'agent_error' as const,
          id: `err-${Date.now()}`,
          message: backendError.message,
          source: 'backend_error' as const,
          ...(backendError.code ? { backendCode: backendError.code } : {}),
        },
      ];
      this.updateTimeline(conn, updated);
      if (isConversationBusy && conn.client.isConnected()) {
        // 2026-09-28 : un tour tourne déjà (autre onglet, autre instance, ou le tour
        // que cette page vient de quitter par F5). Rouvrir la saisie aussitôt laissait
        // l'avocat renvoyer en boucle, et un renvoi tombant sur l'instance du tour le
        // REMPLAÇAIT (cross-tab adoption) : travail perdu. On garde le composer fermé
        // et on arme la surveillance du tour distant : le backend répondra
        // `turn_in_progress` (note « la réponse se termine ») puis `turn_settled`
        // (rechargement de l'historique, brouillon rendu), ou `resumed:false` si le
        // tour vient de finir (rechargement immédiat).
        conn.busyAwaitingSettle = true;
        this.setStatus(conn, 'streaming');
        this.sendSessionResume(conn, sessionKey, 'conversation_busy');
      } else {
        if (isConversationBusy) this.resolvePendingMessageAcceptance(conn, false);
        this.setStatus(conn, isConversationBusy ? 'ready' : 'error');
        if (!isConversationBusy) {
          this.emitTerminalSnapshot(conn, 'error', updated);
        }
      }
      this.log(
        sessionKey,
        conn.conversationId,
        'ERROR',
        `code=${backendError.code ?? 'none'} ${backendError.message}`,
      );
    });

    // -- Connection state changes --

    client.setOnConnectionChange((connected: boolean, reason?: ConnectionCloseReason) => {
      this.log(sessionKey, conn.conversationId, 'CONN_CHANGE', `connected=${connected} reason=${reason ?? 'n/a'} status=${conn.status}`);
      this.events.emit('connection_changed', sessionKey, connected);

      if (!connected) {
        // WHY la reconnexion EN PREMIER (incident 2026-09-11) : le bloc d'affichage
        // ci-dessous ne s'exécute QUE pendant un tour (`isExchangeActive`), et il se
        // trouvait avant cet appel. Tout ce qui s'y passait de travers — une exception
        // remontée d'un abonné de `timeline_updated`, par exemple — emportait le
        // callback avant la relance, et le slot restait figé en `streaming` pour
        // toujours : plus de socket, donc plus de `session_resume`, donc pas
        // d'adoption du MasterLoop, et la réponse déjà produite (texte, cartes de
        // consentement) partait à la poubelle avec le tampon de replay backend.
        // Mesuré ce jour-là en production : 30 coupures en plein tour sur la
        // journée, 0 adoption (13 coupures sur la fenêtre 13h–14h45 où la
        // jointure coupure → reconnexion → reprise a été faite, 0 reprise émise).
        // La reprise d'un tour payé ne doit jamais dépendre du rendu d'un indicateur.
        // Une chasse d'instance en cours sur ce client n'a plus de socket d'origine à
        // remplacer : l'abandonner, sinon une gagnante adoptée pendant le backoff serait
        // jetée par la reconnexion qui suit (revue 2026-09-29).
        conn.client.abortHunt();
        if (reason === 'unexpected') {
          conn.reconnector.attempt();
        }

        // La réponse sur la capacité appartient à la socket morte : la prochaine se
        // renégocie à son ACK, et rien ne part avant. La valeur PUBLIÉE, elle,
        // reste — une coupure ne rend pas le bouton à un backend qui l'a refusé.
        // Une compaction en cours reste affichée jusqu'à la reconnexion : son
        // issue n'est pas connue avant.
        conn.socketCompactContext = 'unknown';
        conn.supportsCanvasOperationProgress = false;
        conn.supportsCanvasOperationProbe = false;
        // A progress frame from the former socket cannot safely continue over a
        // new connection: its backend fence is owned by the old connectionId.
        conn.canvasOperationProgress.cancelAll();

        if (isExchangeActive(conn.status)) {
          this.log(sessionKey, conn.conversationId, 'INTERRUPTED', `reason=${reason ?? 'unknown'}`);
          // Ceinture ET bretelles : même réordonné, ce bloc reste isolé. Un indicateur
          // de déconnexion qui échoue est un défaut cosmétique, jamais une raison de
          // perdre la suite du tour.
          try {
            const timeline = this.getTimeline(conn);
            const updated = conn.reducer.applyDisconnected(timeline);
            this.updateTimeline(conn, updated);
          } catch (err) {
            ErrorService.handle(err, {
              context: { action: 'ws_pool_apply_disconnected', sessionKey },
              silent: true,
            });
          }
        }
      }

      if (connected) {
        // Reconnected — remove disconnected indicators
        const timeline = this.getTimeline(conn);
        const cleaned = conn.reducer.removeDisconnected(timeline);
        if (cleaned.length !== timeline.length) {
          this.updateTimeline(conn, cleaned);
        }

        // `session_resume` N'EST PLUS ÉMIS ICI (incident 2026-09-11) : ce callback part
        // de `ws.onopen`, c'est-à-dire de la poignée de main TCP/HTTP, avant que le
        // backend ait fini d'authentifier la socket et d'attacher son `ws.on('message')`
        // (`WebSocketHandler.setupHandlers` : trois `await` — Supabase, puis deux
        // requêtes OVH — entre le 101 et le listener). Une trame émise dans cette
        // fenêtre est parsée sans auditeur et disparaît sans erreur ni log.
        // L'émission vit désormais dans le handler `connection_established` : l'ACK du
        // backend est la seule preuve que quelqu'un écoute.

        // LE TOUR EN ATTENTE N'EST PLUS RENVOYÉ ICI (incident 2026-09-11, revue) :
        // pour la même raison que `session_resume` ci-dessus, et avec une
        // conséquence pire. Ce callback part de `ws.onopen`, avant que le backend
        // ait attaché `ws.on('message')` ; une trame émise là est jetée sans trace.
        // Or ce bloc VIDAIT `conn.pendingMessage` avant d'envoyer, puis
        // `sendMessageDirect` passait la session en `streaming` — un envoi sur
        // socket ouverte « réussit » toujours côté navigateur, il n'existe aucun
        // accusé. Le message tapé par l'avocat était donc perdu définitivement,
        // affiché comme parti, sous une plume qui ne s'arrêtait jamais.
        // Le handler `connection_established` porte le même bloc, à l'instant où
        // le backend a prouvé qu'il écoute : c'est déjà le chemin de toute
        // première connexion, les reconnexions le partagent désormais. Et si
        // l'accusé n'arrive jamais, le tour reste en attente au lieu d'être vidé.
        // NOTE: if status is streaming with no pending message, the exchange
        // was lost. We do NOT transition here — we wait for connection_established
        // to confirm auth before recovering. See the connection_established handler.
      }
    });

    client.setOnAuthFailure(async () => {
      this.log(sessionKey, conn.conversationId, 'AUTH_FAILURE', 'refreshing token');
      const newToken = await this.tokenManager.refresh();
      if (newToken) {
        conn.reconnector.resetExhausted();
        conn.reconnector.attempt();
      }
    });
  }

  // ==========================================================================
  // Private — message sending
  // ==========================================================================

  /**
   * Send a user_message directly on a connected session.
   *
   * WHY no user_id/cabinet_id: INV-2 — identity comes from JWT at WS auth,
   * not from the message payload which clients can forge.
   */
  private sendMessageDirect(
    conn: PoolConnection,
    content: string,
    toolsConfig: ToolsConfig,
    options: SendMessageOptions,
  ): void {
    conn.currentLocalTurnId = options.localTurnId ?? conn.currentLocalTurnId;
    const requestedCaseId = options.caseId === undefined ? conn.caseId : options.caseId;
    const effectiveCaseId = this.resolveFrameCaseId(conn, requestedCaseId);
    conn.caseId = effectiveCaseId;

    // LOT A — l'état du canvas voyage avec CHAQUE message, et pas seulement
    // quand il change : cet état n'existe nulle part ailleurs dans le contexte
    // du modèle, donc un delta le laisserait aveugle sur tout tour où le canvas
    // était déjà ouvert avant le premier message — l'incident du 12/08/2026.
    // Lu ICI, à l'envoi : le canvas peut avoir été ouvert ou masqué entre le
    // rendu du composeur et le clic.
    //
    // L'expéditeur est passé parce que la question n'est PAS « un canvas est-il
    // à l'écran ? » mais « CETTE conversation détient-elle le canvas ? »
    // (2026-08-13) : depuis que le canvas ne suit plus l'écran, une conversation
    // qui n'en est pas porteuse n'a rien à déclarer — le document est celui d'un
    // autre fil, et l'y faire écrire briserait le routage par session exacte.
    // Plus aucune adoption ici (#1331) : un Canvas ne s'ouvre que dans un fil
    // dont le serveur a confirmé l'existence, donc il naît avec son
    // propriétaire. L'adoption au premier message rattrapait un document
    // orphelin que ce chantier a rendu impossible.
    const canvasState = lireEtatCanvasPourAgent(conn.conversationId);

    const message: UserMessage = {
      type: 'user_message',
      conversation_id: conn.conversationId,
      // Comme Word et le mail : `case_id` demande de remplacer le principal. Renvoyé
      // à chaque message, il rétablirait un principal retiré depuis une autre
      // surface. Un hôte verrouillé, lui, l'impose toujours.
      case_id: options.caseIsAlreadyPrincipal && !conn.lockedCaseId ? undefined : effectiveCaseId ?? undefined,
      mode: 'chat',
      content,
      tools_config: toolsConfig,
      ui_action_capabilities: [...options.uiActionCapabilities],
      // Omis quand le canvas est fermé : le silence vaut « fermé ».
      ...(canvasState ? { canvas_state: canvasState } : {}),
      // Omis sans geste : un champ vide ne dirait rien de plus au serveur.
      ...(options.caseChanges?.length ? { case_changes: options.caseChanges } : {}),
      ...(options.attachments?.length ? { attached_documents: options.attachments } : {}),
      ...(options.modelPreference?.model ? {
        model_override: options.modelPreference.model,
        ...(options.modelPreference.reasoningEffort ? {
          reasoning_effort: options.modelPreference.reasoningEffort,
        } : {}),
      } : {}),
    };

    conn.client.send(message);
    this.setStatus(conn, 'streaming');
    // Un message de chat ne réserve plus le document. L'acquisition se fait au
    // premier signal documentaire (`redaction_job_started` ou tool canvas),
    // sinon une simple question dans un autre fil rerouterait le canvas avant
    // même que l'agent ait demandé à y écrire (#1044).
    this.log(conn.sessionKey, conn.conversationId, 'MSG_SENT', `content=${content.slice(0, 50)}...`);
  }

  /**
   * Dossier demandé pour une trame, avant l'ancre `locked`.
   *
   * `undefined` preserves legacy callers that omitted this per-turn argument;
   * explicit `null` remains a real global-assistant choice. A host with a
   * selectable-but-required dossier instead falls back to its last
   * human-selected target: an unscoped frame would violate that host's context
   * contract before the locked-case guard can apply.
   */
  private resolveRequestedCaseId(conn: PoolConnection, caseId: string | null | undefined): string | null {
    const requestedCaseId = caseId === undefined ? conn.caseId : caseId;
    return conn.caseContextMode === 'host_selectable' && !requestedCaseId
      ? conn.caseId
      : requestedCaseId;
  }

  /**
   * `case_id` effectif d'une trame qui reconstruit la conversation côté backend
   * — `user_message` comme `compact_context`. Une seule source : si les deux
   * divergeaient, la compaction recompterait une conversation (outils, prompt
   * système) différente de celle que le tour suivant enverra.
   *
   * A locked host owns the dossier context. Every other mode receives a per-turn
   * snapshot from the composer: a session can start on A and later send B.
   */
  private resolveFrameCaseId(conn: PoolConnection, requestedCaseId: string | null): string | null {
    return conn.lockedCaseId ?? requestedCaseId;
  }

  /** Motif de refus d'une compaction manuelle, ou `null` si le pool peut la prendre en charge. */
  private describeCompactContextRefusal(conn: PoolConnection): string | null {
    if (!conn.conversationId) return 'conversation inconnue de la socket';
    if (conn.pendingMessage) return 'un tour attend son envoi — le backend répondrait conversation_busy';
    if (isExchangeActive(conn.status)) return `tour en cours (status=${conn.status}) — le backend tient le verrou de conversation`;
    if (conn.contextCompaction.phase !== 'idle') return `une compaction n'est pas soldée (phase=${conn.contextCompaction.phase})`;
    return null;
  }

  /** Envoie la trame et arme le garde-fou ; seul chemin d'émission de `compact_context`. */
  private sendCompactContextFrame(conn: PoolConnection, toolsConfig: ToolsConfig, caseId: string | null): void {
    const wasIdle = conn.contextCompaction.phase === 'idle';
    const frameCaseId = this.resolveFrameCaseId(conn, caseId);
    conn.client.send({
      type: 'compact_context',
      conversation_id: conn.conversationId,
      case_id: frameCaseId ?? undefined,
      tools_config: toolsConfig,
    });
    conn.contextCompaction = {
      phase: 'awaiting_verdict',
      timeout: setTimeout(() => this.expireContextCompaction(conn), COMPACTION_CLIENT_TIMEOUT_MS),
    };
    // Une demande sortie de l'attente d'ACK est déjà `running` : ne pas republier.
    if (wasIdle) this.publishContextCompaction(conn, { status: 'running' });
    this.log(conn.sessionKey, conn.conversationId, 'COMPACT_CONTEXT_SENT', `caseId=${frameCaseId ?? 'none'}`);
  }

  /**
   * Garde-fou : la socket est restée ouverte mais aucun verdict n'est arrivé
   * dans le budget backend (cf. `COMPACTION_CLIENT_TIMEOUT_MS`). Le backend a pu
   * aboutir sans que la trame arrive : la base tranche.
   */
  private expireContextCompaction(conn: PoolConnection): void {
    if (conn.contextCompaction.phase !== 'awaiting_verdict') return;
    this.log(
      conn.sessionKey,
      conn.conversationId,
      'COMPACT_CONTEXT_TIMEOUT',
      `aucun verdict après ${COMPACTION_CLIENT_TIMEOUT_MS} ms — retour au repos et relecture de l'historique`,
    );
    this.settleContextCompaction(conn, { status: 'error', userMessage: COMPACTION_TIMEOUT_MESSAGE }, { historyStale: true });
  }

  /**
   * Solde la compaction : minuteur rendu, retour au repos, puis publication du
   * terminal (`null` = retour silencieux), de la relecture d'historique, et de `idle`.
   */
  private settleContextCompaction(
    conn: PoolConnection,
    terminal: ContextCompactionState | null,
    options: { historyStale: boolean },
  ): void {
    this.clearContextCompactionTimer(conn);
    conn.contextCompaction = { phase: 'idle' };
    if (terminal) this.publishContextCompaction(conn, terminal);
    // Les messages archivés et le marqueur de compaction n'existent qu'en base :
    // aucun tour local ne peut les montrer (la timeline live n'est rendue que
    // pendant un tour actif).
    if (options.historyStale) this.events.emit('conversation_history_stale', conn.sessionKey, conn.conversationId);
    this.publishContextCompaction(conn, IDLE_CONTEXT_COMPACTION);
  }

  private clearContextCompactionTimer(conn: PoolConnection): void {
    if (conn.contextCompaction.phase === 'awaiting_verdict') clearTimeout(conn.contextCompaction.timeout);
  }

  /** N'émet qu'aux changements de la valeur publiée ; la réponse de la socket courante est toujours retenue. */
  private setCompactContextCapability(conn: PoolConnection, capability: CompactContextCapability): void {
    conn.socketCompactContext = capability;
    if (conn.announcedCompactContext === capability) return;
    conn.announcedCompactContext = capability;
    this.events.emit('compact_context_capability_changed', conn.sessionKey, capability);
  }

  private publishContextCompaction(conn: PoolConnection, state: ContextCompactionState): void {
    this.events.emit('context_compaction_changed', conn.sessionKey, conn.conversationId, state);
  }

  /**
   * La socket qui portait la compaction est perdue.
   *
   * - Trame déjà partie : son verdict ne peut plus arriver. Le backend a pu
   *   aboutir, donc retour au repos ET relecture de l'historique.
   * - Demande encore en attente d'ACK : rien n'est parti, l'avocat doit savoir
   *   que sa demande n'a pas eu lieu.
   */
  private abandonContextCompactionOnLostSocket(conn: PoolConnection, cause: string): void {
    const { phase } = conn.contextCompaction;
    if (phase === 'idle') return;
    this.log(conn.sessionKey, conn.conversationId, 'COMPACT_CONTEXT_ABANDONED', `${cause} (phase=${phase})`);
    if (phase === 'awaiting_verdict') {
      this.settleContextCompaction(conn, null, { historyStale: true });
      return;
    }
    this.settleContextCompaction(
      conn,
      { status: 'error', userMessage: COMPACTION_CONNECTION_FAILED_MESSAGE },
      { historyStale: false },
    );
  }

  private emitTerminalSnapshot(
    conn: PoolConnection,
    reason: TerminalTurnReason,
    timeline: TimelineItem[],
    lastAssistantMessageId?: string,
    citationRegistry?: CitationRegistry,
  ): void {
    const localTurnId = conn.currentLocalTurnId;
    if (!localTurnId) {
      this.log(
        conn.sessionKey,
        conn.conversationId,
        'SNAPSHOT_SKIPPED',
        `reason=${reason} missing currentLocalTurnId`,
      );
      return;
    }

    this.events.emit('session_done', {
      sessionKey: conn.sessionKey,
      conversationId: conn.conversationId,
      localTurnId,
      reason,
      timeline: [...timeline],
      lastAssistantMessageId,
      citationRegistry,
      resumedOnLoad: conn.resumedOnLoadTurnId === localTurnId,
    });
    conn.currentLocalTurnId = null;
    conn.resumedOnLoadTurnId = null;
    conn.catchup = null;
  }

  // ==========================================================================
  // Private — reconnection
  // ==========================================================================

  /**
   * Single reconnection attempt: get a fresh token, create a new client,
   * re-wire handlers, and try to connect.
   *
   * @returns `true` if the connection was established.
   */
  private async attemptReconnect(sessionKey: string): Promise<boolean> {
    const conn = this.connections.get(sessionKey);
    if (!conn) return false;

    try {
      const token = await this.tokenManager.getToken();
      if (!token) return false;

      // Detach callbacks on old client to prevent races
      conn.client.setOnConnectionChange(null);
      conn.client.setOnAuthFailure(null);
      conn.client.disconnect();

      // Create fresh client and handler
      const newClient = new WebSocketClient(WS_AGENT_URL, token);
      const newHandler = new MessageHandler();
      conn.client = newClient;
      conn.handler = newHandler;

      // Re-wire handlers on the new client
      this.wireHandlers(conn);

      await newClient.connect();

      // Small grace period to confirm the connection stays up
      await new Promise(resolve => setTimeout(resolve, 500));
      return newClient.isConnected();
    } catch (err) {
      ErrorService.handle(err, {
        context: { action: 'ws_pool_reconnect', sessionKey },
        silent: true,
      });
      return false;
    }
  }

  // ==========================================================================
  // Private — slot management
  // ==========================================================================

  /**
   * Reclaim a slot to free capacity for a new session. Only `ready` slots are
   * safe to drop; `connecting` slots can still carry a queued first prompt, and
   * a `ready` slot with an unsettled manual compaction still has work in flight.
   * See {@link pickSlotToEvict} for the rationale.
   * @returns `true` if a session was evicted.
   */
  private evictReclaimableSlot(): boolean {
    const key = pickSlotToEvict(
      [...this.connections.values()].map(conn => ({
        sessionKey: conn.sessionKey,
        status: conn.status,
        createdAt: conn.createdAt,
        hasContextCompactionInFlight: conn.contextCompaction.phase !== 'idle',
      })),
    );
    if (!key) return false;

    const conn = this.connections.get(key);
    if (!conn) return false;
    this.log(key, conn.conversationId, 'EVICT', `status=${conn.status}`);
    this.cleanup(key);
    return true;
  }

  // ==========================================================================
  // Private — helpers
  // ==========================================================================

  /** Get the current timeline from the cache. */
  private getTimeline(conn: PoolConnection): TimelineItem[] {
    return this.timelineCache.get(conn.sessionKey) ?? [];
  }

  /** Update the timeline cache and emit the change. */
  private updateTimeline(conn: PoolConnection, timeline: TimelineItem[]): void {
    this.timelineCache.set(conn.sessionKey, timeline);
    this.events.emit('timeline_updated', conn.sessionKey, timeline);
  }

  /**
   * Transition to streaming status if not already there.
   * Called on first delta/tool event.
   */
  private ensureStreaming(conn: PoolConnection): void {
    if (conn.status !== 'streaming') {
      this.setStatus(conn, 'streaming');
    }
  }

  /** Update session status and emit change. */
  private setStatus(conn: PoolConnection, next: SessionStatus): void {
    if (conn.status === next) return;
    conn.status = next;
    this.events.emit('status_changed', conn.sessionKey, next);
  }

  /** A null local id is still unique within one WebSocket connection. */
  private documentTurnPinKey(turnId: string | null): string {
    return turnId ?? '__turn_without_local_id__';
  }

  /**
   * Remplace le document d'un résident DÉJÀ MONTÉ, et rend ce que l'écran montre
   * au bout : B prêt, A restauré, ou A perdu (#1338, D4).
   *
   * POURQUOI CE CHEMIN EXISTE (#1345, mesuré le 2026-09-08). Ouvrir une pièce
   * dans un canvas déjà ouvert — le cas courant, l'avocat travaille puis
   * demande « ouvre-moi la pièce n°4 » — accusait un succès sans rien
   * remplacer : `requestResident` coalesce sur le résident chaud et ignore la
   * graine. Changer l'identité documentaire d'un éditeur vivant n'a qu'un seul
   * contrat, `replaceDocument`, et c'est déjà celui du geste humain « Ouvrir
   * dans le canvas » (`OuvrirDansLeCanvasAction`) : le chemin agent l'emprunte
   * désormais aussi, plutôt que d'en avoir un second, plus faible.
   *
   * POURQUOI IL CAPTURE A AVANT DE LE DÉTRUIRE (#1338, D4). Le commit détruit
   * l'éditeur de A. Si B ne devient pas prêt — archive illisible, délai
   * dépassé, tour terminé pendant l'attente —, rien ne remettait A à l'écran :
   * l'état absorbant que le correctif de production #1354 fermait. A est donc
   * exporté AVANT le commit, puis remonté sous une époque neuve si B échoue.
   *
   * ⚠️ **Toujours un export, jamais la graine d'ouverture** (décision D-D, lue
   * sur le code). D-D voulait la graine logique quand
   * `peutReprendreLaSourceInitiale` est vrai ; sur CE chemin il ne l'est jamais :
   * un résident n'est chaud qu'après avoir été prêt (`markMountHot`), et
   * `handleScopeSnapshot` interdit à son contrôleur toute reprise de la source
   * dès qu'un montage est prêt ou expose un éditeur — sans jamais lever
   * l'interdiction. Une branche « graine » serait du code sans entrée.
   *
   * ⚠️ **Avant le commit, un refus LÈVE ; après, plus rien ne lève.** Avant, A
   * est intact et le message le dit. Après, chaque issue a sa trame corrélée
   * (piège 4 de la carte, #1396).
   */
  private async replaceResidentCanvasDocument(input: {
    conn: PoolConnection;
    /**
     * ⚠️ **Nullable, et c'est un invariant, pas une commodité.** Un brouillon
     * rouvert sans pièce source n'a AUCUN uuid de la table `documents`
     * (`CanvasTemplateMeta.documentId`) : y poser le `draftId` le ferait rejouer
     * par l'agent dans `load_document_into_canvas`, qui chercherait une pièce
     * inexistante. `waitForReady` accepte déjà `string | null` — l'attente porte
     * alors sur `(conversation, époque)` seules, ce qui suffit : l'époque est
     * frappée par CE remplacement.
     */
    documentId: string | null;
    blob: Blob;
    meta: CanvasTemplateMeta;
    /** Le résident est celui que l'avocat regarde, ou personne ne regarde. */
    reveleALAvocat: boolean;
    /**
     * Sous quelle identité le document posé s'enregistrera.
     *
     * Posée juste après le commit, avant que B ne soit prêt
     * (`poserLIdentiteDuDocument`). `nouveau-document` : pièce chargée, gabarit,
     * brouillon copié d'un autre fil — sans détachement, le contenu posé
     * s'enregistrait sous l'identifiant du document que l'avocat avait ouvert,
     * qui était ÉCRASÉ. `brouillon-du-fil` : l'identité du brouillon est
     * adoptée au même instant, et non plus après `ready` (revue du 2026-09-23).
     *
     * Requis, sans défaut : un nouvel appelant qui l'oublierait rouvrirait
     * l'écrasement.
     */
    identiteDuDocumentPose: IdentiteDuDocumentPose;
    /** Le signal de l'opération de chargement : fin de tour, socket morte (D9). */
    signal: AbortSignal;
  }): Promise<IssueDuRemplacementChaud> {
    const { conn } = input;
    const conversationId = conn.conversationId;
    // CAPTURER D'ABORD, SÉLECTIONNER ENSUITE — l'ordre inverse n'a pas de retour
    // arrière. `captureCanvasResidentReplacementTarget` peut LEVER
    // (`requireExactScope` : scope disparu, ou propriétaire changé sous nous) ;
    // sélectionné en premier, l'écran aurait alors basculé sur une session que
    // rien ne remplace, et l'avocat regarderait un document qu'il n'a pas
    // demandé pendant qu'un refus part à l'agent. Capturer d'abord se paie de
    // rien : l'identité est relue et revalidée par le runtime au commit.
    const target = captureCanvasResidentReplacementTarget({ conversationId });
    const reprise = await this.capturerAPourLaReprise(conn, target);
    if (input.signal.aborted) {
      throw new Error(
        `the load was cancelled before the replacement (${String(input.signal.reason)}); nothing was `
        + 'replaced and the current document stays on screen.',
      );
    }
    // LA SÉLECTION PRÉCÈDE LE COMMIT. `replaceCanvasResidentTemplate` ne publie
    // la projection React — donc le titre du bandeau — que si la cible est le
    // résident visible AU MOMENT du commit. Sélectionner après laisserait le
    // document peint sous l'ancien nom.
    if (input.reveleALAvocat) canvasDocumentRuntime.selectVisibleSession(conversationId);

    // L'ÉPINGLE DU TOUR N'EST JAMAIS RENDUE : ELLE EST PRÉSENTÉE (#1354/D10).
    // Elle protège cette session contre l'éviction, et `replaceDocument` refuse
    // toute session protégée — mais le tour qui la détient est celui-là même qui
    // demande l'échange. `tryBeginExclusiveReplacement` reconnaît sa poignée
    // AUTHENTIQUE (`runtimePin`, frappée par le runtime) et l'ignore pour
    // l'admission seulement ; le bail rendu autorise `replaceDocument` à passer.
    //
    // ⚠️ L'ancien câblage RENDAIT l'épingle le temps du commit puis la reprenait.
    // Correct seul, il est devenu un défaut en rencontrant #1354 : depuis D9,
    // `releaseDocumentTurnPin` ANNULE le chargement en cours du même tour — donc
    // le chargement qui demandait ce remplacement s'annulait lui-même
    // (`Canvas load requestId=… was cancelled`), et la pièce n'était jamais
    // posée dans un canvas déjà ouvert. Aucun des deux côtés n'avait le défaut ;
    // le rapatriement de pre-prod (`0ab6e2453`) l'a créé en les réunissant.
    // Présenter l'épingle au lieu de la rendre supprime aussi la fenêtre où la
    // session courait sans protection contre l'éviction LRU.
    const turnPin = conn.documentTurnPins.get(this.documentTurnPinKey(conn.currentLocalTurnId));
    if (!turnPin) {
      throw new Error(
        `the document replacement for documentId=${input.documentId ?? 'none'} on conversationId=${conversationId} `
        + `found no live turn pin for turn=${conn.currentLocalTurnId ?? 'none'}. The canvas protects a `
        + 'document only for the turn that pinned it; no document was replaced. Retry from a turn that '
        + 'owns this canvas.',
      );
    }
    const lease = canvasDocumentRuntime.tryBeginExclusiveReplacement(conversationId, turnPin.runtimePin);
    if (!lease) {
      throw new Error(
        `the document replacement for documentId=${input.documentId ?? 'none'} is blocked on `
        + `conversationId=${conversationId}: another documentary job, a lawyer gesture or a second pin `
        + 'holds this document session (the requesting turn may be its only protection). The current '
        + 'document was not replaced; retry once that work is released.',
      );
    }
    let replacement: CanvasRuntimeDocumentReplacementDecision;
    try {
      replacement = replaceCanvasResidentTemplate({
        target,
        blob: input.blob,
        meta: input.meta,
        replacementLease: lease,
      });
    } finally {
      // Le bail ne couvre que le commit SYNCHRONE : il bloque toute autre
      // mutation et toute nouvelle épingle d'agent tant qu'il vit. L'épingle du
      // tour, elle, reste posée et continue de protéger le remontage pendant
      // `waitForReady`.
      lease.release();
    }
    if (!replacement.accepted) {
      throw new Error(
        `the mounted editor refused the atomic replacement (reason=${replacement.reason}) for `
        + `conversationId=${conversationId}. Another documentary job or a lawyer gesture holds `
        + 'this document session; retry once it is released.',
      );
    }
    // JUSTE APRÈS LE COMMIT, AVANT QUE B NE SOIT PRÊT. Au signal `ready`, le
    // contrôleur arme le checkpoint initial de la source sous l'identité qu'il
    // porte à cet instant : détacher après l'attente arriverait trop tard, B
    // partirait sous l'identifiant de A. Et si B ne monte pas, la reprise
    // restaure l'état du contrôleur capturé avant le commit — l'identité de A
    // revient avec A (`restoreRecoveryState`).
    const { adopte: identiteAdoptee } = poserLIdentiteDuDocument(conversationId, input.identiteDuDocumentPose);

    // PAS DE `touch()` ICI, ET C'EST VÉRIFIÉ. L'ancien chemin `already-hot`
    // rafraîchissait le rang LRU (`canvasResidenceCapacity.ts`, `port.touch`)
    // parce qu'il rendait la main aussitôt, sans rien remonter. Ce chemin-ci
    // remonte, et le rafraîchissement arrive de lui-même au bout : quand le
    // nouveau montage devient prêt, `handleScopeSnapshot` appelle
    // `markMountHot`, qui termine par `touchSession`
    // (`canvasSessionRegistry.ts`). Entre les deux, la session porte
    // l'épingle de tour reprise ci-dessus, et `hasEligibleResidence` exclut du
    // choix de victime toute session protégée : le rang LRU ne peut donc pas
    // décider de son sort pendant la fenêtre. Un `touch()` de plus n'ajouterait
    // qu'un appel qu'un lecteur futur croirait nécessaire.
    //
    // Le montage attendu est EXACTEMENT celui que ce remplacement a alloué (D3,
    // #1337), portant la pièce DEMANDÉE : ni l'ancien éditeur encore prêt —
    // l'accusé repartirait avec sa révision —, ni un remontage postérieur, dont
    // personne n'a vérifié le contenu. Le bail est déjà rendu : le tenir pendant
    // cette attente bloquerait toute épingle et toute activité du fil pendant
    // 14 s (piège 2 de la carte, décision D-E).
    const epoqueDeB = replacement.session.mountEpoch;
    let cause: string;
    try {
      const session = await attendreSaufAnnulation(
        canvasDocumentRuntime.waitForReady({
          conversationId,
          mountEpoch: epoqueDeB,
          documentId: input.documentId,
        }, CANVAS_DOCUMENT_REPLACEMENT_TIMEOUT_MS),
        input.signal,
        () => `the load was cancelled while documentId=${input.documentId ?? 'none'} was mounting at `
          + `mountEpoch=${epoqueDeB} (${String(input.signal.reason)}).`,
      );
      return { issue: 'b-pret', session, identiteAdoptee };
    } catch (erreur: unknown) {
      cause = erreur instanceof Error ? erreur.message : String(erreur);
    }
    return this.restaurerAApresUnEchecDeB({ conn, turnPin, reprise, epoqueDeB, cause });
  }

  /**
   * La copie de A que D4 restaurera si B ne monte pas (#1338).
   *
   * A PRÊT : exporté, et un export refusé LÈVE avant le commit. Remplacer un
   * document dont on n'a pas pu garder la copie, ce serait risquer l'état
   * absorbant sur un document que l'avocat a peut-être modifié ; le refus, lui,
   * laisse A intact. A PAS PRÊT (en chargement, en échec) : il n'y a aucun
   * éditeur utilisable à perdre — refuser empêcherait au contraire de réparer
   * un canvas en échec. Le chargement continue sans copie, et un échec de B le
   * dira sans prétendre restaurer.
   *
   * La durée de l'export est journalisée (`CANVAS_RECOVERY_CAPTURED`) : c'est
   * elle qui borne la restauration (`CANVAS_DOCUMENT_RESTORATION_TIMEOUT_MS`).
   */
  private async capturerAPourLaReprise(
    conn: PoolConnection,
    target: CanvasResidentReplacementTarget,
  ): Promise<RepriseDeA> {
    const avant = canvasDocumentRuntime.getSessionForConversation(conn.conversationId);
    if (avant?.state !== 'ready') {
      return {
        copie: null,
        sansCopie: `no copy of it was kept because it was not ready when the load started `
          + `(state=${avant?.state ?? 'absent'} mountEpoch=${avant?.mountEpoch ?? 'none'}).`,
      };
    }
    const debut = performance.now();
    const copie = await captureCanvasResidentRecovery(target);
    this.log(
      conn.sessionKey,
      conn.conversationId,
      'CANVAS_RECOVERY_CAPTURED',
      `mountEpoch=${copie.identity.mountEpoch} exportMs=${Math.round(performance.now() - debut)} `
      + `bytes=${copie.presentation.templateOverride?.size ?? 0}`,
    );
    return { copie, sansCopie: null };
  }

  /**
   * Remet A à l'écran après un B qui n'est pas devenu prêt (#1338, D4, D-E).
   * Ne lève JAMAIS : chacune de ses issues devient une trame corrélée.
   *
   * L'ÉPINGLE DU TOUR, SI ELLE VIT ENCORE, EST PRÉSENTÉE (D10, D-H) : le tour qui
   * a demandé B demande aussi le retour de A. Sur une ANNULATION — fin de tour,
   * socket morte —, `releaseDocumentTurnPin` a d'abord fait avorter ce
   * chargement PUIS rendu l'épingle, qui a quitté `livePins` : la présenter
   * rendrait `null` (piège 5). La restauration passe alors SANS exemption. Elle
   * n'a lieu que si aucun autre travail ne tient la session ; sinon le refus
   * `session-protected` est rendu tel quel (D-E). Même règle quand l'épingle vit
   * mais qu'un second travail s'est épinglé pendant l'attente : `pinCount !== 1`
   * refuse le bail (D-H), et aucune exemption n'est inventée.
   *
   * Le bail ne couvre que le commit synchrone, jamais l'attente de A′.
   */
  private async restaurerAApresUnEchecDeB(input: {
    conn: PoolConnection;
    turnPin: CanvasDocumentTurnPin;
    reprise: RepriseDeA;
    epoqueDeB: number;
    cause: string;
  }): Promise<IssueDuRemplacementChaud> {
    const { conn, epoqueDeB, cause } = input;
    const conversationId = conn.conversationId;
    const perdu = (raison: string): IssueDuRemplacementChaud => ({ issue: 'a-perdu', cause, epoqueDeB, raison });
    const copie = input.reprise.copie;
    if (!copie) return perdu(input.reprise.sansCopie ?? 'no copy of it was kept.');
    const epingleVivante = conn.documentTurnPins.get(this.documentTurnPinKey(input.turnPin.turnId)) === input.turnPin;
    const bail = epingleVivante
      ? canvasDocumentRuntime.tryBeginExclusiveReplacement(conversationId, input.turnPin.runtimePin)
      : null;
    let restauration: CanvasRuntimeDocumentReplacementDecision;
    try {
      restauration = restoreCanvasResidentAfterFailedReplacement({
        recovery: copie,
        failedCandidateMountEpoch: epoqueDeB,
        replacementLease: bail ?? undefined,
      });
    } catch (erreur: unknown) {
      return perdu(`its restoration threw: ${erreur instanceof Error ? erreur.message : String(erreur)}`);
    } finally {
      bail?.release();
    }
    if (!restauration.accepted) {
      const tenue = restauration.reason === 'session-protected'
        ? ': another documentary job or a lawyer gesture holds this document session'
        : '';
      return perdu(
        `the runtime refused to remount it (reason=${restauration.reason}${tenue}); the canvas keeps the `
        + `candidate mountEpoch=${epoqueDeB}, which is not ready.`,
      );
    }
    const epoqueDeAPrime = restauration.session.mountEpoch;
    try {
      const restaure = await canvasDocumentRuntime.waitForReady({
        conversationId,
        mountEpoch: epoqueDeAPrime,
        documentId: copie.documentId,
      }, CANVAS_DOCUMENT_RESTORATION_TIMEOUT_MS);
      return { issue: 'a-restaure', cause, epoqueDeB, restaure };
    } catch (erreur: unknown) {
      return perdu(
        `it was remounted at mountEpoch=${epoqueDeAPrime} but never became ready: `
        + (erreur instanceof Error ? erreur.message : String(erreur)),
      );
    }
  }

  /**
   * Fait ENTRER le document demandé dans une session qui n'a pas d'éditeur.
   *
   * Jumelle froide de `replaceResidentCanvasDocument` : même entrée, même
   * sortie, deux mécaniques qui n'ont rien à voir. Ici le coordinateur de
   * résidence monte lui-même la graine, arbitre la capacité et publie sa
   * session prête — il n'y a ni épingle à rendre, ni montage antérieur à
   * dépasser.
   */
  private async mountColdCanvasDocument(input: {
    conn: PoolConnection;
    /** `null` est une provenance légitime pour un modèle ou une pièce jointe. */
    documentId: string | null;
    documentTitle: string;
    blob: Blob;
    meta: CanvasTemplateMeta;
    /** Le résident est celui que l'avocat regarde, ou personne ne regarde. */
    reveleALAvocat: boolean;
    /** Fin de tour, socket morte ou échéance : la résidence s'abandonne. */
    signal: AbortSignal;
    /** Ce qui reste de l'échéance du gestionnaire (`CanvasResidentRequest.hydrationBudgetMs`). */
    hydrationBudgetMs: () => number;
  }): Promise<CanvasRuntimeSession> {
    const { conn } = input;
    // A resident owns both its editor and its presentation. Passing the
    // downloaded DOCX as the seed mounts B behind the parking boundary
    // when A is visible; writing the visible editor store here would load
    // B's Word into A.
    const residence = await canvasDocumentRuntime.requestResident({
      conversationId: conn.conversationId,
      seed: {
        documentId: input.documentId,
        presentation: {
          templateOverride: input.blob,
          templateMeta: input.meta,
          documentTitle: input.documentTitle,
          activeDraft: null,
          draftNeedsHydration: false,
        },
      },
      visibility: input.reveleALAvocat ? 'select' : 'preserve',
      signal: input.signal,
      hydrationBudgetMs: input.hydrationBudgetMs,
    });
    // Le coordinateur ne rend que des scopes prêts, mais son type le dit d'un
    // cran trop large (`disposed` y est possible). L'écarter ici plutôt que
    // l'affirmer par un `as` : un scope disposé n'a plus d'éditeur, donc plus de
    // révision, et l'accusé partirait sur un document que personne ne peut lire.
    const { state, ...reste } = residence.session;
    if (state === 'disposed') {
      throw new Error(
        `the canvas runtime disposed conversationId=${reste.conversationId} mountEpoch=${reste.mountEpoch} `
        + `while mounting documentId=${input.documentId ?? 'none'}; nothing is loaded. `
        + 'Re-open the canvas, then retry the load.',
      );
    }
    return { ...reste, state };
  }

  /**
   * Protège le document de CETTE conversation pour la durée du tour.
   *
   * ⚠️ **Le second paramètre a disparu, et la branche de conflit avec lui**
   * (#1336). Elle refusait un tour « déjà épinglé sur une AUTRE session
   * documentaire » : depuis que la conversation EST la clé du document logique,
   * une connexion — dont la conversation est fixée à la création du slot — ne
   * peut plus en désigner deux dans le même tour. Comparer les deux rendait la
   * même valeur avec le défaut et sans lui.
   *
   * ⚠️ **Et le refus RESTE, sous une autre cause** : `pinCanvasDocumentTurn`
   * lève quand le runtime refuse l'épingle (remplacement manuel exclusif en
   * cours, session logique absente). Cette issue est rendue, jamais jetée : le
   * handler `redaction_job_started` est SYNCHRONE et hors `try`, un jet y
   * casserait le `forEach` de dispatch et ferait sauter les autres écouteurs de
   * la même trame. La cause voyage avec le refus parce que les quatre appelants
   * la relaient à l'agent, qui n'a pas d'autre trace.
   */
  private ensureDocumentTurnPinned(conn: PoolConnection): { ok: true } | { ok: false; cause: string } {
    const key = this.documentTurnPinKey(conn.currentLocalTurnId);
    if (conn.documentTurnPins.has(key)) {
      return { ok: true };
    }

    let pin: CanvasDocumentTurnPin;
    try {
      pin = pinCanvasDocumentTurn({
        conversationId: conn.conversationId,
        turnId: conn.currentLocalTurnId,
        tokenId: `${conn.sessionKey}:${key}`,
      });
    } catch (error) {
      const cause = error instanceof Error ? error.message : String(error);
      this.log(
        conn.sessionKey,
        conn.conversationId,
        'CANVAS_TURN_PIN_REFUSED',
        `turn=${conn.currentLocalTurnId ?? 'none'} — ${cause}`,
      );
      return { ok: false, cause };
    }
    conn.documentTurnPins.set(key, pin);
    this.log(
      conn.sessionKey,
      conn.conversationId,
      'CANVAS_TURN_PINNED',
      `turn=${conn.currentLocalTurnId ?? 'none'}`,
    );
    return { ok: true };
  }

  /** Releases only the handle captured for the terminal turn; idempotent. */
  private releaseDocumentTurnPin(conn: PoolConnection, turnId: string | null, reason: string): void {
    if (conn.canvasDocumentLoad?.turnId === turnId) conn.canvasDocumentLoad.controller.abort(reason);
    const key = this.documentTurnPinKey(turnId);
    const pin = conn.documentTurnPins.get(key);
    if (!pin) return;
    pin.release();
    conn.documentTurnPins.delete(key);
    this.log(
      conn.sessionKey,
      conn.conversationId,
      'CANVAS_TURN_RELEASED',
      `turn=${turnId ?? 'none'} conversationId=${pin.conversationId} reason=${reason}`,
    );
  }

  /** Connection teardown owns every remaining pin on that connection only. */
  private releaseAllDocumentTurnPins(conn: PoolConnection, reason: string): void {
    conn.canvasDocumentLoad?.controller.abort(reason);
    for (const [key, pin] of conn.documentTurnPins) {
      pin.release();
      this.log(
        conn.sessionKey,
        conn.conversationId,
        'CANVAS_TURN_RELEASED',
        `turn=${key} conversationId=${pin.conversationId} reason=${reason}`,
      );
    }
    conn.documentTurnPins.clear();
  }

  /** Fully tear down a session: disconnect, remove from maps. Timer-free. */
  private cleanup(sessionKey: string): void {
    const conn = this.connections.get(sessionKey);
    if (!conn) return;

    this.log(sessionKey, conn.conversationId, 'CLEANUP');
    for (const [key, admission] of this.canvasAdmissionAbortControllers) {
      if (admission.conversationId !== conn.conversationId) continue;
      admission.controller.abort('owning WebSocket session was destroyed');
      this.canvasAdmissionAbortControllers.delete(key);
    }
    // A prepared admission owns a physical editor until its SSE terminal. A
    // destroyed socket cannot deliver that terminal, so retaining the pin
    // would permanently consume one of the four local residents. Scope the
    // release to this conversation: request ids are only unique per socket.
    for (const [key, pin] of this.canvasAdmissionPins) {
      if (pin.conversationId !== conn.conversationId) continue;
      pin.release();
      this.canvasAdmissionPins.delete(key);
    }
    // Une connexion détruite ne délivrera jamais son terminal. Libérer tous les
    // handles qu'ELLE possède évite un résident retenu à vie sans toucher les
    // sessions exactes des autres conversations.
    this.releaseAllDocumentTurnPins(conn, 'connexion détruite');
    conn.canvasOperationProgress.cancelAll();
    this.clearAllRedactionPlanDraftFlushes(conn);
    this.resolvePendingMessageAcceptance(conn, false);
    // La session quitte le store juste après : rendre le minuteur suffit, un
    // garde-fou qui tirerait plus tard viserait une connexion disparue.
    this.clearContextCompactionTimer(conn);
    conn.contextCompaction = { phase: 'idle' };
    conn.reconnector.abort();
    conn.client.setOnConnectionChange(null);
    conn.client.setOnAuthFailure(null);
    conn.client.disconnect();
    conn.handler.clear();

    this.connections.delete(sessionKey);
    this.timelineCache.delete(sessionKey);

    // WHY emit after delete: the session no longer exists in the pool, so the
    // bridge must drop it from the store too. Without this, a torn-down session
    // (inactivity expiry, eviction, manual close…) leaves a tombstone in Zustand:
    // the sidebar shows a stuck red dot and, worse, AgentV3View reattaches the
    // dead key — the next user message then hits `sendMessage: session not found`
    // and is silently dropped. The send path additionally re-opens on a dead key,
    // but removing the tombstone is what keeps the UI honest. (2026-06-26)
    this.events.emit('session_removed', sessionKey);
  }

  /** Settles the single in-flight composer acceptance without retaining callbacks. */
  /**
   * Envoie `session_resume` et fige le tour local auquel le verdict s'appliquera (#1688).
   * Un message en file à cet instant porte DÉJÀ `currentLocalTurnId` (posé à l'envoi,
   * avant la mise en file) : le verdict ne le concerne pas, il concerne le tour d'avant la
   * coupure, que ce message va de toute façon remplacer. On fige alors une sentinelle
   * qu'aucun tour réel n'égalera, pour que le verdict soit ignoré.
   */
  private sendSessionResume(conn: PoolConnection, sessionKey: string, why: string): void {
    conn.resumeVerdictTurnId = conn.pendingMessage ? RESUME_VERDICT_SUPERSEDED : conn.currentLocalTurnId;
    this.log(sessionKey, conn.conversationId, 'RESUME_SENT', `status=${conn.status} why=${why} turn=${conn.currentLocalTurnId ?? 'none'}`);
    conn.client.send({
      type: 'session_resume',
      conversation_id: conn.conversationId,
      ...(this.awaitsCatchup(conn) ? { catchup: true as const } : {}),
    } as FrontendMessage);
  }

  /**
   * Le tour courant a été repris au chargement de page et attend encore son début (#1888).
   * Comparé par id, comme `resumedOnLoadTurnId` : un message en file porte déjà son propre
   * tour, qui n'a rien à rattraper.
   */
  private awaitsCatchup(conn: PoolConnection): boolean {
    return conn.catchup !== null && conn.catchup.turnId === conn.currentLocalTurnId;
  }

  /**
   * Chasse d'instance (#1816). Déclenchée par le verdict « hors de portée » quand il nomme
   * une instance du tour DIFFÉRENTE de celle qui répond : le tampon de rejeu vit là-bas,
   * une reconnexion parallèle peut le trouver. Rien sans `turn_instance` (backend ancien,
   * verrou sans détenteur, instance du tour morte) ; rien tant que `busyAwaitingSettle`
   * (posé AVANT le `session_resume` du cas « déjà en cours » et remis à zéro seulement à
   * `turn_settled` / `agent_done` / nouveau tour) : le tour attendu est alors celui d'un
   * autre onglet, dont la grâce ne doit jamais être adoptée ici. Un onglet caché chasse
   * aussi (décision 2026-09-30 : une garde de visibilité posée ici transformait « pas
   * maintenant » en « jamais », le verdict n'étant pas rejoué au retour). Le client
   * refuse lui-même une chasse pendant une autre (`skipped`) : le pool ne tient aucun
   * drapeau, un seul propriétaire pour ce fait. La mécanique vit dans le client ; le pool
   * ne voit qu'une connexion. Rend `true` si une chasse part : l'appelant ne pose alors pas
   * la note « déconnecté » ; elle est posée ici si la chasse échoue, et jamais si elle est
   * gagnée (le tampon puis le direct arrivent par le chemin normal, `agent_done` clôt le tour).
   */
  private maybeHuntForTurn(conn: PoolConnection, sessionKey: string, verdict: { instance?: string; turn_instance?: string; turn_owner_connected?: true }): boolean {
    const { instance, turn_instance: turnInstance } = verdict;
    if (!instance || !turnInstance || instance === turnInstance) return false;
    if (conn.busyAwaitingSettle) return false;
    // Ce que le verdict d'origine savait déjà ; une candidate peut l'apprendre ensuite en
    // atteignant l'instance du tour (seule à voir la socket qui le tient).
    const ownerConnectedAtVerdict = verdict.turn_owner_connected === true;
    this.log(sessionKey, conn.conversationId, 'TURN_HUNT_START', `turn on ${turnInstance}, answered by ${instance}`);
    const conversationId = conn.conversationId;
    conn.client
      // La gagnante est la socket qui recevra le tour : c'est elle qui doit demander le
      // rattrapage, le `session_resume` de la socket d'origine ayant été refusé (#1888).
      .huntForTurn(conversationId, { catchup: this.awaitsCatchup(conn) })
      .then((result: TurnHuntResult) => {
        this.log(sessionKey, conversationId, 'TURN_HUNT_END', `${result.outcome} (${result.reason}) attempts=${result.attempts} durationMs=${result.durationMs} instance=${result.instance ?? 'none'}`);
        // Deux faits distincts, deux gardes. « La gagnante est adoptée » ne dépend que de la
        // connexion : détruite, plus rien à faire. « Le tour est encore attendu » ne gouverne
        // que la timeline : un Stop pendant la chasse (`cancelStream` passe en `ready` sans
        // abandonner la chasse) laisse une socket adoptée dont le circuit Canvas reste à
        // rouvrir, sans quoi le tour SUIVANT serait refusé `CANVAS_MOUNT_NOT_REANNOUNCED`.
        if (this.connections.get(sessionKey) !== conn) return;
        const turnStillAwaited = isExchangeActive(conn.status) && conn.remoteTurnAwaited;
        if (result.outcome !== 'won') {
          // Chasse perdue : l'attente de `turn_settled` reprend son cours visible.
          if (turnStillAwaited) {
            this.updateTimeline(conn, conn.reducer.applyServerFinishing(
              this.getTimeline(conn),
              serverFinishingCause(ownerConnectedAtVerdict || result.turnOwnerConnected),
            ));
          }
          return;
        }
        if (turnStillAwaited) this.updateTimeline(conn, conn.reducer.removeServerFinishing(this.getTimeline(conn)));
        // La gagnante est une socket NEUVE côté backend : sa capacité de sonde Canvas est
        // indexée par connectionId (`SessionState.supportsCanvasOperationProbe`), et son ACK
        // générique a été consommé par la chasse, pas par ce handler. Renvoyer la trame,
        // sinon les opérations Canvas du tour repris retombent en timeout dur. Les autres
        // capacités (acceptance, compaction, progress) sont lues sur l'ACK de la socket
        // d'origine : identiques hors rolling deploy, acceptable.
        if (conn.supportsCanvasOperationProbe) {
          conn.client.send({
            type: 'canvas_operation_probe_capability',
            payload: { protocolVersion: 1 },
          } as FrontendMessage);
        }
        // La coupure a suspendu le circuit Canvas du tour côté backend
        // (`cancelCanvasOpsForConnection`) : sans écriture en vol, transport
        // `connection_interrupted` (#1829), que seule une réannonce du montage émise PAR la
        // socket adoptée rouvre. Sans elle, la chasse ramène bien le tour mais chaque appel
        // Canvas est refusé `CANVAS_MOUNT_NOT_REANNOUNCED` (avant #1829 : coupure toujours
        // `unresponsive`, refus `CANVAS_TRANSPORT_UNRESPONSIVE`, prod 2026-09-30). Une
        // coupure PENDANT une écriture reste `unresponsive` : la réannonce du même montage ne
        // la rouvre pas, seul un nouveau montage le fait. Ordre garanti : `huntForTurn`
        // ne se résout `won` qu'après `resumed:true`, que le backend n'envoie qu'une fois
        // la SessionState adoptée, et `adoptSocket` a déjà fait de la gagnante `this.ws`.
        this.reannounceMountedCanvas(conn);
      })
      .catch((error: unknown) => {
        this.log(sessionKey, conversationId, 'TURN_HUNT_ERROR', error instanceof Error ? error.message : String(error));
        if (this.connections.get(sessionKey) === conn && isExchangeActive(conn.status) && conn.remoteTurnAwaited) {
          this.updateTimeline(conn, conn.reducer.applyServerFinishing(this.getTimeline(conn), serverFinishingCause(ownerConnectedAtVerdict)));
        }
      });
    return true;
  }

  /**
   * Fin d'un tour que cette socket n'a pas streamé jusqu'au bout (tour terminé hors de
   * portée, rien à reprendre, ou refus `conversation_busy` dont le tour distant vient de
   * finir). Mêmes libérations qu'`agent_done`, puis l'événement `turn_settled` que le
   * bridge traduit en rollback du tour local PUIS rechargement de l'historique, dans cet
   * ordre imposé par le store (hydrateTranscript refuse tant qu'un activeTurn existe).
   */
  private settleRemoteTurn(conn: PoolConnection, sessionKey: string, opts: { accepted: boolean; reason: string }): void {
    this.resolvePendingMessageAcceptance(conn, opts.accepted);
    this.releaseDocumentTurnPin(conn, conn.currentLocalTurnId, opts.reason);
    for (const [jobId, turnId] of conn.redactionTurnIdByJob) {
      if (turnId === conn.currentLocalTurnId) conn.redactionTurnIdByJob.delete(jobId);
    }
    this.updateTimeline(conn, conn.reducer.removeServerFinishing(this.getTimeline(conn)));
    const localTurnId = conn.currentLocalTurnId;
    conn.currentLocalTurnId = null;
    conn.catchup = null;
    conn.pendingMessage = null;
    conn.busyAwaitingSettle = false;
    conn.remoteTurnAwaited = false;
    conn.resumeVerdictTurnId = null;
    this.events.emit('turn_settled', sessionKey, conn.conversationId, localTurnId);
    this.setStatus(conn, 'ready');
    this.log(sessionKey, conn.conversationId, 'TURN_SETTLED', `${opts.reason}; history reload requested`);
  }

  private resolvePendingMessageAcceptance(conn: PoolConnection, accepted: boolean): void {
    const resolve = conn.pendingMessageAcceptance;
    conn.pendingMessageAcceptance = null;
    resolve?.(accepted);
  }

  // ==========================================================================
  // Private — structured logging
  // ==========================================================================

  /** Fige un job sans ouvrir son flux, ou rend le conflit visible. */
  private lierTravailDeRedaction(sessionKey: string, demande: DemandeDEcoute): boolean {
    return this.decisionDeTravailAcceptee(
      sessionKey,
      demande,
      redactionStreamHub.lierTravail(demande),
    );
  }

  /** Ouvre/rejoint le flux après la liaison et l'admission exacte. */
  private ecouterTravailDeRedaction(sessionKey: string, demande: DemandeDEcoute): boolean {
    return this.decisionDeTravailAcceptee(
      sessionKey,
      demande,
      redactionStreamHub.ecouter(demande),
    );
  }

  /** Point unique du refus : aucun appelant ne doit afficher ensuite un succès. */
  private decisionDeTravailAcceptee(
    sessionKey: string,
    demande: DemandeDEcoute,
    decision: DecisionDeLiaison | DecisionDEcoute,
  ): boolean {
    if (decision.statut === 'refusee') {
      toast.error(decision.raison, { ...toastDuration(8000) });
      this.log(sessionKey, demande.conversationId, 'REDACTION_JOB_REFUSED', decision.raison);
      return false;
    }
    if (decision.statut !== 'conflit') return true;

    const message = `La rédaction n'a pas démarré : le job ${demande.jobId} est déjà lié à la `
      + `conversation ${decision.liaisonExistante.conversationId}. `
      + 'Recharge cette conversation puis relance la rédaction.';
    toast.error(message, { ...toastDuration(8000) });
    this.log(sessionKey, demande.conversationId, 'REDACTION_JOB_CONFLICT', message);
    return false;
  }

  /**
   * Ouvrir le canvas puis attendre qu'il soit écrivable — **la règle unique**
   * des chemins agent qui vont muter le document (#860).
   *
   * Remplace deux copies d'une boucle de sondage de 8 s (100 ms de pas) qui ne
   * s'armaient que `if (wasOff)` : elles n'attendaient donc que dans le cas
   * qu'elles venaient elles-mêmes de provoquer, et un canvas déjà ouvert mais
   * pas encore prêt — échange de gabarit en cours, panneau encore en
   * chargement — échouait sur-le-champ. La garde portait sur « ai-je
   * allumé ? » au lieu de « est-ce prêt ? ».
   *
   * Aucune échéance GLOBALE par défaut : chaque attente a sa borne propre, et la
   * borne du tout est celle du demandeur backend, corrélée à SA requête
   * (`SessionState.waitForCanvasOpResult`). Un gestionnaire dont la suite coûte
   * encore du temps après cette ouverture passe `options.echeance` : la somme
   * des bornes par étape dépasserait sinon celle de son demandeur
   * (`canvas_open_document_request`, 2026-10-09).
   *
   * ⚠️ **`amorce` est REQUISE, sans défaut** (#1586) : chaque appelant dit s'il
   * écrit dans le document du fil (`brouillon-du-fil` — son brouillon enregistré
   * est cherché avant de monter) ou s'il veut délibérément une page neuve
   * (`aucune`). Un défaut ferait retomber le prochain appelant sur la page
   * vierge qui supprimait le brouillon du fil.
   */
  private async openCanvasForAgentWrite(
    slot: string,
    convId: string,
    operation: string,
    options: {
      readonly amorce: 'brouillon-du-fil' | 'aucune';
      /**
       * Le papier à en-tête d'une page neuve ; absent = le défaut du cabinet. Avec `aucune`, c'est
       * la page voulue. Avec `brouillon-du-fil`, il ne sert que si le fil n'a NI document dans
       * l'onglet NI brouillon enregistré : la page alors montée part de ce modèle (`open_canvas`).
       */
      readonly depart?: CanvasDocumentDepart;
      /**
       * L'échéance du gestionnaire appelant, quand il en tient une
       * (`canvas_open_document_request`). Chaque attente d'ici en reçoit ce qui
       * reste, et la résidence son signal : abandonnée à l'échéance, elle ne
       * monte rien après un accusé « rien n'a bougé ». Le montage à froid reçoit
       * ce qui reste : à 14 s fixes, un éditeur lent était détruit alors que le
       * demandeur avait encore du budget (mêmes refus que l'admission, 19-21/10).
       */
      readonly echeance?: EcheanceUnique;
    },
  ): Promise<
    // `alreadyOpen` remonte jusqu'à l'appelant pour `open_canvas` : c'est la
    // seule chose qui distingue « je viens d'ouvrir » de « c'était déjà
    // ouvert », et l'agent en a besoin pour ne pas annoncer à l'avocat une
    // ouverture qui n'a rien changé dans le canvas.
    | {
        ok: true;
        instance: SuperDocInstance;
        editor: SuperDocRuntimeEditor;
        alreadyOpen: boolean;
      }
    | { ok: false; error: string }
  > {
    const conn = this.connections.get(slot);
    if (!conn) {
      return { ok: false, error: `Canvas ${operation} refused: WebSocket slot=${slot} no longer exists.` };
    }
    // Toute opération agent passe par la même admission exacte que les jobs.
    // Un Canvas visible est une projection UI, jamais une capacité physique.
    //
    // ⚠️ **Le raccourci « une session existe déjà, ne l'acquiers pas » a été
    // retiré, et il laissait passer un refus** (#1336). Il ne servait qu'à
    // récupérer un `documentSessionId` sans repasser par le registre ; or
    // `acquireForConversation` est idempotente ET porte le seam de refus d'un
    // fil en cours de suppression (#1334). Un fil supprimé dont le document
    // était encore monté empruntait donc la branche courte et échappait au
    // refus.
    //
    // L'acquisition LÈVE dans ce cas. Cette fonction promet un
    // `{ok:false, error}` pour tous ses autres refus, et quatre handlers
    // l'appellent hors de tout `try` : laisser l'exception remonter romprait son
    // contrat et n'enverrait aucune trame corrélée — le backend attendrait son
    // propre délai au lieu d'apprendre la raison.
    try {
      canvasDocumentRuntime.acquireForConversation(convId);
    } catch (error) {
      return {
        ok: false,
        error: `Canvas ${operation} refused: ${error instanceof Error ? error.message : String(error)}`,
      };
    }
    const epingle = this.ensureDocumentTurnPinned(conn);
    if (!epingle.ok) {
      return {
        ok: false,
        error: `Canvas ${operation} refused for conversationId=${convId}: ${epingle.cause}`,
      };
    }
    // L'épingle PRÉCÈDE l'attente réseau de l'amorce : pendant la recherche, une
    // restauration concurrente voit un fil tenu par un tour et ne peut poser
    // qu'une session logique idempotente avec celle de l'amorce.
    const { echeance } = options;
    /** Passe une attente sous l'échéance de l'appelant, s'il en tient une. */
    const sousLEcheance = <T>(etape: string, pending: Promise<T>): Promise<T> => (
      echeance ? echeance.avant(etape, pending) : pending
    );
    /** Le refus d'une échéance dépassée : rien n'a été ouvert ni remplacé pour l'agent. */
    const refusDEcheance = (erreur: EcheanceDepasseeError): { ok: false; error: string } => ({
      ok: false,
      error: `Canvas ${operation} refused for conversationId=${convId}: ${erreur.message}. Nothing was `
        + 'written or replaced for this request. Retry once; if it is refused again, tell the lawyer the '
        + 'Canvas is not responding.',
    });
    if (options.amorce === 'brouillon-du-fil') {
      let amorce: Awaited<ReturnType<WebSocketPool['amorcerLeBrouillonDuFil']>>;
      try {
        amorce = await sousLEcheance(
          'looking up the saved draft of this conversation',
          this.amorcerLeBrouillonDuFil(convId, operation),
        );
      } catch (err) {
        // `amorcerLeBrouillonDuFil` ne lève jamais : seule l'échéance arrive ici.
        if (err instanceof EcheanceDepasseeError) return refusDEcheance(err);
        throw err;
      }
      if (!amorce.ok) {
        this.log(slot, convId, 'CANVAS_DRAFT_SEED_REFUSED', `${operation}: ${amorce.error}`);
        return amorce;
      }
    }
    const failure = (cause: unknown): { ok: false; error: string } => {
      const session = canvasDocumentRuntime.getSessionForConversation(convId);
      const presentation = canvasDocumentRuntime.getScope(convId)?.getPresentation() ?? null;
      return {
        ok: false,
        error: describeCanvasUnavailable({
          operation,
          conversationId: convId,
          documentTitle: presentation?.documentTitle
            ?? presentation?.templateMeta?.documentTitle
            ?? null,
          documentId: session?.documentId ?? null,
          cause,
        }),
      };
    };

    try {
      await sousLEcheance('mounting the canvas residence', canvasDocumentRuntime.requestResident(options.amorce === 'aucune'
        // PAGE NEUVE : elle ne dépend d'aucune source. Sans cette graine vierge
        // déclarée remplaçante, la résidence rejouait l'ouverture RETENUE du
        // fil — et quand celle-ci était un chargement qui n'avait jamais monté
        // (`load_document_into_canvas` en échec d'hydratation), la page neuve
        // sur laquelle l'agent se rabattait échouait à son tour, sur le
        // document dont il ne voulait plus (2026-10-09). Un document déjà
        // éditable n'est pas concerné : il se réhydrate depuis son brouillon,
        // et c'est le remplacement plus bas qui pose la page neuve.
        ? {
          conversationId: convId,
          seed: { documentId: null, presentation: presentationDUnePageNeuve(options.depart) },
          seedReplacesUnmountedOpening: true,
          visibility: 'preserve',
          signal: echeance?.signal,
          hydrationBudgetMs: echeance?.resteMs,
        }
        : {
          conversationId: convId,
          seed: this.graineDuFilOuPageNeuve(convId, options.depart),
          visibility: 'preserve',
          signal: echeance?.signal,
          hydrationBudgetMs: echeance?.resteMs,
        }));
    } catch (err) {
      this.log(slot, convId, 'CANVAS_ADMISSION_FAILED', `${operation}: ${err instanceof Error ? err.message : String(err)}`);
      if (err instanceof EcheanceDepasseeError) return refusDEcheance(err);
      if (err instanceof CanvasDraftContentUnreadableError) {
        return { ok: false, error: refusDeContenuDeBrouillonIllisible(`Canvas ${operation} refused for conversationId=${convId}`, err) };
      }
      return failure(err);
    }

    // La résidence exacte est prête et aucun refus préparatoire ne subsiste.
    // Le signal reste juste avant le déploiement : le dock publie la bonne
    // scène dans la même tâche, puis `openCanvas` la rend visible (#1588).
    this.signalerAdmissionAgentCanvas(conn);
    const { alreadyOpen } = openCanvas(`${operation} — conversation ${convId}`);
    if (!alreadyOpen) this.log(slot, convId, 'REDACTION_MODE_AUTO_OPEN', operation);

    let instance: SuperDocInstance;
    try {
      instance = await sousLEcheance(
        'waiting for the canvas editor to become writable',
        waitForCanvasEditorReady(
          convId,
          echeance ? Math.min(CANVAS_MOUNT_WAIT_MS, echeance.resteMs()) : CANVAS_MOUNT_WAIT_MS,
        ),
      );
    } catch (err) {
      this.log(slot, convId, 'CANVAS_WAIT_FAILED', `${operation}: ${err instanceof Error ? err.message : String(err)}`);
      if (err instanceof EcheanceDepasseeError) return refusDEcheance(err);
      return failure(err);
    }

    const editor = instance.activeEditor ?? null;
    if (!editor) {
      // L'éditeur a disparu entre la résolution de l'attente et cette lecture
      // (démontage, éviction). Rare, mais on le dit tel quel plutôt que de
      // laisser `applyActions` planter sur un éditeur nul.
      return failure('l\'éditeur SuperDoc a disparu entre la fin du montage et l\'application des actions.');
    }
    this.log(slot, convId, 'CANVAS_WAIT_OK', operation);
    return { ok: true, instance, editor, alreadyOpen };
  }

  /**
   * Laisse l'écran qui rejoint ce fil y arriver avant un `write_markdown` en
   * `replace` (#1588 × #1586).
   *
   * Ce verbe refuse de remplacer un document que l'avocat ne voit pas (hôte au
   * parking). Or la bascule du dock vers l'assistant vient de partir avec le
   * signal : la route de l'onglet désigne déjà `/assistant?c=<fil>`, mais
   * l'emplacement ne réclame l'hôte qu'au commit de cette route, alors que
   * l'exécuteur n'enchaîne que des microtâches. Sans cette attente, le premier
   * remplacement lancé depuis le dock était refusé à coup sûr, et l'agent
   * demandait à l'avocat d'afficher le canvas qu'il avait déjà sous les yeux.
   *
   * N'attend QUE si l'écran rejoint ce fil et que son hôte est encore parqué :
   * un avocat resté ailleurs n'arrivera pas, et le refus de l'exécuteur est
   * alors la bonne réponse. À l'échéance, l'exécution reprend et l'exécuteur
   * juge seul.
   *
   * ⚠️ « L'écran rejoint ce fil » se lit de DEUX façons depuis que la bascule
   * s'annonce avant de partir : la route de l'onglet le montre déjà, ou une
   * bascule est annoncée pour lui. La route seule ne suffit plus — pendant les
   * `DELAI_DANNONCE_AVANT_LA_BASCULE_MS` de l'annonce, l'onglet affiche encore
   * l'écran quitté, et l'attente ne s'armait plus du tout : le premier
   * remplacement lancé depuis le dock repartait en
   * `REPLACE_REFUSED_CANVAS_NOT_VISIBLE`.
   */
  private async laisserLEcranRejoindreLeFil(sessionKey: string, conversationId: string): Promise<void> {
    const ecranEnRoute = lEcranAssistantAfficheLeFil(lireLaRouteDeLOnglet(), conversationId)
      || uneBasculeDuDockEstAnnoncee(conversationId);
    if (!ecranEnRoute) return;
    if (!isCanvasHostParked(conversationId)) return;
    const affiche = await attendreQueLHoteSoitAffiche(conversationId, CANVAS_MOUNT_WAIT_MS);
    if (!affiche) {
      this.log(
        sessionKey,
        conversationId,
        'CANVAS_HOST_NOT_CLAIMED',
        `the assistant route shows this conversation but no slot claimed its canvas within ${CANVAS_MOUNT_WAIT_MS} ms; the executor judges visibility as is`,
      );
    }
  }

  /**
   * Amorce le brouillon enregistré d'un fil avant qu'un agent ne demande sa
   * résidence (#1586), et traduit l'issue en refus lisible par l'agent.
   *
   * La décision vit dans l'orchestration documentaire — la même recherche et le
   * même tri-état que la restauration de `/assistant`. Ici ne se décide que la
   * phrase : ce que l'agent doit faire, et ce qu'il ne doit surtout pas faire.
   * Aucun nouveau code sur le fil : le refus voyage dans le champ `error` de la
   * trame de résultat de chaque appelant.
   */
  private async amorcerLeBrouillonDuFil(
    conversationId: string,
    operation: string,
  ): Promise<{ readonly ok: true } | { readonly ok: false; readonly error: string }> {
    let resultat: Awaited<ReturnType<typeof executerLIntentionDocumentaire>>;
    try {
      resultat = await executerLIntentionDocumentaire({
        kind: 'amorcer-le-brouillon-avant-admission',
        conversationId,
      });
    } catch (error: unknown) {
      // L'orchestration convertit ses échecs métier en issues : une levée ici est
      // un défaut de programme. Les appelants promettent pourtant une trame
      // corrélée pour tout refus — le backend attendrait sinon son propre délai.
      return {
        ok: false,
        error: `Canvas ${operation} refused for conversationId=${conversationId}: the saved-draft seeding step threw `
          + `(${error instanceof Error ? error.message : String(error)}). Nothing was mounted. Do not write into the `
          + 'Canvas; report this failure.',
      };
    }
    switch (resultat.issue) {
      case 'document-deja-dans-l-onglet':
      case 'aucun-brouillon':
      case 'brouillon-pose':
      case 'brouillon-deja-en-place':
        return { ok: true };
      case 'restauration-indisponible':
        return {
          ok: false,
          error: `Canvas ${operation} refused for conversationId=${conversationId}: this conversation's saved Canvas `
            + `draft could not be looked up (${resultat.etat.diagnostic}). Nothing was mounted, so no blank page can `
            + `overwrite that draft. Retry ${operation} once; if it fails again, tell the lawyer the saved document is `
            + 'unreachable and do not write into the Canvas.',
        };
      case 'refusee':
        return {
          ok: false,
          error: `Canvas ${operation} refused for conversationId=${conversationId}: ${resultat.raison}. Nothing was `
            + `mounted. Retry ${operation} once that opening completes.`,
        };
      case 'amorce-impossible':
        // Aucune ouverture concurrente ici : promettre d'en attendre la fin
        // ferait relancer l'agent sur un refus qui se reproduira à l'identique.
        return {
          ok: false,
          error: `Canvas ${operation} refused for conversationId=${conversationId}: ${resultat.raison}. Nothing was `
            + 'mounted, so no blank page can overwrite that draft. Do not write into the Canvas of this conversation; '
            + 'report this failure to the lawyer.',
        };
    }
  }

  /** Structured log for WS lifecycle events. convId is truncated to 8 chars. */

  private log(slot: string, convId: string, event: string, detail?: string): void {
    console.log(`[WS:${slot}:${convId?.slice(0, 8)}] ${event}${detail ? ' — ' + detail : ''}`);
  }
}

// ==========================================================================
// HMR cleanup
// ==========================================================================

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    WebSocketPool.getInstance().destroyAll();
  });
}
